import { INCOME_CATEGORIES, sortCategories } from './category-store.js';
import {
  DEMO_NEED_BASELINE, DEMO_WANT_BASELINE, ZERO, add, amountFromLegacy, anchorForDayOfMonth,
  creditTermsFrom, demoAccounts, demoCreditTerms, minM, neg, demoGoals, demoIncome, demoObligations,
  clampPos, kop, plainDateFromLocal, sub, valueOr, withCardFacts, withLoanFacts, type LoanFact,
  instanceAmount, nextOccurrenceFrom, nextOccurrenceInclusive, obligationSchedule, scheduleFrom,
  type AcceptedPlan, type Account, type Amount, type PeriodDeal, type PeriodTopicOff, dealsLeft, type AvailableInput, type CreditTerms, type PeriodBaseline, type PeriodIncome, type PeriodPin,
  type FocusKind, type Goal, type IncomeStream, type Loan, type Money, type Obligation,
  type PaymentSchedule,
  type AcceptedAllocation, type HorizonRole, type IncomeExpectation, type PaymentDecision,
  type IncomeAmountBasis, type IncomeRhythm,
  type PlainDate, type PlanMode, type PlanPart, type PlannedAllocation, type PlannedExpense,
  type Reserve, type ReceivedIncome, type Consent, type ConsentKind, type ConsentMethod,
  type InsightCandidate, type InsightDisplayState,
  activeAllocation, allocationView, distributeRest, expectedRest, freeFromIncome, fulfilledBy,
  proposeAllocation,
  type AllocationView, type AllocationEntry,
  resolveHorizon,
  activeStablePlan, bufferOf, decisionAllowed, decisionOf, hasDates, horizonPayout, liquidOf,
  payoutId, reservedOf, rhythmOf, roleOfStream, stableOutlook, stableReadiness, suggestStable,
  bufferAt, paidOnOf, shiftMonth,
  type BufferMove, type HorizonInput, type PayoutAction, type PayoutCadence,
  type StableBudgetPlan, type StableOutlook,
  type StablePayoutFact, type StableReadiness,
  type AllocationBasis, type AllocationProposal, type FutureMoney, type IncomeFact,
  type RestTarget,
  type ShortfallDecision,
  type ObligationPayment, type ReserveTarget, type TradeoffChoice, type PeriodChoice,
  classifyImport, planLink,
  type ExpenseCandidate, type ImportVerdict, type ImportedReceipt, type LinkPlan,
  choicePins, nextPin, occurrenceOf, pendingOccurrences,
  usualSpending, fromHistory, type Usual, cardEnvelopeNow, creditState,
} from '@skoka/core';
import { looksLikePayment } from './payment-match.js';
import { STARTER_CATEGORIES, type Category, type NewCategoryMode } from './categories.js';
import {
  clearStores, getAll, getMeta, mutate, put, putAll, queued, quietly, remove, replaceAll, setMeta,
  transact, LOCAL_CHANGED, STORES, type StoreName, type TxScope,
} from './db.js';
import { decode, type PersistedState } from './persisted.js';
import { rememberGroupOrder, sortAccounts } from './account-groups.js';
import { migrateAccountIcon, migrateCategoryIcon } from './icon-migrate.js';

/** Операция в локальном хранилище. Суммы всегда положительные, знак задаёт type. */
/**
 * Позиция чека (И6: одна операция — N позиций).
 *
 * Хранится вместе с операцией, потому что состав чека восстановить потом
 * неоткуда: снимок нигде не сохраняется. Категории у позиции пока нет —
 * каскад классификации и каталог (§10.1, §10.2) ещё не сделаны, а придумывать
 * категорию каждой строке без них значило бы наполнить данные догадками.
 */
export interface TxItem {
  name: string;
  /** Штуки или килограммы: на чеке бывает и «2ШТ», и «0.314КГ». */
  qty: number;
  price: Money;
  sum: Money;
}

/**
 * Связь операции с тем, что она исполняет.
 *
 * Ровно одна на операцию: событие имеет один источник, и два адреса означали
 * бы двойной учёт. Без связей нельзя ни построить факт плана, ни отличить
 * подтверждённый приход от нового дохода, ни собрать историю взносов.
 */
export type TxLink =
  /**
   * Приход. `incomeStreamId` — какой ожидаемый доход он закрывает; без него
   * это разовые деньги (подарок, продажа), и ничей прогноз они не закрывают.
   * `expectedRest` — сколько человек ещё ждёт по этому же приходу.
   */
  | {
    kind: 'income';
    incomeStreamId?: string;
    expectedRest?: Money;
    receiptId?: string;
    /** Какое ожидание закрывает этот приход. */
    expectationId?: string;
    /**
     * Что это за деньги.
     *
     * `regular` — ожидаемый доход: он закрывает ожидание, двигает горизонт и
     * обучает ритм. `oneoff` — подарок, продажа, премия: деньги доливают
     * бюджет, но ритму не учат и горизонт не трогают. `refund` — возврат: это
     * не заработок, а отмена прошлой траты, и в доходах ему места нет.
     * Перевод между своими счетами сюда не попадает вовсе — у него свой тип
     * операции.
     */
    incomeKind?: 'regular' | 'oneoff' | 'refund';
    /**
     * Какое наступление дохода закрывает приход — дата по расписанию.
     *
     * Записывается один раз, вскоре после самого прихода (`keepPeriod`), и
     * дальше не пересматривается: от неё зависят границы периодов аналитики,
     * и перенос зарплатного дня не должен переписывать прошлые месяцы.
     */
    occurrence?: PlainDate;
    /**
     * Откуда это известно: приход закрыл ожидание, человек уточнил сам или
     * приход отнесён по расписанию (оценка, её можно переспросить).
     */
    occurrenceBasis?: 'expectation' | 'confirmed' | 'schedule';
  }
  /** Ключ наступления платежа: `${obligationId}:${scheduledDate}`. */
  | { kind: 'obligation'; instanceId: string; obligationId: string }
  | { kind: 'goal'; goalId: string }
  /**
   * Досрочный по долгу. `mode` — что сделал банк: сократил срок (по
   * умолчанию) или платёж; от него зависит платёж дальше.
   */
  | { kind: 'loan'; loanId: string; mode?: 'term' | 'payment' };

export interface Tx {
  id: string;
  type: 'expense' | 'income' | 'transfer' | 'adjustment';
  accountId: string;
  counterAccountId?: string;
  amount: Money;
  /** Дата экономического события: для покупки кредиткой — дата покупки (И3). */
  occurredAt: string; // ISO
  occurredDate: PlainDate;
  categoryId?: string;
  part?: PlanPart;
  note?: string;
  /** Снимок чека, из которого создана операция. Лежит только на устройстве. */
  receiptId?: string;
  /**
   * Ключ действия, создавшего операцию.
   *
   * Один на одно нажатие. По нему повтор доставки отличается от второй
   * настоящей оплаты: совпадение суммы и даты ничего не доказывает.
   */
  requestId?: string;
  /**
   * Версия раскладки, действовавшая в момент записи.
   *
   * Привязка к версии, а не к дате: пересогласование посреди дня и запись
   * задним числом иначе уменьшат не ту корзину. Версия ставится тогда, когда
   * человек рассказал о событии, — в этом состоянии он и принимал решения.
   */
  allocationId?: string;
  /**
   * Изменил ли эту операцию баланс счёта.
   *
   * Отсутствие поля означает «да» — так ведут себя все прежние операции.
   * Ложь ставится там, где капитал уменьшился до того, как человек назвал
   * остаток: платёж оплачен утром, а баланс введён вечером уже за вычетом.
   * Записать расход и при этом списать деньги значило бы посчитать их дважды,
   * а не записывать вовсе — потерять след, и тогда сверка отнесёт эти деньги
   * в «Прочее» как настоящую трату (И8: источник цифры должен быть виден).
   */
  balanceApplied?: boolean;
  /**
   * Сколько платёж взял из отложенного под него: конверт платежа, деньги под
   * кредитку. Остальное — из свободных денег раскладки. Нет поля — старая
   * запись или не платёж.
   */
  reserveUsed?: Money;
  /** Перевод запаса стабильного бюджета: раскладка учитывает его по журналу запаса. */
  buffer?: true;
  /**
   * Корректировка: какой остаток назвал человек на момент операции. У
   * кредитки — минусом. Сумма корректировки — разница с записанным, со
   * знаком; она меняется, когда задним числом находятся пропущенные операции,
   * а названный остаток — нет.
   */
  target?: Money;
  /** Корректировка: разница на момент записи — чтобы показать «уточнилась». */
  initial?: Money;
  /** Что эта операция исполняет: приход, платёж, взнос в цель, платёж по долгу. */
  link?: TxLink;
  items?: TxItem[];
  source: 'manual' | 'chat' | 'obligation_auto' | 'receipt_qr' | 'receipt_photo' | 'bank_screen';
  createdAt: string;
}

/**
 * Состояние приложения: сохраняемые факты плюс вычисляемое.
 *
 * Разделение существенно для синхронизации: на сервер уезжает только
 * `PersistedState`, а `today` и `isEmpty` остаются здесь. Снимок с чужим
 * «сегодня» испортил бы первый же расчёт на другом устройстве.
 */
export interface AppState extends Omit<PersistedState, 'needBaseline' | 'wantBaseline'> {
  /**
   * Обычные траты числом — для движков, которые считают деньги.
   *
   * Неизвестное превращается в ноль **в одном месте**, здесь, и это видно.
   * Новые расчёты (запас хода, ликвидность, распределение) берут `baselines`
   * и обязаны отличать «не знаю» от нуля сами.
   */
  needBaseline: Money;
  wantBaseline: Money;
  /**
   * Те же значения со статусом известности.
   *
   * Это «обычно в месяц» — по истории трат (usual.ts), с оценкой человека как
   * стартовой точкой. Все расчёты берут его отсюда; оценка — в `estimates`.
   */
  baselines: { need: Amount; want: Amount };
  /** Оценка человека как есть: её правят в «Обычные траты» и её же везёт снимок. */
  estimates: { need: Amount; want: Amount };
  /** Откуда «обычно»: по скольким месяцам, оценка или закреплено. */
  usual: Usual;
  /**
   * Поля снимка, которых эта версия клиента не знает.
   *
   * Пришли от более нового клиента и возвращаются в снимок как есть. Телефон,
   * отставший на версию, не должен обеднять данные просто тем, что открыл
   * приложение.
   */
  snapshotUnknown: Record<string, unknown>;
  /** Сегодняшняя дата устройства. Не сохраняется и не синхронизируется. */
  today: PlainDate;
  /** Пусто до первого счёта: показываем настройку, а не нули. */
  isEmpty: boolean;
}

/**
 * Ключ одного действия человека.
 *
 * Не идентификатор записи: по нему повтор доставки отличается от второго
 * настоящего действия с теми же числами.
 */
export const newRequestId = (): string => uid();

const uid = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `id_${Date.now()}_${Math.random().toString(36).slice(2)}`;

export const todayPd = (): PlainDate => plainDateFromLocal(new Date());

/**
 * «Сейчас» для времени операции — по местным часам, в той же записи, что и
 * время, названное человеком (`2026-10-14T12:20:00.000Z` — это 12:20 на его
 * часах, а не по Гринвичу).
 *
 * Прежде «сейчас» писалось по Гринвичу, а названное время — по местным
 * часам. В Москве трата, записанная в 12:20 «сейчас», становилась 09:20 и
 * оказывалась раньше кофе, записанного на 11:00, а правка операции показывала
 * время на три часа меньше. Для корректировки остатка порядок решает, какие
 * траты в сверенном числе уже учтены, — ошибиться в нём нельзя.
 */
export function localMoment(d: Date = new Date()): string {
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${plainDateFromLocal(d)}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}Z`;
}

/**
 * Время записи, строго растущее внутри приложения.
 *
 * По нему упорядочены части зарплаты и ответы человека об остатке. Две
 * записи в одну миллисекунду (пакетный разбор выписки, быстрые нажатия)
 * иначе получили бы равное время, и порядок решал бы случайный ключ.
 */
let lastStamp = 0;
export const stamp = (): string => {
  const now = Math.max(Date.now(), lastStamp + 1);
  lastStamp = now;
  return new Date(now).toISOString();
};

/** Заменить запись в снимке списка без изменения остальных строк. */
const withRecord = <T extends { id: string }>(rows: readonly T[], next: T): T[] => [
  ...rows.filter((x) => x.id !== next.id), next,
];

/**
 * Как изменение счетов и резервов влияет на «Пока не распределено».
 *
 * Остальные корзины принятой раскладки от движения буфера не меняются,
 * поэтому для сохранения равенства нужен ровно прирост ликвидных денег
 * минус прирост связанных денег. Такой расчёт работает и для неликивидной
 * копилки, и для перевода между разными счетами — догадка по виду движения
 * там неизбежно ошибается.
 */
function allocationDelta(
  beforeAccounts: readonly Account[], beforeReserves: readonly Reserve[],
  afterAccounts: readonly Account[], afterReserves: readonly Reserve[],
): Money {
  const liquidDelta = sub(liquidOf(afterAccounts), liquidOf(beforeAccounts));
  const reservedDelta = sub(
    reservedOf(afterAccounts, afterReserves),
    reservedOf(beforeAccounts, beforeReserves),
  );
  return sub(liquidDelta, reservedDelta);
}

const allocationMoveFields = (
  allocation: Pick<AcceptedAllocation, 'id'> | null, delta: Money,
): Pick<BufferMove, 'allocationId' | 'unallocatedDelta'> | Record<string, never> =>
  allocation && delta !== ZERO
    ? { allocationId: allocation.id, unallocatedDelta: delta }
    : {};

/**
 * С какого дня приложение ведёт учёт.
 *
 * Для нового человека дату ставит завершение настройки. Для тех, кто
 * настроился раньше, чем эта дата появилась, она выводится один раз и
 * сохраняется — иначе граница добросовестности плыла бы при каждой правке
 * остатка.
 *
 * Порядок источников: сохранённое значение → самая поздняя дата актуальности
 * остатков активных счетов (в этот день человек сверил картину с реальностью)
 * → сегодня, если не известно ничего.
 */
async function trackingStart(accounts: readonly Account[]): Promise<PlainDate | null> {
  const saved = await getMeta<PlainDate>('trackingStartedAt');
  if (saved) return saved;

  const alive = accounts.filter((a) => !a.archived);
  if (alive.length === 0) return null;

  const dates = alive.map((a) => a.balanceAsOf).filter((d): d is PlainDate => !!d).sort();
  const derived = dates.length > 0 ? dates[dates.length - 1]! : todayPd();
  await setMeta('trackingStartedAt', derived);
  return derived;
}

/**
 * Освободить запас выключенного режима.
 *
 * Починка для записей, сделанных до того, как отключение стало всегда
 * освобождать запас. Такой конверт — деньги без хозяина: выключенный план
 * из действующих пропадает, экран предлагает завести режим заново, а
 * конверт продолжает вычитаться из доступного, и снять его негде.
 *
 * Освобождение возвращает деньги в распоряжение человека и записывается в
 * журнал — молча они не исчезают и не появляются.
 */
async function repairStableStorage(): Promise<void> {
  await queued('stable:repair-storage', () => transact(
    ['stablePlans', 'stablePayouts', 'stableMoves', 'reserves', 'accounts', 'allocations'],
    async (db) => {
      const plans = await db.all<StableBudgetPlan>('stablePlans');
      const payouts = await db.all<StablePayoutFact>('stablePayouts');
      const moves = await db.all<BufferMove>('stableMoves');
      const reserves = await db.all<Reserve>('reserves');
      const accounts = await db.all<Account>('accounts');
      const allocation = activeAllocation(await db.all<AcceptedAllocation>('allocations'));
      const stopped = new Set(plans.filter((p) => p.status === 'stopped').map((p) => p.id));
      const moveIds = new Set(moves.map((m) => m.id));
      const now = new Date().toISOString();

      /*
        Версия, где отключение освобождало запас, записывала это как
        плановую выплату. Раскладка от этого сходилась, зато месячный отчёт
        утверждал, что человек «получил из буфера» весь остаток. Переносим
        эффект в движение освобождения, а выдуманный факт удаляем.
      */
      for (const fact of payouts) {
        if (!fact.paidOn) {
          db.put('stablePayouts', {
            ...fact,
            paidOn: plainDateFromLocal(new Date(fact.paidAt)),
          });
        }
        if (!fact.id.startsWith(`${fact.planId}:stop:`)) continue;
        const linked = moves.find((m) => m.payoutId === fact.id);
        const targetAllocation = fact.allocationId ? { id: fact.allocationId } : allocation;
        if (linked) {
          const { payoutId: _drop, ...rest } = linked;
          void _drop;
          db.put('stableMoves', {
            ...rest,
            ...allocationMoveFields(
              targetAllocation,
              fact.unallocatedDelta ?? fact.amount,
            ),
          } satisfies BufferMove);
        } else {
          const id = `stable:repair-stop:${fact.id}`;
          if (!moveIds.has(id)) {
            db.put('stableMoves', {
              id,
              planId: fact.planId,
              date: fact.paidOn ?? plainDateFromLocal(new Date(fact.paidAt)),
              amount: neg(fact.amount),
              kind: 'release',
              ...allocationMoveFields(
                targetAllocation,
                fact.unallocatedDelta ?? fact.amount,
              ),
              createdAt: now,
            } satisfies BufferMove);
          }
        }
        db.del('stablePayouts', fact.id);
      }

      /*
        Старый выключенный режим мог оставить живой конверт. Починка теперь
        одна транзакция и один устойчивый ключ: две вкладки и повторная
        загрузка не создают два освобождения, а сбой не оставляет удаление
        без записи в журнале.
      */
      for (const reserve of reserves.filter(
        (r) => r.forKind === 'stable' && stopped.has(r.forId) && r.amount > ZERO,
      )) {
        const id = `stable:repair-orphan:${reserve.id}`;
        const afterReserves = reserves.filter((r) => r.id !== reserve.id);
        if (!moveIds.has(id)) {
          db.put('stableMoves', {
            id,
            planId: reserve.forId,
            date: todayPd(),
            amount: neg(reserve.amount),
            kind: 'release',
            ...allocationMoveFields(
              allocation,
              allocationDelta(accounts, reserves, accounts, afterReserves),
            ),
            createdAt: now,
          } satisfies BufferMove);
        }
        db.del('reserves', reserve.id);
      }
    },
  ));
}

/** Настройка завершена — с этого дня приложение отвечает за картину. */
export async function markTrackingStarted(date: PlainDate = todayPd()): Promise<void> {
  const saved = await getMeta<PlainDate>('trackingStartedAt');
  if (!saved) await setMeta('trackingStartedAt', date);
}

export async function loadState(): Promise<AppState> {
  let categories = await getAll<Category>('categories');
  if (!(await getMeta<boolean>('categoryDefaultsReady'))) {
    if (categories.length === 0) {
      await putAll('categories', STARTER_CATEGORIES);
      categories = STARTER_CATEGORIES;
    }
    if (!categories.some(c => c.kind === 'income')) {
      await putAll('categories', INCOME_CATEGORIES);
      categories = [...categories, ...INCOME_CATEGORIES];
    }
    await setMeta('categoryDefaultsReady', true);
  }

  await repairStableStorage();

  // Значки до Font Awesome — имена lucide и эмодзи. Переводятся один раз и
  // сохраняются: дальше в хранилище и в снимке только новые имена.
  const movedCategories = categories.map(migrateCategoryIcon);
  if (movedCategories.some((c, i) => c !== categories[i])) {
    await putAll('categories', movedCategories.filter((c, i) => c !== categories[i]));
    categories = movedCategories;
  }
  // Порядок, как человек расставил (28.10.2026): везде, где категории списком.
  categories = sortCategories(categories);

  const [storedAccounts, storedTerms, incomeStreams, storedObligations, goals, plannedExpenses, transactions] =
    await Promise.all([
      getAll<Account>('accounts'),
      getAll<CreditTerms>('creditTerms'),
      getAll<IncomeStream>('incomeStreams'),
      getAll<Obligation>('obligations'),
      getAll<Goal>('goals'),
      getAll<PlannedExpense>('plannedExpenses'),
      getAll<Tx>('transactions'),
    ]);
  const accounts = storedAccounts.map(migrateAccountIcon);
  if (accounts.some((a, i) => a !== storedAccounts[i])) {
    await putAll('accounts', accounts.filter((a, i) => a !== storedAccounts[i]));
  }
  const storedLoans = await getAll<Loan>('loans');
  const stablePlans = await getAll<StableBudgetPlan>('stablePlans');
  const stablePayouts = await getAll<StablePayoutFact>('stablePayouts');
  const reserves = await getAll<Reserve>('reserves');
  const stableMoves = await getAll<BufferMove>('stableMoves');
  const plannedAllocations = await getAll<PlannedAllocation>('plannedAllocations');
  const expectedIncomes = await getAll<IncomeExpectation>('expectedIncomes');

  // Условия карт приходят из двух мест: из записи карты и из кредита. Главная —
  // запись карты: в неё пишут лист карты и каждый перевод на карту, а кредит
  // хранит то, что было названо в настройке. Когда главным был кредит,
  // погашение не доходило до расчёта. Из кредита условия берутся, только
  // если записи карты нет. Ключ — счёт карты, дублей быть не может.
  const byAccount = new Map(creditTermsFrom(storedLoans).map((t) => [t.accountId, t]));
  for (const t of storedTerms) byAccount.set(t.accountId, t);
  const creditTerms = [...byAccount.values()];
  // Кредитка в «Долгах» — с долгом и условиями самой карты. Остальные долги —
  // с остатком из записанных платежей: «Оплатил» уменьшает остаток на тело
  // платежа, досрочный — на всю сумму. Править остаток руками не нужно.
  const loans = withLoanFacts(
    withCardFacts(storedLoans, accounts, creditTerms), loanFactsOf(storedLoans, transactions, storedObligations), todayPd());
  const obligations = withLoanPayments(storedObligations, storedLoans, loans);

  const goalEventsList = await getAll<GoalEvent>('goalAllocations');
  // Отсутствие значения в хранилище — это «не спрашивали», а не ноль.
  const need = amountFromLegacy((await getMeta<Money>('needBaseline')) ?? null);
  const want = amountFromLegacy((await getMeta<Money>('wantBaseline')) ?? null);
  const usualPinned = (await getMeta<string[]>('usualPinned')) ?? [];
  const accountGroups = (await getMeta<string[]>('accountGroups')) ?? [];
  rememberGroupOrder(accountGroups);
  const profileName = await getMeta<string>('profileName');
  const trackingStartedAt = await trackingStart(accounts);
  const focusOverride = await getMeta<FocusKind>('focusOverride');
  const obligationsKnown = await getMeta<boolean>('obligationsKnown');
  const planMode = await getMeta<PlanMode>('planMode');
  const tradeoffChoice = await getMeta<TradeoffChoice>('tradeoffChoice');
  const newCategoryMode = await getMeta<NewCategoryMode>('newCategoryMode');
  const periodChoice = await getMeta<PeriodChoice>('periodChoice');
  const acceptedPlans = await getAll<AcceptedPlan>('acceptedPlans');
  const periodPins = (await getMeta<PeriodPin[]>('periodPins')) ?? [];
  const periodBaselines = (await getMeta<PeriodBaseline[]>('periodBaselines')) ?? [];
  const dismissedHints = (await getMeta<string[]>('dismissedHints')) ?? [];
  const periodDeals = (await getMeta<PeriodDeal[]>('periodDeals')) ?? [];
  const periodTopicsOff = (await getMeta<PeriodTopicOff[]>('periodTopicsOff')) ?? [];
  const periodReviewLater = (await getMeta<string[]>('periodReviewLater')) ?? [];
  const allocations = await getAll<AcceptedAllocation>('allocations');
  const consents = await getAll<Consent>('consents');
  const insightLog = await getAll<InsightDisplayState>('insightLog');
  const snapshotUnknown = (await getMeta<Record<string, unknown>>('snapshotUnknown')) ?? {};
  const receiptImports = await listReceiptImports();

  return {
    today: todayPd(),
    snapshotUnknown,
    receiptImports,
    // Счета — в порядке человека: группы и места внутри групп. Отсюда их
    // берут все списки и все «счёт по умолчанию» в формах.
    accounts: sortAccounts(accounts, accountGroups), creditTerms, loans, incomeStreams, obligations, goals, plannedExpenses,
    plannedAllocations,
    stablePlans, stablePayouts, stableMoves,
    expectedIncomes,
    categoryDefaultsReady: true, categories, transactions, reserves, acceptedPlans, periodPins, periodBaselines, dismissedHints, allocations, consents, insightLog,
    periodDeals, periodTopicsOff, periodReviewLater, usualPinned, accountGroups,
    ...usualState({ need, want }, usualPinned, transactions, obligations, categories, trackingStartedAt ?? undefined),
    goalEvents: goalEventsList,
    ...(obligationsKnown === true ? { obligationsKnown: true } : {}),
    ...(profileName ? { profileName } : {}),
    ...(trackingStartedAt ? { trackingStartedAt } : {}),
    ...(focusOverride ? { focusOverride } : {}),
    ...(planMode ? { planMode } : {}),
    ...(tradeoffChoice ? { tradeoffChoice } : {}),
    ...(newCategoryMode ? { newCategoryMode } : {}),
    ...(periodChoice ? { periodChoice } : {}),
    isEmpty: accounts.length === 0,
  };
}

// ────────────────────────────────────────────────────────────── кредиты

/**
 * Завести кредит.
 *
 * Кредит владеет своим ежемесячным платежом: если передано обязательство, оно
 * привязывается, а не создаётся заново. Иначе один и тот же платёж оказался бы
 * записан дважды — и вычелся из доступного дважды.
 */
export async function addLoan(input: Omit<Loan, 'id'>): Promise<Loan> {
  const loan: Loan = {
    ...input, id: uid(),
    ...(input.principalAsOf && !input.principalSetAt ? { principalSetAt: new Date().toISOString() } : {}),
  };
  await put('loans', loan);
  return loan;
}

/**
 * Правка кредита.
 *
 * `remove` — поля, которые человек стёр. Без него очистить дату было нельзя:
 * пустое поле просто не попадало в правку, и прежнее значение оставалось
 * жить. Для даты выдачи это означало бы, что приложение продолжает считать
 * возраст долга по числу, которое человек уже убрал.
 */
export async function updateLoan(
  id: string, patch: Partial<Loan>, remove: readonly (keyof Loan)[] = [],
): Promise<void> {
  const all = await getAll<Loan>('loans');
  const l = all.find((x) => x.id === id);
  if (!l) return;
  const next = { ...l, ...patch };
  // Остаток назван заново — с этой минуты факты того же дня его уменьшают.
  if ((patch.principal !== undefined || patch.principalAsOf !== undefined) && !patch.principalSetAt) {
    next.principalSetAt = new Date().toISOString();
  }
  for (const key of remove) delete next[key];
  // Набежавшие проценты считаются при чтении: в записи им не место.
  delete next.accrued; delete next.accruedSince; delete next.accruedPerDay;
  await put('loans', next);
}

/**
 * Убрать кредит.
 *
 * Обязательство, которым он платился, уходит вместе с ним: платёж по
 * несуществующем кредите продолжал бы вычитаться из доступного каждый месяц.
 */
export async function deleteLoan(id: string): Promise<void> {
  const all = await getAll<Loan>('loans');
  const l = all.find((x) => x.id === id);
  if (!l) return;
  // Через общее удаление платежа: конверты под него уходят вместе с ним.
  if (l.obligationId) await deleteObligation(l.obligationId);
  // Запланированные досрочные по нему — тоже: долга, который они гасят, больше нет.
  for (const o of (await getAll<Obligation>('obligations')).filter((x) => x.prepay?.loanId === id)) {
    await deleteObligation(o.id);
  }
  if (l.card) await remove('creditTerms', l.card.accountId);
  await remove('loans', id);
}

/** Расписание платежа по кредиту: каждый месяц с даты платежа до конца срока. */
export const loanSchedule = (payDate: PlainDate, endDate?: PlainDate, from?: PlainDate): PaymentSchedule =>
  scheduleFrom({
    anchor: payDate, repeat: 'month',
    ...(endDate ? { end: { kind: 'date' as const, date: endDate } } : {}),
    ...(from ? { from } : {}),
  });

/** Ближайшая дата платежа по кредиту — то, что показывает форма. */
export function loanPayDate(loan: Loan, obligations: readonly Obligation[], today: PlainDate): PlainDate {
  const o = obligations.find((x) => x.id === loan.obligationId);
  return (o ? nextOccurrenceFrom(obligationSchedule(o, today), today) : null)
    ?? nextOccurrenceInclusive(today, loan.paymentDay);
}

/**
 * Привести платёж кредита к условиям из формы кредита.
 *
 * Сумма и название меняются сразу. Расписание — только если тронуты число или
 * срок: тогда оно считается с сегодня, как при правке платежа, и уже
 * подтверждённые оплаты остаются как есть. Не тронуты — календарь не
 * сдвигается от того, что человек поправил ставку.
 */
async function applyLoanPayment(o: Obligation, input: {
  name: string; amount: Money; payDate: PlainDate; endDate?: PlainDate;
  /** Счёт, с которого платится. Не передан — остаётся прежний. */
  accountId?: string;
}): Promise<Obligation> {
  const today = todayPd();
  const was = obligationSchedule(o, today);
  const day = Number(input.payDate.slice(8, 10));
  const moved = was.kind !== 'every' || was.unit !== 'month' || (was.n ?? 1) !== 1
    || Number(was.anchor.slice(8, 10)) !== day
    || (was.until ?? '') !== (input.endDate ?? '');
  const next: Obligation = {
    ...o,
    name: input.name.trim() || o.name,
    kind: 'loan',
    amount: input.amount,
    ...(input.accountId ? { accountId: input.accountId } : {}),
    ...(o.amountType === 'variable' ? { amountEstimate: input.amount } : {}),
    ...(moved ? { schedule: loanSchedule(input.payDate, input.endDate, today), dayOfMonth: day } : {}),
  };
  await put('obligations', next);
  return next;
}

/**
 * Платёж кредита в «Платежах»: уже заведённый или новый.
 *
 * Ипотека обычно уже стоит в обязательных платежах, когда человек решает
 * завести её долгом. Прежде кредит всегда создавал свой платёж, и одна
 * ипотека вычиталась из «Доступно» дважды. Теперь платёж с тем же названием,
 * которым не владеет другой кредит, становится платежом этого кредита — с
 * суммой и датой из формы.
 */
export async function upsertLoanPayment(input: {
  name: string; amount: Money; payDate: PlainDate; endDate?: PlainDate; accountId: string;
  /**
   * Какой платёж взять. Нет — платёж с тем же названием, если такой есть;
   * `'new'` — завести новый; иначе — идентификатор платежа, выбранного в форме.
   */
  target?: string;
}): Promise<Obligation> {
  const owned = new Set((await getAll<Loan>('loans')).map((l) => l.obligationId).filter(Boolean));
  const free = (await getAll<Obligation>('obligations')).filter((o) => o.isActive && !owned.has(o.id));
  const key = input.name.trim().toLowerCase();
  const same = input.target === 'new' ? undefined
    : input.target ? free.find((o) => o.id === input.target)
      : free.find((o) => o.name.trim().toLowerCase() === key);
  if (same) return applyLoanPayment(same, input);
  return addObligation({
    name: input.name, accountId: input.accountId, amount: input.amount,
    dayOfMonth: Number(input.payDate.slice(8, 10)), kind: 'loan',
    schedule: loanSchedule(input.payDate, input.endDate),
  });
}

/**
 * После правки кредита — его платёж в «Платежах» с теми же суммой, датой и
 * сроком. Прежде правка меняла только кредит: «Долги» считали по новой сумме,
 * а из «Доступно» вычиталась старая.
 */
export async function syncLoanPayment(loanId: string, payDate: PlainDate, accountId?: string): Promise<void> {
  const loan = (await getAll<Loan>('loans')).find((l) => l.id === loanId);
  if (!loan) return;
  const o = loan.obligationId
    ? (await getAll<Obligation>('obligations')).find((x) => x.id === loan.obligationId)
    : undefined;
  // Личный долг без графика — платежа нет. Был, а теперь возвращают разом —
  // платёж уходит, иначе он продолжал бы вычитаться из «Доступно».
  if (loan.payment <= ZERO) {
    if (o) await deleteObligation(o.id);
    if (loan.obligationId) await updateLoan(loanId, {}, ['obligationId']);
    return;
  }
  const input = {
    name: loan.name, amount: add(loan.payment, loan.extraMonthly ?? ZERO), payDate,
    ...(loan.endDate ? { endDate: loan.endDate } : {}),
    ...(accountId ? { accountId } : {}),
  };
  if (o) { await applyLoanPayment(o, input); return; }
  // Платежа не было — долг стали возвращать частями: заводим его.
  const fallback = accountId ?? (await getAll<Account>('accounts')).find((a) => !a.archived && a.isLiquid)?.id;
  if (!fallback) return;
  const created = await upsertLoanPayment({ ...input, accountId: fallback });
  await updateLoan(loanId, { obligationId: created.id });
}

/**
 * Факты по долгам из операций: платёж по графику — расход по платежу долга,
 * досрочный — расход со связью с самим долгом.
 */
export function loanFactsOf(
  loans: readonly Loan[], txs: readonly Tx[], obligations: readonly Obligation[] = [],
): Map<string, LoanFact[]> {
  // Запланированный досрочный — разовый платёж в «Платежах»: его «Оплатил»
  // записывает операцию по платежу, а для долга это досрочный взнос.
  const planned = new Map(obligations.filter((o) => o.prepay).map((o) => [o.id, o.prepay!]));
  const out = new Map<string, LoanFact[]>();
  for (const l of loans) {
    if (l.kind === 'credit_card') continue;
    const list: LoanFact[] = [];
    for (const t of txs) {
      if (t.type !== 'expense' || !t.link) continue;
      if (t.link.kind === 'obligation' && l.obligationId && t.link.obligationId === l.obligationId) {
        list.push({ date: t.occurredDate, amount: t.amount, kind: 'payment', at: t.createdAt });
      } else if (t.link.kind === 'obligation' && planned.get(t.link.obligationId)?.loanId === l.id) {
        const plan = planned.get(t.link.obligationId)!;
        list.push({ date: t.occurredDate, amount: t.amount, kind: 'extra', at: t.createdAt, ...(plan.mode === 'payment' ? { mode: 'payment' as const } : {}) });
      } else if (t.link.kind === 'loan' && t.link.loanId === l.id) {
        list.push({ date: t.occurredDate, amount: t.amount, kind: 'extra', at: t.createdAt, ...(t.link.mode === 'payment' ? { mode: 'payment' as const } : {}) });
      }
    }
    if (list.length > 0) out.set(l.id, list);
  }
  return out;
}

/**
 * Платёж кредита в «Платежах» — такой, какой он сейчас.
 *
 * Досрочный с «уменьшить платёж» делает платёж меньше записанного в договоре.
 * Обязательство кредита хранит прежнюю сумму, и без поправки «Можно тратить»
 * держало бы под платёж лишнее, а «Оплатил» предлагал бы старую сумму.
 * Поправка — разница, а не замена: досрочное «каждый месяц» в обязательстве
 * остаётся.
 */
export function withLoanPayments(
  obligations: readonly Obligation[], stored: readonly Loan[], loans: readonly Loan[],
): Obligation[] {
  const cut = new Map<string, Money>();
  for (const l of loans) {
    const was = stored.find((x) => x.id === l.id);
    if (!was || !l.obligationId || l.payment >= was.payment) continue;
    cut.set(l.obligationId, sub(was.payment, l.payment));
  }
  if (cut.size === 0) return [...obligations];
  return obligations.map((o) => (cut.has(o.id) ? { ...o, amount: clampPos(sub(o.amount, cut.get(o.id)!)) } : o));
}

/**
 * Досрочно каждый месяц — частью платежа в «Платежах».
 *
 * Прежде для этого заводилась цель «Закрыть долг», и деньги в ней копились,
 * хотя на деле уходили банку: цель показывала «накоплено», а остаток долга
 * стоял на месте. Теперь сумма просто добавляется к платежу долга: она
 * вычитается из «Доступно» до даты, а «Оплатил» уменьшает остаток.
 */
export async function setLoanExtra(loanId: string, extra: Money, mode: 'term' | 'payment' = 'term'): Promise<void> {
  const loan = (await getAll<Loan>('loans')).find((l) => l.id === loanId);
  if (!loan) return;
  await updateLoan(loanId, extra > ZERO ? { extraMonthly: extra, extraMode: mode } : {},
    extra > ZERO ? [] : ['extraMonthly', 'extraMode']);
  if (!loan.obligationId) return;
  await mutate<Obligation>('obligations', loan.obligationId, (o) => {
    if (!o) return undefined;
    const amount = add(loan.payment, extra > ZERO ? extra : ZERO);
    // Прошлые оплаты остаются при своей сумме (04.11.2026): без снимка
    // требование пересчитывалось по новой — октябрь, оплаченный на 35 862 ₽,
    // после «досрочно каждый месяц 5 000 ₽» становился недоплаченным на 5 000.
    const payments = o.payments?.map((p) => (p.requiredAt === undefined ? { ...p, requiredAt: o.amount } : p));
    return { ...o, amount, ...(payments ? { payments } : {}), ...(o.amountType === 'variable' ? { amountEstimate: amount } : {}) };
  });
}

/**
 * Ставка кредитки — в её кредите. У карты, заведённой до того, как карты
 * попадали в «Долги», кредита нет: он создаётся здесь, с условиями карты.
 */
export async function setCardRate(accountId: string, ratePct: number): Promise<void> {
  const loans = await getAll<Loan>('loans');
  const loan = loans.find((l) => l.kind === 'credit_card' && l.card?.accountId === accountId);
  if (loan) { await updateLoan(loan.id, { ratePct }); return; }
  const acc = (await getAll<Account>('accounts')).find((a) => a.id === accountId);
  const t = (await getAll<CreditTerms>('creditTerms')).find((x) => x.accountId === accountId);
  if (!acc || !t) return;
  await addLoan({
    name: acc.name, kind: 'credit_card', principal: clampPos(kop(-acc.balance)), ratePct,
    payment: ZERO, paymentDay: t.paymentDay, accountId, allowsEarly: true,
    card: {
      accountId, limit: t.limit, statementDebt: t.statementDebt, graceDays: t.graceDays, reserved: t.reserved,
    },
  });
}

/**
 * Разовый досрочный платёж — расходом со связью с долгом, как и платёж по
 * графику. Остаток долга уменьшается на всю сумму.
 */
export async function addLoanPrepayment(input: {
  loanId: string; accountId: string; amount: Money; date: PlainDate;
  /** Время «ЧЧ:ММ» и заметка — как у ручной записи. */
  time?: string; note?: string;
  /** Что сделал банк: сократил срок (по умолчанию) или платёж. */
  mode?: 'term' | 'payment';
  /**
   * Досрочное — вместе с оплатой платежа из его листа: конверт под этот
   * платёж снимается и на эту сумму. Лист обещает «конверт снимется на
   * списанную сумму» — списано и то, что ушло досрочно (21.10.2026).
   */
  envelopeOf?: string;
}): Promise<void> {
  const loan = (await getAll<Loan>('loans')).find((l) => l.id === input.loanId);
  if (!loan) return;
  if (input.mode) await updateLoan(loan.id, { prepayMode: input.mode });
  const o = loan.obligationId
    ? (await getAll<Obligation>('obligations')).find((x) => x.id === loan.obligationId)
    : undefined;
  await addExpense({
    accountId: input.accountId,
    amount: input.amount,
    categoryId: o?.categoryId ?? 'other',
    part: 'need',
    date: input.date,
    ...(input.time ? { time: input.time } : {}),
    note: input.note ?? `Досрочно: ${loan.name}`,
    link: { kind: 'loan', loanId: loan.id, ...(input.mode === 'payment' ? { mode: 'payment' as const } : {}) },
  });
  if (input.envelopeOf) await releaseObligationReserve(input.envelopeOf, input.amount, input.accountId);
}

/**
 * Досрочный в будущий день — план, а не запись.
 *
 * Разовая строка «Досрочно: …» в «Платежах»: до даты «Можно тратить» держит
 * под неё деньги, а «Оплатил» уменьшает долг целиком — так же, как
 * записанный сразу досрочный. Деньги ещё не ушли, и долг до этого не меняется.
 */
export async function planLoanPrepayment(input: {
  loanId: string; accountId: string; amount: Money; date: PlainDate; mode: 'term' | 'payment';
}): Promise<Obligation | null> {
  const loan = (await getAll<Loan>('loans')).find((l) => l.id === input.loanId);
  if (!loan) return null;
  const own = loan.obligationId
    ? (await getAll<Obligation>('obligations')).find((x) => x.id === loan.obligationId)
    : undefined;
  const o: Obligation = {
    id: uid(),
    name: `Досрочно: ${loan.name}`,
    kind: 'loan',
    amountType: 'fixed',
    amount: input.amount,
    schedule: { kind: 'once', anchor: input.date, shiftRule: 'exact', from: input.date },
    dayOfMonth: Number(input.date.slice(8, 10)),
    shiftRule: 'exact',
    accountId: input.accountId,
    ...(own?.categoryId ? { categoryId: own.categoryId } : {}),
    part: 'need',
    prepay: { loanId: loan.id, mode: input.mode },
    isActive: true,
  };
  await put('obligations', o);
  await updateLoan(loan.id, { prepayMode: input.mode });
  return o;
}

// ────────────────────────────────────────────────────────────── счета

export async function addAccount(input: {
  name: string;
  kind: Account['kind'];
  balance: Money;
  isLiquid?: boolean;
  /** Значок, выбранный при создании. Пусто — значок вида счёта. */
  icon?: string;
  /** Цвет значка. Пусто — цвет вида счёта. */
  color?: string;
}): Promise<Account> {
  const acc: Account = {
    id: uid(),
    name: input.name.trim(),
    kind: input.kind,
    balance: input.balance,
    ...(input.icon ? { icon: input.icon } : {}),
    ...(input.color ? { color: input.color } : {}),
    // Кредитка не участвует в «Доступно» никогда: кредитный лимит —
    // не деньги пользователя (PRD §2.1).
    isLiquid: input.isLiquid ?? input.kind !== 'credit_card',
    includeInNetWorth: true,
    // Остаток назван сегодня: всё, что случилось раньше, в нём уже учтено.
    balanceAsOf: todayPd(),
  };
  await put('accounts', acc);
  return acc;
}

export async function addCreditTerms(t: CreditTerms): Promise<void> {
  await put('creditTerms', t);
}

/**
 * Завести кредитку: счёт, условия карты и кредит в «Долгах».
 *
 * Один путь для настройки и для «+» на экране «Счета». Прежде второй заводил
 * только счёт с условиями, и карта не попадала в «Долги»: ни в сумму долгов,
 * ни в очередь гашения.
 */
export async function addCreditCard(input: {
  name: string;
  /** Текущий долг, положительным числом. */
  debt: Money;
  limit: Money;
  /** Сколько внести для беспроцентного периода. */
  statementDebt: Money;
  reserved: Money;
  paymentDay: number;
  graceDays: number;
  ratePct: number;
  icon?: string;
  color?: string;
}): Promise<{ account: Account; loan: Loan }> {
  const account = await addAccount({
    name: input.name, kind: 'credit_card', balance: kop(-input.debt),
    ...(input.icon ? { icon: input.icon } : {}),
    ...(input.color ? { color: input.color } : {}),
  });
  await addCreditTerms({
    accountId: account.id,
    limit: input.limit,
    statementDebt: input.statementDebt,
    reserved: input.reserved,
    paymentDay: input.paymentDay,
    graceDays: input.graceDays,
  });
  // Карта — такой же кредит, как ипотека: у неё есть остаток, ставка и
  // платёж. Отличают её грейс, лимит и растущий от трат долг, и это
  // поля, а не отдельная сущность.
  const loan = await addLoan({
    name: account.name, kind: 'credit_card',
    principal: input.debt,
    ratePct: input.ratePct,
    payment: ZERO,
    paymentDay: input.paymentDay,
    accountId: account.id,
    allowsEarly: true,
    card: {
      accountId: account.id,
      limit: input.limit,
      statementDebt: input.statementDebt,
      graceDays: input.graceDays,
      reserved: input.reserved,
    },
  });
  return { account, loan };
}

/**
 * Сколько уже отложено под платёж по кредитке.
 *
 * Из «Доступно» вычитается только недобор резерва, а не весь платёж
 * (PRD §2.6). Пока отложенное некуда записать, предупреждение висит вечно
 * и цифра занижается на всю сумму долга.
 */
export async function upsertCreditTerms(
  accountId: string,
  patch: Partial<Omit<CreditTerms, 'accountId'>>,
): Promise<void> {
  const all = await getAll<CreditTerms>('creditTerms');
  const existing = all.find((x) => x.accountId === accountId);
  const base: CreditTerms = existing ?? {
    accountId,
    limit: ZERO,
    statementDebt: ZERO,
    reserved: ZERO,
    paymentDay: 10,
    graceDays: 55,
  };
  await put('creditTerms', { ...base, ...patch });
}

export async function setCreditReserved(accountId: string, reserved: Money): Promise<void> {
  const all = await getAll<CreditTerms>('creditTerms');
  const t = all.find((x) => x.accountId === accountId);
  if (!t) return;
  await put('creditTerms', { ...t, reserved });
}

export async function updateAccount(
  id: string,
  patch: Partial<Pick<Account, 'name' | 'balance' | 'isLiquid' | 'icon' | 'color' | 'kind'>>,
  /** На какую дату назван остаток. По умолчанию — сегодня. */
  asOf?: PlainDate,
): Promise<void> {
  const all = await getAll<Account>('accounts');
  const acc = all.find((a) => a.id === id);
  if (!acc) return;
  // Кредитка — не просто вид: у неё лимит, условия и долг в «Долгах». Сменить
  // вид в обе стороны значило бы потерять их молча.
  if (patch.kind && patch.kind !== acc.kind && (patch.kind === 'credit_card' || acc.kind === 'credit_card')) {
    throw new Error('Кредитку нельзя сделать обычным счётом, а обычный счёт — кредиткой');
  }
  // Названный остаток всегда относится к какой-то дате. Без неё нельзя
  // отличить «эта трата уже учтена» от «её ещё нужно списать».
  //
  // Совпадение с прежним числом дату не отменяет: «посмотрел, там столько же»
  // — такая же свежая сверка, как и «стало меньше». Раньше она не засчитывалась,
  // и дата сверки застревала в прошлом ровно у того, кто аккуратно проверяет
  // счёт и ничего с тех пор не потратил.
  //
  // Имя, значок, цвет и вид сверкой не являются: правка названия не должна
  // переносить дату остатка — иначе трата задним числом молча перестаёт
  // списываться.
  const dated = patch.balance !== undefined
    ? { balanceAsOf: asOf ?? todayPd() }
    : {};
  const next: Account = { ...acc, ...patch, ...dated };
  if (patch.icon === '') delete next.icon;
  if (patch.color === '') delete next.color;
  await put('accounts', next);
}

export async function archiveAccount(id: string): Promise<void> {
  const all = await getAll<Account>('accounts');
  const acc = all.find((a) => a.id === id);
  if (!acc) return;
  await put('accounts', { ...acc, archived: true, archivedAt: todayPd() });
}

/** Вернуть счёт из архива: «Архивировать» не должно быть дорогой в один конец. */
export async function restoreAccount(id: string): Promise<void> {
  const all = await getAll<Account>('accounts');
  const acc = all.find((a) => a.id === id);
  if (!acc) return;
  const { archived: _archived, archivedAt: _at, ...rest } = acc;
  await put('accounts', rest);
}

/**
 * Порядок счетов, расставленный человеком: группы и счета внутри групп.
 *
 * `ids` — все счета в новом порядке; место внутри группы — номер по этому
 * списку. Пишем только изменившиеся: перестановка двух карт не должна
 * переписывать каждый счёт в снимке.
 */
export async function saveAccountOrder(groups: readonly string[], ids: readonly string[]): Promise<void> {
  await setMeta('accountGroups', groups.length > 0 ? [...groups] : null);
  await transact(['accounts'], async (db) => {
    const all = await db.all<Account>('accounts');
    ids.forEach((id, i) => {
      const a = all.find((x) => x.id === id);
      if (a && a.order !== i) db.put('accounts', { ...a, order: i });
    });
  });
}

// ──────────────────────────────────────────────────── корректировка остатка

/** Сколько было на счёте перед моментом `at` — по записям. */
async function balanceBefore(accountId: string, at: string, except?: string): Promise<Money> {
  const acc = (await getAll<Account>('accounts')).find((a) => a.id === accountId);
  if (!acc) throw new Error('Счёт не найден');
  const later = (await getAll<Tx>('transactions')).filter((t) => t.id !== except && t.occurredAt > at);
  return sub(acc.balance, add(ZERO, ...later.map((t) => effectOn(t, accountId))));
}

/** Время операции из даты и «ЧЧ:ММ» — по тем же правилам, что у новой записи. */
const momentOf = (date: PlainDate, time?: string): string => (/^\d{2}:\d{2}$/.test(time ?? '')
  ? `${date}T${time}:00.000Z`
  : date === todayPd() ? localMoment() : `${date}T12:00:00.000Z`);

/**
 * Закреплённое на счёте сверх остатка — уменьшить до остатка.
 *
 * Сначала цели, потом платежи: отложенное на цель можно отложить снова, а
 * недобор под платёж человек увидит в плане. Запас стабильного бюджета не
 * трогаем — у него своя сверка.
 */
async function trimReserves(accountId: string): Promise<void> {
  const acc = (await getAll<Account>('accounts')).find((a) => a.id === accountId);
  if (!acc) return;
  const mine = (await getAll<Reserve>('reserves')).filter((r) => r.accountId === accountId);
  let over = sub(add(ZERO, ...mine.map((r) => r.amount)), clampPos(acc.balance));
  const rank = (r: Reserve) => (r.forKind === 'goal' ? 0 : r.forKind === 'planned' ? 1 : 2);
  for (const r of mine.filter((x) => x.forKind !== 'stable').sort((a, b) => rank(a) - rank(b) || b.amount - a.amount)) {
    if (over <= ZERO) break;
    const cut = minM(r.amount, over);
    over = sub(over, cut);
    if (cut === r.amount) await remove('reserves', r.id);
    else await put('reserves', { ...r, amount: sub(r.amount, cut) });
  }
}

/** Категория для процентов и комиссий по кредитке: своя, если есть, иначе «Прочее». */
async function feesCategory(): Promise<{ id: string; part: PlanPart }> {
  const every = await getAll<Category>('categories');
  const all = every.filter((c) => !c.archived && (c.kind ?? 'expense') === 'expense');
  const own = all.find((c) => c.id === 'other.fees') ?? all.find((c) => /процент|комисс/i.test(c.name));
  if (own) return { id: own.id, part: own.defaultPart === 'want' ? 'want' : 'need' };
  // Своей нет, а стандартную человек не убирал — стандартная: справочник мог
  // ещё не разложиться. Убрал — в «Прочее».
  return every.some((c) => c.id === 'other.fees') ? { id: 'other', part: 'need' } : { id: 'other.fees', part: 'need' };
}

/**
 * Скорректировать остаток: «сейчас на счёте столько».
 *
 * Записывается операция «Корректировка» на разницу — не трата и не доход: в
 * расходы, категории и план она не входит. Остаток после неё держится на
 * названном числе: трата задним числом, найденная потом в выписке, остаток
 * не трогает, а уменьшает корректировку (`shiftAt`).
 *
 * Разницы нет — записывать нечего, возвращается `null`.
 */
export async function adjustBalance(input: {
  accountId: string;
  /** Сколько на счёте стало. У кредитки — минусом: это долг. */
  target: Money;
  date: PlainDate;
  time?: string;
  note?: string;
  /** Под платежи и цели закреплено больше, чем останется, — уменьшить закреплённое. */
  trim?: boolean;
  /** Кредитка: выросший долг — это проценты и комиссии, записать тратой. */
  asFees?: boolean;
}): Promise<Tx | null> {
  const acc = (await getAll<Account>('accounts')).find((a) => a.id === input.accountId);
  if (!acc) throw new Error('Счёт не найден');
  if (acc.balanceAsOf && input.date < acc.balanceAsOf) {
    throw new Error('Остаток этого счёта назван позже — корректировать раньше этой даты нельзя');
  }
  const at = momentOf(input.date, input.time);
  const amount = sub(input.target, await balanceBefore(acc.id, at));
  if (amount === ZERO) return null;

  if (input.asFees && acc.kind === 'credit_card' && amount < ZERO) {
    const fees = await feesCategory();
    return addExpense({
      accountId: acc.id, amount: neg(amount), categoryId: fees.id, part: fees.part, date: input.date,
      ...(input.time ? { time: input.time } : {}), ...(input.note ? { note: input.note } : {}),
      applyToBalance: true,
    });
  }

  const tx: Tx = {
    ...makeTx({
      ...(await allocationTag()),
      type: 'adjustment', accountId: acc.id, amount, date: input.date,
      ...(input.time ? { time: input.time } : {}), ...(input.note ? { note: input.note } : {}),
    }),
    occurredAt: at,
    target: input.target,
    initial: amount,
  };
  await putAll('transactions', [tx]);
  await shiftAt(acc.id, at, amount, tx.id);
  if (input.trim) await trimReserves(acc.id);
  return tx;
}

/**
 * Правка корректировки: другой остаток, дата или заметка.
 *
 * Разница считается заново от названного остатка — так же, как при записи.
 * Правка человека — новая точка отсчёта: «уточнилась» после неё не пишем.
 */
export async function updateAdjustment(id: string, patch: {
  target?: Money; date?: PlainDate; time?: string; note?: string;
}): Promise<void> {
  const old = (await getAll<Tx>('transactions')).find((t) => t.id === id);
  if (!old || old.type !== 'adjustment') throw new Error('Корректировка не найдена');
  const acc = (await getAll<Account>('accounts')).find((a) => a.id === old.accountId);
  const date = patch.date ?? old.occurredDate;
  if (acc?.balanceAsOf && date < acc.balanceAsOf) {
    throw new Error('Остаток этого счёта назван позже — корректировать раньше этой даты нельзя');
  }
  await reverseOnAccounts(old);
  const at = /^\d{2}:\d{2}$/.test(patch.time ?? '') || (patch.date && patch.date !== old.occurredDate)
    ? momentOf(date, patch.time) : old.occurredAt;
  // Целевой остаток — названный прежде либо, у старой записи без него, тот,
  // что получался с её разницей.
  const target = patch.target ?? old.target ?? add(await balanceBefore(old.accountId, old.occurredAt, old.id), old.amount);
  const amount = sub(target, await balanceBefore(old.accountId, at, old.id));
  const next: Tx = {
    ...old, amount, target, initial: amount, occurredAt: at, occurredDate: date,
    ...(patch.note !== undefined ? { note: patch.note } : {}),
  };
  if (next.note === '') delete next.note;
  await put('transactions', next);
  await applyOnAccounts(next);
}

/**
 * «Это были траты»: корректировка в минус становится обычной тратой.
 *
 * Остаток не меняется — меняется смысл записи: деньги ушли на эту категорию,
 * и теперь они в расходах, «Аналитике» и плане.
 */
export async function adjustmentToExpense(id: string, categoryId: string, part: PlanPart): Promise<void> {
  const old = (await getAll<Tx>('transactions')).find((t) => t.id === id);
  if (!old || old.type !== 'adjustment') throw new Error('Корректировка не найдена');
  if (old.amount >= ZERO) throw new Error('Тратой может стать только корректировка в минус');
  const { target: _target, initial: _initial, ...rest } = old;
  await put('transactions', { ...rest, type: 'expense', amount: neg(old.amount), categoryId, part });
}

/**
 * Цель с уже накопленной суммой.
 *
 * Накопленное — это деньги, которые физически лежат на каком-то счёте
 * (инвариант И5: счёт ≠ цель). Если этот счёт помечен ликвидным, те же деньги
 * попадут и в «Доступно» — поэтому счёт под накопления нужно помечать
 * «не для трат».
 */
export async function addGoal(input: {
  name: string;
  targetAmount: Money;
  currentAmount: Money;
  targetDate?: PlainDate;
  /** С какого дня копим. Без неё цель просит деньги сразу. */
  startDate?: PlainDate;
  monthlyContribution: Money;
  kind: Goal['kind'];
  priority: Goal['priority'];
  /** Долг, который цель закрывает досрочно: от его ставки считается очередь. */
  loanId?: string;
  /** Где копим: счёт цели. */
  accountId?: string;
  // Возвращает созданную цель — как и остальные `add*`: иначе вызывающему
  // коду негде взять её идентификатор.
}): Promise<Goal> {
  const g: Goal = {
    id: uid(),
    name: input.name.trim(),
    kind: input.kind,
    targetAmount: input.targetAmount,
    currentAmount: input.currentAmount,
    ...(input.targetDate ? { targetDate: input.targetDate } : {}),
    ...(input.startDate ? { startDate: input.startDate } : {}),
    ...(input.loanId ? { loanId: input.loanId } : {}),
    ...(input.accountId !== undefined ? { accountId: input.accountId } : {}),
    priority: input.priority,
    monthlyContribution: input.monthlyContribution,
    contributionMode: input.monthlyContribution > ZERO ? 'fixed' : 'residual',
    fundedInPeriod: false,
    status: 'active',
  };
  await put('goals', g);
  return g;
}

export async function deleteGoal(id: string): Promise<void> {
  const reserves = await getAll<Reserve>('reserves');
  for (const r of reserves.filter((x) => x.forKind === 'goal' && x.forId === id)) {
    await remove('reserves', r.id);
  }
  await remove('goals', id);
}

// ──────────────────────────────────────────────────── доход и обязательства

export async function addIncome(input: {
  name: string; accountId: string; amount: Money; dayOfMonth: number; isVariable: boolean;
  /** Ритм поступлений. Без него — прежняя модель «каждый месяц по числу». */
  rhythm?: IncomeRhythm;
  amountBasis?: IncomeAmountBasis;
  /** Задаёт ли источник срок. По умолчанию да. */
  horizonRole?: HorizonRole;
  /** Аванс к зарплате: срок задаёт, период аналитики не режет. */
  advance?: boolean;
}): Promise<IncomeStream> {
  const rhythm = input.rhythm
    ?? { kind: 'dates' as const, days: [input.dayOfMonth], shiftRule: 'previous_business_day' as const };
  const s: IncomeStream = {
    id: uid(),
    name: input.name.trim() || 'Зарплата',
    accountId: input.accountId,
    amount: input.amount,
    isVariable: input.isVariable,
    rhythm,
    amountBasis: input.amountBasis ?? (input.isVariable ? 'estimate' : 'known'),
    horizonRole: input.horizonRole ?? 'checkpoint',
    ...(input.advance ? { advance: true } : {}),
    // `dayOfMonth` остаётся ради старых читателей: они ничего не знают о
    // ритме, а первое число из списка описывает ту же дату.
    dayOfMonth: rhythm.kind === 'dates' ? (rhythm.days[0] ?? input.dayOfMonth) : input.dayOfMonth,
    // Зарплату переносят на предшествующий рабочий день — это норма в РФ,
    // и от неё зависит горизонт «до зарплаты».
    shiftRule: 'previous_business_day',
    isActive: true,
  };
  await put('incomeStreams', s);
  return s;
}

export async function addObligation(input: {
  name: string; accountId: string; amount: Money; dayOfMonth: number;
  kind?: Obligation['kind']; isVariable?: boolean;
  /**
   * Периодичность. По умолчанию ежемесячно — как было до появления расписаний.
   * Годовая страховка с этим полем перестаёт считаться ежемесячным платежом.
   */
  every?: { n: number; unit: 'day' | 'week' | 'month' | 'year' };
  /** Дата первой оплаты. Нужна для годовых и недельных: числа месяца им мало. */
  firstDate?: PlainDate;
  /**
   * Готовое расписание. Формы собирают его общим правилом (`repeat.ts`), и
   * тогда `every`/`firstDate` не нужны: здесь уже всё — ритм, окончание и
   * дата, с которой расписание действует.
   */
  schedule?: PaymentSchedule;
  /** Возвращает созданное: онбордингу нужно сразу отметить уже оплаченные. */
}): Promise<Obligation> {
  const anchor = input.firstDate
    ?? anchorForDayOfMonth(input.dayOfMonth, todayPd());
  const o: Obligation = {
    id: uid(),
    name: input.name.trim(),
    kind: input.kind ?? 'other',
    amountType: input.isVariable ? 'variable' : 'fixed',
    // Сумма одной оплаты, а не месячная нагрузка: для годового платежа это
    // разные числа, и второе нигде не хранится.
    amount: input.amount,
    ...(input.isVariable ? { amountEstimate: input.amount } : {}),
    // Готовое расписание главнее собранного из кусков: формы отдают его
    // целиком, и разовый платёж иначе не выразить вовсе.
    ...(input.schedule
      ? { schedule: input.schedule }
      : input.every && !(input.every.n === 1 && input.every.unit === 'month')
        ? {
          schedule: {
            kind: 'every' as const,
            n: input.every.n,
            unit: input.every.unit,
            anchor,
            shiftRule: 'exact' as const,
            ...(input.firstDate ? { from: input.firstDate } : {}),
          },
        }
        : {}),
    // День месяца держим согласованным с расписанием: два разных ответа на
    // вопрос «какого числа» рано или поздно разойдутся на экране.
    dayOfMonth: input.schedule
      ? Number(input.schedule.anchor.slice(8, 10))
      : input.dayOfMonth,
    shiftRule: 'exact',
    accountId: input.accountId,
    part: 'need',
    isActive: true,
  };
  await put('obligations', o);
  return o;
}

/**
 * Конверт: закрепить часть остатка счёта под требование.
 *
 * Резерв всегда лежит на конкретном счёте — иначе одни деньги можно посчитать
 * дважды: как свободный остаток и как уже закрывающие платёж. Именно на этом
 * ловилась цифра «Доступно».
 */
/**
 * Приходы, уже записанные фактом, — в том виде, в каком их читает прогноз.
 *
 * Одна функция на всё приложение намеренно: два места, выводящие «что уже
 * пришло» по-разному, — это два разных ответа на вопрос, сколько у человека
 * денег. Разовые приходы (подарок, продажа) сюда не попадают: они ничей
 * ожидаемый доход не закрывают.
 */
export const receivedIncomes = (txs: readonly Tx[]): ReceivedIncome[] => txs
  .filter((t): t is Tx & { link: { kind: 'income'; incomeStreamId: string } } => (
    t.type === 'income' && t.link?.kind === 'income' && !!t.link.incomeStreamId
  ))
  .map((t) => ({
    incomeStreamId: t.link.incomeStreamId,
    date: t.occurredDate,
    ...(t.createdAt ? { at: t.createdAt } : {}),
    ...((t.link as { expectedRest?: Money }).expectedRest !== undefined
      ? { rest: (t.link as { expectedRest?: Money }).expectedRest! }
      : {}),
  }));

// ─────────────────────────────────────────── назначения будущих денег

/**
 * Назначить будущий приход на платёж или цель.
 *
 * Денег на счетах это не двигает и конверта не создаёт: их ещё нет. Всё, что
 * здесь появляется, — решение человека о том, куда пойдут ожидаемые деньги.
 * Конверт возникнет позже, когда приход станет фактом и человек подтвердит
 * исполнение (`executeAllocation`).
 *
 * `available` — сколько ещё свободно у этого прихода. Назначить больше нельзя:
 * одни и те же деньги, обещанные дважды, выглядят на экране как вдвое большая
 * сумма, которой не существует.
 */
export async function planFromIncome(input: {
  /** Откуда придут деньги: наступление дохода или названное ожидание. */
  source: FutureMoney;
  /** Когда деньги ждут. Для ожидания — его дата. */
  expectedDate: PlainDate;
  amount: Money;
  target: PlannedAllocation['target'];
  /** Сколько ожидается от этого прихода. Без оценки назначать не из чего. */
  expected: Money;
}): Promise<{ allocation: PlannedAllocation | null; free: Money }> {
  const all = await getAll<PlannedAllocation>('plannedAllocations');
  const free = freeFromIncome(all, { source: input.source, amount: input.expected });
  if (input.amount <= ZERO || input.amount > free) return { allocation: null, free };

  const allocation: PlannedAllocation = {
    id: uid(),
    source: input.source,
    // Старые поля пишутся ради читателей, не знающих про источник: у
    // ожидания потока их нет, и тогда там пусто по смыслу, а не по забывчивости.
    incomeStreamId: input.source.kind === 'stream_occurrence' ? input.source.incomeStreamId : '',
    expectedDate: input.source.kind === 'stream_occurrence'
      ? input.source.expectedDate
      : input.expectedDate,
    amount: input.amount,
    target: input.target,
    createdAt: new Date().toISOString(),
    status: 'planned',
  };
  await put('plannedAllocations', allocation);
  return { allocation, free: sub(free, input.amount) };
}

/** Отменить назначение: деньги снова свободны для другого решения. */
export async function cancelAllocation(id: string): Promise<void> {
  await mutate<PlannedAllocation>('plannedAllocations', id, (a) => (
    a && a.status !== 'done' ? { ...a, status: 'cancelled' } : a
  ));
}

/**
 * Назначения, которые ждут исполнения после записанного прихода.
 *
 * Само по себе наступление даты ничего не подтверждает: деньги либо пришли,
 * либо нет, и знает об этом человек, а не календарь. Поэтому список строится
 * от **факта** прихода.
 */
export async function allocationsToExecute(
  incomeStreamId: string, date: PlainDate,
): Promise<PlannedAllocation[]> {
  return fulfilledBy(await getAll<PlannedAllocation>('plannedAllocations'), incomeStreamId, date);
}

/**
 * Исполнить назначение: превратить обещание в закреплённые деньги.
 *
 * Делается только по подтверждению человека. Тихо закрепить деньги, потому
 * что «по плану так и было», нельзя: приход мог оказаться меньше, а планы к
 * этому дню — другими.
 *
 * Повтор безопасен: исполненное назначение второй раз конверт не увеличивает.
 */
export async function executeAllocation(input: {
  id: string;
  /** Счёт, на который пришли деньги: конверт всегда принадлежит счёту. */
  accountId: string;
  /** Сколько закрепить на самом деле. Меньше назначенного — обычное дело. */
  amount?: Money;
  cycleStart?: PlainDate;
}): Promise<{ done: boolean; amount: Money }> {
  const all = await getAll<PlannedAllocation>('plannedAllocations');
  const a = all.find((x) => x.id === input.id);
  if (!a || a.status === 'done' || a.status === 'cancelled') return { done: false, amount: ZERO };

  const amount = input.amount !== undefined && input.amount < a.amount ? input.amount : a.amount;
  if (amount <= ZERO) return { done: false, amount: ZERO };

  // Отметка ставится первой: иначе повтор доставки между записью конверта и
  // отметкой закрепил бы деньги дважды.
  let claimed = false;
  await mutate<PlannedAllocation>('plannedAllocations', input.id, (cur) => {
    if (!cur || cur.status === 'done' || cur.status === 'cancelled') return cur;
    claimed = true;
    return { ...cur, status: 'done', amount };
  });
  if (!claimed) return { done: false, amount: ZERO };

  if (a.target.kind === 'obligation') {
    const { obligationId } = a.target;
    // Конверт этого счёта, а не сумма конвертов всех счетов: чужие деньги
    // прибавлять к своим нельзя.
    const here = (await getAll<Reserve>('reserves')).find(
      (x) => x.forKind === 'obligation' && x.forId === obligationId
        && x.accountId === input.accountId,
    );
    await setReserve({
      accountId: input.accountId,
      forKind: 'obligation',
      forId: obligationId,
      amount: add(here?.amount ?? ZERO, amount),
    });
  } else if (a.target.kind === 'card') {
    // Кредитка: конверт под её платёж на счёте прихода — так же, как «Отложено на платёж».
    // С датой платежа (04.11.2026): назначенное на декабрьский платёж не должно уйти в ноябрьский.
    const { accountId, dueDate } = a.target;
    const here = (await getAll<Reserve>('reserves')).find(
      (x) => x.forKind === 'credit' && x.forId === accountId && x.accountId === input.accountId && x.forDate === dueDate,
    );
    await setReserve({
      accountId: input.accountId,
      forKind: 'credit',
      forId: accountId,
      amount: add(here?.amount ?? ZERO, amount),
      forDate: dueDate,
    });
  } else {
    await fundGoal({
      goalId: a.target.goalId,
      requestId: `allocation:${a.id}`,
      amount,
      cycleStart: input.cycleStart ?? todayPd(),
      fromAccountId: input.accountId,
    });
  }
  return { done: true, amount };
}

export async function setReserve(input: {
  accountId: string;
  forKind: ReserveTarget;
  forId: string;
  amount: Money;
  /** Конверт под карту к более позднему платежу (04.11.2026); без даты — к ближайшему. */
  forDate?: PlainDate;
}): Promise<void> {
  const all = await getAll<Reserve>('reserves');
  const existing = all.find(
    (r) => r.accountId === input.accountId && r.forKind === input.forKind && r.forId === input.forId
      && (r.forDate ?? null) === (input.forDate ?? null),
  );
  if (input.amount <= ZERO) {
    if (existing) await remove('reserves', existing.id);
    return;
  }
  await put('reserves', { id: existing?.id ?? uid(), ...input });
}

/**
 * Доложить в конверт на этом счёте: «Отложить 9 000 ₽ с «Наличные»».
 *
 * Прибавляет к тому, что уже закреплено, а не заменяет: конверт «Няня» на
 * 9 000 ₽ держит оплату 16-го, и 9 000 под 23-е ложатся рядом.
 */
export async function topUpReserve(input: { accountId: string; forKind: ReserveTarget; forId: string; amount: Money }): Promise<void> {
  if (input.amount <= ZERO) return;
  const all = await getAll<Reserve>('reserves');
  // Конверт с датой (на более поздний платёж карты) — отдельный: сюда не прибавляется.
  const existing = all.find((r) => r.accountId === input.accountId && r.forKind === input.forKind && r.forId === input.forId && !r.forDate);
  await setReserve({ ...input, amount: add(existing?.amount ?? ZERO, input.amount) });
}

/**
 * Подтвердить взнос в цель за этот приход.
 *
 * Деньги переводит человек — приложение только записывает то, что уже
 * случилось. Поэтому способов два, и оба настоящие:
 *
 * - **перевёл на другой счёт** — создаётся перевод, и деньги физически уходят
 *   с тратимого счёта;
 * - **оставил на том же счёте** — создаётся конверт, потому что иначе те же
 *   деньги останутся в «Доступно» и будут потрачены.
 *
 * Отметка ставится датой прихода, а не булевым: сброс нового цикла делает
 * календарь, а не обещание когда-нибудь сбросить флаг.
 */
/**
 * Событие цели: взнос, пропуск или изъятие.
 *
 * `Goal.fundedCycle` хранит одну дату и ставится одинаково и при взносе, и
 * при пропуске — по нему нельзя отличить «отложил» от «в этом месяце не
 * получилось». Журнал различает их и хранит способ: перевод, конверт на том
 * же счёте или ручная правка.
 *
 * Живёт в сторе `goalAllocations`, который был объявлен и не использовался.
 */
export interface GoalEvent {
  /** У взноса свой ключ: несколько взносов одного цикла не затирают друг друга. */
  key: string;
  goalId: string;
  cycleStart: PlainDate;
  date: PlainDate;
  kind: 'contribution' | 'skip' | 'withdrawal';
  amount: Money;
  via: 'transfer' | 'reserve' | 'manual';
  txId?: string;
  /** Новый перевод закреплён под цель; старые записи без метки не трогаем. */
  reservedAccountId?: string;
  createdAt: string;
}

async function recordGoalEvent(e: Omit<GoalEvent, 'key' | 'createdAt'>, key = `${e.goalId}:${e.cycleStart}:${e.kind}`): Promise<void> {
  await put('goalAllocations', {
    ...e,
    key,
    createdAt: new Date().toISOString(),
  } satisfies GoalEvent);
}

export const goalEvents = (): Promise<GoalEvent[]> => getAll<GoalEvent>('goalAllocations');

export async function fundGoal(input: {
  goalId: string;
  amount: Money;
  cycleStart: PlainDate;
  /** Куда отложено. Без счёта — деньги остаются на месте под конвертом. */
  fromAccountId?: string;
  toAccountId?: string;
  /** Когда отложено — из формы записи; по умолчанию сегодня. */
  date?: PlainDate;
  time?: string;
  note?: string;
  /** Один ключ на подтверждение; повтор доставки не создаёт второй взнос. */
  requestId?: string;
}): Promise<void> {
  const prior = goalFundQueue.get(input.goalId) ?? Promise.resolve();
  const next = prior.then(() => fundGoalOnce(input), () => fundGoalOnce(input));
  goalFundQueue.set(input.goalId, next.then(() => undefined, () => undefined));
  return next;
}

const goalFundQueue = new Map<string, Promise<void>>();

async function fundGoalOnce(input: Parameters<typeof fundGoal>[0]): Promise<void> {
  const goals = await getAll<Goal>('goals');
  const g = goals.find((x) => x.id === input.goalId);
  if (!g) return;

  // Старые вызовы без ключа сохраняют защиту от точного повтора.
  // Формы передают отдельный ключ для каждого настоящего подтверждения.
  const request = input.requestId ?? JSON.stringify(['legacy', input.amount, input.fromAccountId,
    input.toAccountId, input.date ?? todayPd(), input.time, input.note]);
  const eventKey = `${g.id}:${input.cycleStart}:contribution:${request}`;
  if ((await goalEvents()).some((e) => e.key === eventKey)) return;

  let txId: string | undefined;
  let via: GoalEvent['via'] = 'manual';
  if (input.amount > ZERO && input.fromAccountId && input.toAccountId
    && input.fromAccountId !== input.toAccountId) {
    const tx = await addTransfer({
      fromId: input.fromAccountId, toId: input.toAccountId,
      amount: input.amount, date: input.date ?? todayPd(),
      ...(input.time ? { time: input.time } : {}),
      note: input.note || `Взнос в цель «${g.name}»`,
      // Имя цели в примечании — текст, а не связь: по нему нельзя собрать
      // историю взносов, и после переименования цели след теряется.
      link: { kind: 'goal', goalId: g.id },
    });
    txId = tx.id;
    via = 'transfer';
    // Перевод между двумя тратимыми счетами сам по себе не убирает деньги
    // из «Можно тратить». Закрепляем взнос и на счёте назначения.
    await shiftGoalReserve(g.id, input.toAccountId, input.amount);
  } else if (input.amount > ZERO && input.fromAccountId) {
    // Деньги остались на том же счёте — держим их конвертом, иначе они
    // попадут в «Доступно» и будут потрачены ещё раз.
    const existing = (await getAll<Reserve>('reserves')).find(
      (r) => r.forKind === 'goal' && r.forId === g.id && r.accountId === input.fromAccountId,
    );
    await setReserve({
      accountId: input.fromAccountId, forKind: 'goal', forId: g.id,
      amount: add(existing?.amount ?? ZERO, input.amount),
    });
    // Взнос на том же счёте операции не создаёт: деньги никуда не уходили.
    // Без журнала он не оставлял следа вовсе.
    via = 'reserve';
  }

  await put('goals', {
    ...g,
    currentAmount: add(g.currentAmount, input.amount),
    fundedCycle: input.cycleStart,
  });

  await recordGoalEvent({
    goalId: g.id,
    cycleStart: input.cycleStart,
    date: input.date ?? todayPd(),
    kind: 'contribution',
    amount: input.amount,
    via,
    ...(txId ? { txId, reservedAccountId: input.toAccountId } : {}),
  }, eventKey);
}

/**
 * Пропустить взнос в этом цикле.
 *
 * Не «отменить цель»: в этом месяце могла случиться покупка. Деньги остаются
 * свободными, просьба уходит до следующего прихода, а цель отодвигается — и
 * это честнее, чем висящее напоминание, которое человек научится не замечать.
 */
export async function skipGoalThisCycle(goalId: string, cycleStart: PlainDate): Promise<void> {
  const goals = await getAll<Goal>('goals');
  const g = goals.find((x) => x.id === goalId);
  if (!g) return;
  await put('goals', { ...g, fundedCycle: cycleStart });
  // Пропуск ставит тот же `fundedCycle`, что и взнос: просьба отложить уходит
  // до следующего прихода. Но это разные события, и «способность копить»
  // по ним считается по-разному — журнал их различает.
  await recordGoalEvent({
    goalId: g.id,
    cycleStart,
    date: todayPd(),
    kind: 'skip',
    amount: ZERO,
    via: 'manual',
  });
}

// ──────────────────────────────────────────────── разовые приходы

/**
 * Записать приход, которого ждут.
 *
 * Деньгами он от этого не становится: в «Доступно» ожидаемое не входит
 * (см. `arrivals.ts`). Запись меняет другое — у нехватки появляется дата, а у
 * приложения появляется, о чём спросить, когда день настанет.
 */
export async function addExpectedIncome(input: {
  name: string;
  amount: Money;
  expectedDate: PlainDate;
  accountId: string;
  /** Источник, если это наступление нерегулярного дохода, а не разовые деньги. */
  streamId?: string;
  /** Сумму можно не знать: «жду гонорар, сколько — не скажу». */
  amountBasis?: IncomeAmountBasis;
  /**
   * Должны ли текущие деньги дожить до этого прихода.
   *
   * По умолчанию нет: обещанный возврат за куртку не может быть следующей
   * точкой планирования. Сказать обратное человек может явно.
   */
  horizonRole?: HorizonRole;
}): Promise<IncomeExpectation> {
  const income: IncomeExpectation = {
    id: uid(),
    name: input.name.trim() || 'Приход',
    amount: input.amount,
    // Человек назвал и сумму, и день сам: это подтверждение, а не расчёт и не
    // догадка по истории. От этого зависит, вправе ли дата быть горизонтом.
    amountBasis: input.amountBasis ?? (input.amount > ZERO ? 'known' : 'unknown'),
    expectedDate: input.expectedDate,
    dateBasis: 'confirmed',
    accountId: input.accountId,
    ...(input.streamId ? { streamId: input.streamId } : {}),
    ...(input.horizonRole ? { horizonRole: input.horizonRole } : {}),
    status: 'planned',
    createdAt: new Date().toISOString(),
  };
  await put('expectedIncomes', income);
  return income;
}

/**
 * Деньги пришли.
 *
 * Сумма подтверждается отдельно от обещанной: ждали пятнадцать, вернули
 * десять — записывать надо десять, а не то, на что рассчитывали.
 *
 * **Часть — это не всё.** Пришло меньше ожидаемого — ожидание не закрывается
 * и горизонт не двигается: деньги ещё ждут, и решать за человека, что
 * остатка не будет, приложение не вправе. Закрыть остаток он говорит сам
 * (`closeExpectation`).
 *
 * Ритму учит только приход по источнику: у разовых денег следующего раза нет.
 */
export async function receiveExpectedIncome(id: string, amount?: Money): Promise<void> {
  const list = await getAll<IncomeExpectation>('expectedIncomes');
  const e = list.find((x) => x.id === id);
  if (!e || (e.status !== 'planned' && e.status !== 'partial')) return;

  const got = amount ?? expectedRest(e);
  if (got <= ZERO) return;
  const tx = await addIncomeTx({
    accountId: e.accountId,
    amount: got,
    date: todayPd(),
    source: e.name,
    note: e.streamId ? e.name : 'Разовый приход',
    expectationId: e.id,
    incomeKind: e.streamId ? 'regular' : 'oneoff',
    ...(e.streamId ? { incomeStreamId: e.streamId } : {}),
  });

  const received = add(e.receivedAmount ?? ZERO, got);
  const done = received >= e.amount;
  await put('expectedIncomes', {
    ...e,
    receivedAmount: received,
    status: done ? 'received' : 'partial',
    txId: tx.id,
    txIds: [...(e.txIds ?? []), tx.id],
    decidedAt: stamp(),
  });
}

/**
 * «Больше по этому приходу не будет».
 *
 * Закрывает остаток ожидания без записи денег: пришло меньше, и человек это
 * подтвердил. Горизонт после этого вправе перейти к следующему приходу — до
 * этого мгновения он стоял на месте.
 */
export async function closeExpectation(id: string): Promise<void> {
  await mutate<IncomeExpectation>('expectedIncomes', id, (e) => {
    if (!e) return undefined;
    // Ничего не пришло вовсе — это не «получено», а «не случилось».
    const status = (e.receivedAmount ?? ZERO) > ZERO ? 'received' as const : 'missed' as const;
    return { ...e, status, decidedAt: stamp() };
  });
}

/** Не пришло и не придёт. Запись остаётся — стирать историю нечестно. */
export async function cancelExpectedIncome(id: string): Promise<void> {
  await mutate<IncomeExpectation>(
    'expectedIncomes', id,
    (e) => (e ? { ...e, status: 'cancelled' as const, decidedAt: stamp() } : undefined),
  );
}

/**
 * Приходы, которые действительно случились.
 *
 * Расписание не доказывает, что деньги пришли, — поэтому и подтверждение
 * наступления, и оценка ритма считаются отсюда. Переводы между своими счетами
 * сюда не попадают вовсе (у них другой тип операции), а возврат и разовые
 * деньги помечены своим видом: ритму они не учат.
 */
export function incomeFacts(txs: readonly Tx[]): IncomeFact[] {
  const out: IncomeFact[] = [];
  for (const t of txs) {
    if (t.type !== 'income') continue;
    const link = t.link;
    if (!link || link.kind !== 'income') continue;
    const kind = link.incomeKind ?? (link.incomeStreamId ? 'regular' : 'oneoff');
    out.push({
      date: t.occurredDate,
      kind,
      ...(link.incomeStreamId ? { streamId: link.incomeStreamId } : {}),
    });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

// ────────────────────────────────────────────── оперативная раскладка денег

/**
 * Предложение раскладки на текущих данных.
 *
 * Считается заново при каждом показе: предложение живёт секунды, а за это
 * время может прийти зарплата или списаться платёж.
 */
export function allocationProposal(state: AppState, over: {
  basis?: AllocationBasis;
  protectDays?: number;
} = {}): AllocationProposal {
  void over.basis;
  return proposeAllocation(planInput(state, over));
}

/**
 * Вход расчёта раскладки из состояния приложения.
 *
 * Отдельной функцией, потому что тем же входом пользуются экран конфликта и
 * предпросмотр последствий: считать в трёх местах по-разному — верный способ
 * показать человеку три разные правды.
 */
/**
 * Вход лестницы горизонта из состояния — в одном месте.
 *
 * Собирался в четырёх экранах порознь, и стоило появиться новому источнику
 * срока (выплате себе из буфера), как один экран знал бы о нём, а три —
 * нет. Горизонт обязан быть одним и тем же числом везде, иначе «Можно
 * потратить» на главной и в предложении раскладки разойдутся.
 */
export function horizonInput(state: AppState): HorizonInput {
  const stable = stableSnapshot(state);
  return {
    today: state.today,
    streams: state.incomeStreams,
    expectations: state.expectedIncomes,
    facts: incomeFacts(state.transactions),
    ...(state.trackingStartedAt ? { since: state.trackingStartedAt } : {}),
    // Выплата себе — точка, но только обеспеченная и только будущая: это
    // решает ядро (`horizonPayout`), здесь её просто передают дальше.
    ...(stable?.horizonPayout
      ? { selfPayout: { id: stable.plan.id, date: stable.horizonPayout.scheduledDate } }
      : {}),
  };
}

export function planInput(state: AppState, over: { protectDays?: number } = {}) {
  return ({
    today: state.today,
    accounts: state.accounts,
    reserves: state.reserves,
    obligations: state.obligations,
    // Карты — та же часть будущего, что и на главной: платёж по выписке до
    // прихода держит деньги так же, как ипотека.
    creditTerms: state.creditTerms,
    goals: state.goals,
    incomeStreams: state.incomeStreams,
    needBaseline: state.baselines.need,
    horizon: resolveHorizon(horizonInput(state)),
    mode: state.planMode ?? 'balanced',
    fundedThisMonth: fundedThisMonth(state),
    ...(state.trackingStartedAt ? { trackingStartedAt: state.trackingStartedAt } : {}),
    ...(over.protectDays !== undefined ? { protectDays: over.protectDays } : {}),
    current: activeAllocation(state.allocations),
  });
}

/**
 * Сколько уже внесено в каждую цель в этом месяце.
 *
 * Нужно, чтобы невыполненная часть взноса не удерживалась заново при каждом
 * приходе: человек, отложивший половину, не должен видеть требование полной
 * суммы во второй раз.
 */
function fundedThisMonth(state: AppState): Record<string, Money> {
  const month = state.today.slice(0, 7);
  const out: Record<string, Money> = {};
  for (const e of state.goalEvents) {
    if (e.kind !== 'contribution') continue;
    if (!e.date.startsWith(month)) continue;
    out[e.goalId] = add(out[e.goalId] ?? ZERO, e.amount);
  }
  return out;
}

/**
 * Принять раскладку.
 *
 * Три обязанности, каждая из которых однажды стоила бы человеку денег:
 *
 * · **свежесть.** Предложение строилось на снимке данных; если с тех пор
 *   что-то изменилось — остаток, конверт, платёж, цель, горизонт, — старое
 *   предложение не применяется. Иначе закрепятся суммы, которых уже нет.
 * · **идемпотентность.** Второе нажатие той же кнопки не создаёт вторую
 *   версию и не удваивает конверты: между нажатием и записью проходит время,
 *   и палец успевает нажать дважды.
 * · **конверты — источник правды.** Раскладка не заводит собственный остаток
 *   резерва, а записывает настоящие `Reserve`: две правды об одних и тех же
 *   рублях — это всегда расхождение в чью-то пользу.
 */
export async function acceptAllocation(input: {
  state: AppState;
  proposal: AllocationProposal;
  basis: AllocationBasis;
  /**
   * Решение человека при нехватке.
   *
   * Обязательно, когда защищённого слоя не хватает: выбирать, какой платёж
   * останется без денег, приложение не вправе. Раньше здесь стоял
   * автоматический список целей — то есть именно то, чего делать нельзя.
   */
  shortfall?: ShortfallDecision;
}): Promise<{
  allocation: AcceptedAllocation | null;
  stale: boolean;
  needsChoice?: boolean;
  /** Пересчитанное предложение: его и надо показать вместо устаревшего. */
  proposal?: AllocationProposal;
}> {
  const existing = await getAll<AcceptedAllocation>('allocations');
  /*
    Повтор нажатия узнаётся по версии **предложения**, а не текущих данных.

    После применения данные меняются — появляются конверты, — поэтому
    сравнение с пересчитанным отпечатком всегда давало бы «не совпало». А
    человек всего лишь нажал дважды, пока шла запись.
  */
  const already = existing.find((a) => a.inputVersion === input.proposal.inputVersion);
  if (already) return { allocation: already, stale: false };

  /*
    Свежесть проверяется по хранилищу, а не по состоянию вызывающего.

    Состояние экрана — снимок, сделанный при открытии: пока человек читал
    предложение, деньги могли уйти в другой вкладке или на другом устройстве.
    Сравнивать снимок сам с собой бессмысленно — проверка всегда сходилась
    бы, а закреплялись бы суммы, которых уже нет.
  */
  const current = await loadState();
  const fresh = allocationProposal(current);
  if (fresh.inputVersion !== input.proposal.inputVersion) {
    // Вместе с отказом отдаём новый расчёт: человеку нужно увидеть, что
    // изменилось, а не просто получить «попробуйте ещё раз».
    return { allocation: null, stale: true, proposal: fresh };
  }
  // Нехватка без решения человека не применяется вовсе.
  if (fresh.shortfall > ZERO && !input.shortfall) {
    return { allocation: null, stale: false, needsChoice: true };
  }
  const active = activeAllocation(existing);

  const now = new Date().toISOString();
  const allocation: AcceptedAllocation = {
    id: uid(),
    scope: 'allocation',
    acceptedAt: now,
    effectiveFrom: now,
    basis: input.basis,
    horizon: resolveHorizon(horizonInput(current)),
    liquid: fresh.liquid,
    reserved: fresh.reserved,
    lines: fresh.lines,
    inputVersion: fresh.inputVersion,
    structureVersion: fresh.structureVersion,
    ...(active ? { supersedes: active.id } : {}),
    ...(input.shortfall ? { shortfall: input.shortfall } : {}),
  };

  // Конверты под то, что раскладка закрепила. Счёт — тот, где лежат деньги.
  const from = current.accounts.find((a) => a.isLiquid && !a.archived && a.kind !== 'credit_card');
  if (from) {
    /*
      Строки складываются по требованию, а не пишутся по одной.

      У одного обязательства в окне бывает несколько наступлений — у аренды
      при горизонте в месяц их два, у недельного платежа четыре. Конверт же
      под обязательство один, и запись «уже закреплено + эта строка» для
      каждой строки по отдельности читала прежний остаток из того же снимка:
      вторая строка не добавлялась, а **замещала** первую. Деньги второго
      платежа оставались свободными, а сверка потом обвиняла человека в
      расхождении ровно на его сумму.
    */
    const want = new Map<string, { kind: ReserveTarget; id: string; amount: Money }>();
    const plus = (kind: ReserveTarget, id: string, amount: Money): void => {
      const key = `${kind}:${id}`;
      const had = want.get(key);
      want.set(key, { kind, id, amount: add(had?.amount ?? ZERO, amount) });
    };
    for (const line of fresh.lines.obligations) plus('obligation', line.obligationId, line.amount);
    // Под карту конверт заводится на её счёт: так его видят «Можно
    // потратить» и прогноз, и платёж по карте потом снимает его сам.
    for (const line of fresh.lines.cards ?? []) plus('credit', line.accountId, line.amount);
    for (const line of fresh.lines.goals) plus('goal', line.goalId, line.amount);

    for (const { kind, id, amount } of want.values()) {
      // Конверт под карту к более позднему сроку (`forDate`) — отдельный, сюда не складывается.
      const already = add(...current.reserves
        .filter((r) => r.forKind === kind && r.forId === id && !r.forDate)
        .map((r) => r.amount));
      await setReserve({ accountId: from.id, forKind: kind, forId: id, amount: add(already, amount) });
    }
  }

  await put('allocations', allocation);
  return { allocation, stale: false };
}

/** Служебные заметки переводов запаса — у старых записей нет `buffer`. */
const BUFFER_NOTES = new Set([
  'Выплата себе из буфера', 'Освобождение запаса при отключении режима', 'Взято из запаса раньше срока',
]);

/**
 * Операции для остатков корзин — одним списком для главной и записи
 * остатка: два набора признаков разошлись бы при первой же правке.
 *
 * Счета операции нужны, чтобы покупка по кредитке, её погашение и платёж
 * без конверта сходились с записанным, а не читались как «мимо приложения».
 * Переводы запаса и взносы в цели раскладка учитывает иначе — по журналу
 * запаса и по строкам целей.
 */
export function allocationEntries(
  txs: readonly Tx[], payouts: readonly { txId?: string }[],
): AllocationEntry[] {
  const payoutTx = new Set(payouts.map((x) => x.txId).filter((x): x is string => !!x));
  return txs.map((t) => ({
    type: t.type,
    amount: t.amount,
    part: t.part === 'want' ? 'want' as const : 'need' as const,
    accountId: t.accountId,
    ...(t.counterAccountId ? { toAccountId: t.counterAccountId } : {}),
    ...(t.allocationId ? { allocationId: t.allocationId } : {}),
    ...(t.balanceApplied === false ? { balanceApplied: false as const } : {}),
    ...(t.link?.kind === 'income' && t.link.incomeKind
      ? { incomeKind: t.link.incomeKind }
      : {}),
    ...(t.link?.kind === 'obligation'
      ? { settles: 'obligation' as const, instanceId: t.link.instanceId }
      : {}),
    ...(t.link?.kind === 'goal' ? { settles: 'goal' as const } : {}),
    ...(t.reserveUsed !== undefined ? { reserveUsed: t.reserveUsed } : {}),
    ...(t.type === 'transfer' && (t.buffer || payoutTx.has(t.id) || t.link?.kind === 'goal'
      || BUFFER_NOTES.has(t.note ?? '')) ? { accounted: true } : {}),
  }));
}

/**
 * Остатки корзин из состояния.
 *
 * Собиралось в четырёх экранах порознь — на главной, в разборе части, в
 * предложении раскладки и в предпросмотре правок, — и каждая копия знала
 * свой набор признаков операции. Стоило появиться выплате себе из буфера,
 * как один экран посчитал бы её, а три увидели бы расхождение и попросили
 * пересчитать раскладку на ровном месте.
 */
export function allocationViewOf(
  state: AppState, allocation: AcceptedAllocation,
): AllocationView {
  return allocationView({
    allocation,
    accounts: state.accounts,
    reserves: state.reserves,
    entries: allocationEntries(state.transactions, state.stablePayouts ?? []),
    // Выплата себе доходом не является, но «не распределено» растёт: деньги
    // перестали быть связанными буфером.
    selfPayouts: (state.stablePayouts ?? []).map((x) => ({
      amount: x.unallocatedDelta ?? x.amount,
      ...(x.allocationId ? { allocationId: x.allocationId } : {}),
    })),
    bufferAdjustments: (state.stableMoves ?? [])
      .filter((x) => x.unallocatedDelta !== undefined)
      .map((x) => ({
        delta: x.unallocatedDelta!,
        ...(x.allocationId ? { allocationId: x.allocationId } : {}),
      })),
  });
}

// ────────────────────────────────────────────── стабильный бюджет (§15 CB)

/**
 * Что сейчас со стабильным бюджетом.
 *
 * Одна функция на всё приложение: план, остаток буфера, прогноз и точка
 * горизонта. Считать это в каждом экране порознь значило бы получить четыре
 * разных ответа на вопрос «когда следующая выплата».
 */
export function stableSnapshot(state: AppState): {
  plan: StableBudgetPlan;
  buffer: Money;
  outlook: StableOutlook;
  /** Выплата, задающая горизонт. `null` — необеспечена или уже наступила. */
  horizonPayout: ReturnType<typeof horizonPayout>;
} | null {
  const plan = activeStablePlan(state.stablePlans ?? []);
  if (!plan) return null;
  const buffer = bufferOf(state.reserves, plan.id);
  const outlook = stableOutlook({
    today: state.today,
    plan,
    buffer,
    payouts: (state.stablePayouts ?? []).filter((x) => x.planId === plan.id),
  });
  return {
    plan,
    buffer,
    outlook,
    /*
      Приостановленный режим срока не задаёт.

      Выплат в нём не будет, и держать ими горизонт значило бы планировать
      жизнь до денег, которые никто не собирается себе отдавать. Сам план при
      этом остаётся: человек его приостановил, а не выключил.
    */
    horizonPayout: plan.status === 'active' ? horizonPayout(outlook, state.today) : null,
  };
}

/**
 * Стабильный бюджет в месячном отчёте.
 *
 * Считается по **всем** планам, включая выключенные: выплаты прошлого
 * никуда не деваются от того, что режим отключили в конце месяца. Иначе
 * отчёт молча потерял бы строку «получено из своего буфера» — то есть
 * именно те деньги, которые человек в этом месяце и прожил.
 */
export function stableMonth(state: AppState, month: string): StableRange | null {
  return stableRange(state, `${month}-01` as PlainDate, `${shiftMonth(month, 1)}-01` as PlainDate);
}

/** Стабильный бюджет за отрезок: выплаты, сверка, запас на его границах. */
export interface StableRange {
  /** Выплаты, **проведённые** в отрезке. */
  payouts: { date: PlainDate; amount: Money }[];
  /** Поправка остатка по сверке за отрезок. Со знаком. */
  reconciled: Money;
  buffer: { start: Money; end: Money };
}

/**
 * То же, что `stableMonth`, за любой отрезок `[first, next)` — для периода
 * от зарплаты до зарплаты и любого выбранного в аналитике.
 */
export function stableRange(state: AppState, first: PlainDate, next: PlainDate): StableRange | null {
  const plans = state.stablePlans ?? [];
  if (plans.length === 0) return null;
  const ids = new Set(plans.map((p) => p.id));
  const now = add(...plans.map((p) => bufferOf(state.reserves, p.id)));
  const moves = (state.stableMoves ?? []).filter((m) => ids.has(m.planId));

  return {
    /*
      Месяц отвечает за факт, поэтому выплата попадает в него по дню, когда
      деньги двигались, а не по дате графика. Просроченная августовская
      выплата, проведённая 21 сентября, — сентябрьский факт.
    */
    payouts: (state.stablePayouts ?? [])
      .filter((x) => ids.has(x.planId) && decisionOf(x) === 'paid' && x.amount > ZERO)
      .map((x) => ({ date: paidOnOf(x), amount: x.amount }))
      .filter((x) => x.date >= first && x.date < next),
    reconciled: add(...moves
      .filter((m) => m.kind === 'reconcile' && m.date >= first && m.date < next)
      .map((m) => m.amount)),
    /*
      Остаток считается на обе границы отрезка, а не «на начало и сегодня».
      Прежде в конец любого месяца подставлялся текущий остаток, и сентябрь
      после октябрьской выплаты показывал октябрьское число.
    */
    buffer: { start: bufferAt(now, moves, first), end: bufferAt(now, moves, next) },
  };
}

/**
 * Включить стабильный бюджет.
 *
 * Буфер создаётся конвертом на выбранном счёте — теми же `Reserve`, что и
 * все прочие закреплённые деньги. Второго способа хранить остаток буфера
 * нет намеренно: две правды об одних и тех же рублях — это всегда
 * расхождение в чью-то пользу.
 */
/**
 * Готовность к режиму на текущих данных.
 *
 * `have` — деньги, которые человек мог бы отложить в буфер: свободный
 * остаток ликвидных счетов плюс то, что уже лежит в буфере. Чужие конверты
 * сюда не входят: они закреплены под другое, и предлагать режим за их счёт
 * значило бы предлагать разобрать цель.
 */
export function stableReadinessOf(state: AppState, over: { have?: Money } = {}): StableReadiness {
  const plan = activeStablePlan(state.stablePlans ?? []);
  const free = sub(liquidOf(state.accounts), reservedOf(state.accounts, state.reserves));
  const have = over.have ?? add(free > ZERO ? free : ZERO, plan ? bufferOf(state.reserves, plan.id) : ZERO);
  return stableReadiness({
    today: state.today,
    obligations: state.obligations,
    reserves: state.reserves,
    needBaseline: state.baselines.need,
    have,
  });
}

/** Стоит ли самому заговорить о режиме. Открыть его вручную можно всегда. */
export function stableSuggested(state: AppState): boolean {
  return suggestStable({
    readiness: stableReadinessOf(state),
    // «Когда как» — это ритм без дат. Отсутствие дохода вовсе тоже считается:
    // человек между проектами живёт ровно из запаса.
    hasIrregularIncome: state.incomeStreams.filter((s) => s.isActive).length === 0
      || state.incomeStreams.some((s) => s.isActive && rhythmOf(s).kind === 'irregular'),
    hasRegularHorizon: state.incomeStreams.some(
      (s) => s.isActive && hasDates(rhythmOf(s)) && roleOfStream(s) === 'checkpoint',
    ),
    hasPlan: (state.stablePlans ?? []).some((p) => p.status !== 'stopped'),
  });
}

/**
 * Сколько на счёте свободно от конвертов.
 *
 * Общий ответ для всех, кто собирается что-то закрепить: закрепить больше,
 * чем лежит, — значит записать одни и те же рубли в два запаса.
 */
export function freeOnAccount(
  state: AppState, accountId: string, exceptReserveIds: readonly string[] = [],
): Money {
  const account = state.accounts.find((a) => a.id === accountId);
  if (!account) return ZERO;
  const held = add(...state.reserves
    .filter((r) => r.accountId === accountId && !exceptReserveIds.includes(r.id))
    .map((r) => r.amount));
  return clampPos(sub(account.balance, held));
}

/**
 * Включить стабильный бюджет.
 *
 * План, конверт и первая запись в журнале — одной транзакцией: план без
 * конверта означал бы режим без денег, конверт без плана — запертые деньги,
 * которыми некому распорядиться.
 *
 * Два отказа до всякой записи. **Второй действующий план** завёл бы два
 * запаса и два расписания на одни деньги; **зависший конверт выключенного
 * режима** означает, что старые деньги ещё не разобраны, и заводить поверх
 * них новый режим значит потерять их из виду.
 */
export async function createStablePlan(input: {
  accountId: string;
  toAccountId: string;
  /** Сколько выделить в буфер сейчас. */
  buffer: Money;
  cadence: PayoutCadence;
  payout: Money;
  firstPayoutDate: PlainDate;
}): Promise<{ plan: StableBudgetPlan | null; reason?: string }> {
  return queued('stable:create', () => transact(
    ['stablePlans', 'reserves', 'stableMoves', 'accounts', 'allocations'],
    async (db): Promise<{ plan: StableBudgetPlan | null; reason?: string }> => {
      const plans = await db.all<StableBudgetPlan>('stablePlans');
      if (plans.some((p) => p.status !== 'stopped')) {
        return { plan: null, reason: 'Стабильный бюджет уже включён' };
      }
      const reserves = await db.all<Reserve>('reserves');
      const hanging = reserves.find((r) => r.forKind === 'stable' && r.amount > ZERO);
      if (hanging) {
        return {
          plan: null,
          reason: 'Остался запас прошлого режима — сначала освободи его',
        };
      }
      const accounts = await db.all<Account>('accounts');
      const allocation = activeAllocation(await db.all<AcceptedAllocation>('allocations'));
      const account = accounts.find((a) => a.id === input.accountId);
      if (!account) return { plan: null, reason: 'Счёт не найден' };
      const held = add(...reserves
        .filter((r) => r.accountId === input.accountId).map((r) => r.amount));
      const free = clampPos(sub(account.balance, held));
      if (input.buffer > free) {
        return {
          plan: null,
          reason: `Свободных денег на счёте ${formatRub(free)} — меньше, чем в запас`,
        };
      }

      const now = new Date().toISOString();
      const plan: StableBudgetPlan = {
        id: uid(),
        accountId: input.accountId,
        toAccountId: input.toAccountId,
        cadence: input.cadence,
        payout: input.payout,
        firstPayoutDate: input.firstPayoutDate,
        status: 'active',
        createdAt: now,
      };
      db.put('stablePlans', plan);
      if (input.buffer > ZERO) {
        const reserve: Reserve = {
          id: uid(),
          accountId: input.accountId,
          forKind: 'stable' as const,
          forId: plan.id,
          amount: input.buffer,
        };
        db.put('reserves', reserve);
        db.put('stableMoves', {
          id: uid(),
          planId: plan.id,
          date: todayPd(),
          amount: input.buffer,
          kind: 'open',
          ...allocationMoveFields(
            allocation,
            allocationDelta(accounts, reserves, accounts, [...reserves, reserve]),
          ),
          createdAt: now,
        } satisfies BufferMove);
      }
      return { plan };
    },
  ));
}

export async function updateStablePlan(
  id: string, patch: Partial<StableBudgetPlan>,
): Promise<void> {
  const plan = (await getAll<StableBudgetPlan>('stablePlans')).find((p) => p.id === id);
  if (!plan) return;
  await put('stablePlans', { ...plan, ...patch });
}

/**
 * Пополнить буфер.
 *
 * Конверт растёт, свободных денег становится меньше — и это правда: деньги
 * перестали быть свободными. Новую раскладку принимает человек, поэтому
 * здесь она не создаётся: приложение не решает за него, что делать с
 * остальными деньгами прихода.
 */
export async function topUpBuffer(
  planId: string, amount: Money, kind: BufferMove['kind'] = 'top_up',
): Promise<{ done: boolean; reason?: string }> {
  if (amount <= ZERO) return { done: false, reason: 'Сумма не задана' };
  // Конверт и журнал — одной транзакцией: буфер, выросший без записи в
  // истории, сделал бы месячный отчёт неверным задним числом.
  return queued(`stable:${planId}`, () => transact(
    ['stablePlans', 'reserves', 'stableMoves', 'accounts', 'allocations'],
    async (db): Promise<{ done: boolean; reason?: string }> => {
    const plan = (await db.all<StableBudgetPlan>('stablePlans')).find((p) => p.id === planId);
    if (!plan) return { done: false, reason: 'Режим не включён' };
    const reserves = await db.all<Reserve>('reserves');
    /*
      Больше, чем свободно на счёте, закрепить нельзя: конверты сверх
      остатка — это одни и те же рубли, записанные в два запаса.
    */
    const account = (await db.all<Account>('accounts')).find((a) => a.id === plan.accountId);
    const held = add(...reserves
      .filter((r) => r.accountId === plan.accountId).map((r) => r.amount));
    if (!account) return { done: false, reason: 'Счёт запаса не найден' };
    const free = clampPos(sub(account.balance, held));
    if (amount > free) {
      return {
        done: false,
        reason: `Свободных денег на счёте ${formatRub(free)} — меньше суммы пополнения`,
      };
    }
    const accounts = await db.all<Account>('accounts');
    const allocation = activeAllocation(await db.all<AcceptedAllocation>('allocations'));
    const envelope = reserves.find(
      (r) => r.forKind === 'stable' && r.forId === plan.id && r.accountId === plan.accountId,
    );
    const nextReserve: Reserve = {
      id: envelope?.id ?? uid(),
      accountId: plan.accountId,
      forKind: 'stable' as const,
      forId: plan.id,
      amount: add(bufferOf(reserves, plan.id), amount),
    };
    const nextReserves = withRecord(reserves, nextReserve);
    db.put('reserves', nextReserve);
    db.put('stableMoves', {
      id: uid(),
      planId: plan.id,
      date: todayPd(),
      amount,
      kind,
      ...allocationMoveFields(
        allocation,
        allocationDelta(accounts, reserves, accounts, nextReserves),
      ),
      createdAt: new Date().toISOString(),
    } satisfies BufferMove);
    return { done: true };
  }));
}

/**
 * Что можно сделать с наступлением выплаты прямо сейчас.
 *
 * Отдельная проверка, а не догадка экрана: кнопку «Выплатить себе» рисуют
 * три места, и разойтись им нельзя. Приостановленный и выключенный режим
 * денег не выдают вовсе — иначе пауза означала бы только надпись.
 */
export function payoutAllowed(
  state: AppState, scheduledDate: PlainDate, action: PayoutAction = 'pay',
): { ok: boolean; reason?: string } {
  const snapshot = stableSnapshot(state);
  if (!snapshot) return { ok: false, reason: 'Режим не включён' };
  // Правило одно на всех — из ядра. Своя копия здесь неизбежно разошлась бы
  // с экраном: кнопка обещала бы то, чего запись не делает.
  return decisionAllowed({
    plan: snapshot.plan,
    outlook: snapshot.outlook,
    scheduledDate,
    action,
  });
}

/** Что записывается по решению человека о наступлении. */
type PayoutWrite = { done: boolean; reason?: string; payout?: StablePayoutFact };

/**
 * Выплатить себе.
 *
 * Вся запись — **одна транзакция**. Это не осторожность про запас: одно
 * событие меняет здесь пять картин — конверт буфера, остатки двух счетов,
 * операцию перевода, факт выплаты и журнал. Записанные по одной, они дают
 * два способа соврать, и оба дорогие:
 *
 * · **сбой посередине.** Резерв уменьшен, а выплаты нет: деньги стали
 *   свободными без причины, и человек потратит их дважды — сейчас и когда
 *   выплата всё-таки запишется;
 * · **два одновременных нажатия.** Обе проверки «не выплачено ли уже»
 *   проходят по старым данным, и на другой счёт уезжают два перевода.
 *   Особенно легко это поймать двумя вкладками, где ничьей очереди в памяти
 *   нет вовсе.
 *
 * Под одной блокировкой хранилищ вторая такая транзакция ждёт первую и
 * видит её факт — поэтому устойчивый ключ наступления работает ключом
 * идемпотентности, а не пожеланием: повтор возвращает **тот же** факт с тем
 * же `txId`, ничего не записывая.
 *
 * Три отказа до всякой записи: режим не активен, дата не принадлежит
 * ближайшему наступлению, денег в буфере меньше выплаты.
 */
export async function payoutSelf(input: {
  planId: string;
  scheduledDate: PlainDate;
  /** Сумма, если человек решил выплатить меньше. По умолчанию — из плана. */
  amount?: Money;
}): Promise<PayoutWrite> {
  /*
    Проверка права — на свежем состоянии, до транзакции: её ответ нужен
    человеку словами, а внутри транзакции остаётся только арифметика.
    Гонку это не открывает — ключ наступления всё равно проверяется внутри.
  */
  const state = await loadState();
    /*
      Повтор проверяется **раньше** права.

      После первой выплаты ближайшим наступлением становится следующее, и
      проверка права на ту же дату честно отвечает «это не оно». Для
      человека, нажавшего дважды, это выглядело бы ошибкой там, где всё
      прошло: повтор обязан вернуть тот же результат, а не отказ.
    */
  const done = (state.stablePayouts ?? []).some(
    (x) => x.id === payoutId(input.planId, input.scheduledDate) && decisionOf(x) === 'paid',
  );
  if (!done) {
    const allowed = payoutAllowed(state, input.scheduledDate);
    if (!allowed.ok) return { done: false, ...(allowed.reason ? { reason: allowed.reason } : {}) };
  }

  return queued(`stable:${input.planId}`, () => transact(
    ['stablePlans', 'stablePayouts', 'stableMoves', 'reserves', 'accounts', 'transactions',
      'allocations', 'creditTerms'],
    async (db): Promise<PayoutWrite> => {
      const plan = (await db.all<StableBudgetPlan>('stablePlans')).find((x) => x.id === input.planId);
      if (!plan) return { done: false, reason: 'Режим не включён' };
      if (plan.status !== 'active') {
        return { done: false, reason: 'Режим приостановлен: новые выплаты не наступают' };
      }

      const id = payoutId(plan.id, input.scheduledDate);
      const already = await db.get<StablePayoutFact>('stablePayouts', id);
      if (already) {
        /*
          Решение по наступлению уже есть. Повтор выплаты получает тот же
          факт и тот же перевод — а вот выплатить то, что человек пометил
          пропущенным, молча нельзя: ответить «готово», не выдав денег,
          хуже, чем отказать.
        */
        return decisionOf(already) === 'skipped'
          ? { done: false, reason: 'Эта выплата отмечена пропущенной' }
          : { done: true, payout: already };
      }

      const reserves = await db.all<Reserve>('reserves');
      /*
        Сначала всё чтение и все отказы, и только потом записи.

        Отказ после первой записи так же опасен, как сбой: транзакция
        завершится успешно и сохранит половину — например, уменьшенный
        резерв без выплаты. Поэтому ниже первой записи нет ни одного
        `return` с отказом.
      */
      const accounts = await db.all<Account>('accounts');
      const buffer = bufferOf(reserves, plan.id);
      const amount = input.amount ?? plan.payout;
      if (amount <= ZERO) return { done: false, reason: 'Сумма выплаты не задана' };
      if (amount > buffer) {
        return {
          done: false,
          reason: `В буфере ${formatRub(buffer)} — на выплату ${formatRub(amount)} не хватает`,
        };
      }

      const moving = plan.toAccountId !== plan.accountId;
      const from = accounts.find((a) => a.id === plan.accountId);
      const to = accounts.find((a) => a.id === plan.toAccountId);
      if (moving && (!from || !to)) return { done: false, reason: 'Счёт не найден' };
      /*
        Кредитка получателем быть не может: платёж по ней приводит в порядок
        ещё и долг по выписке с конвертом под него, и делать это мимоходом
        внутри выплаты значило бы завести второй, расходящийся путь того же
        расчёта.
      */
      if (moving && to?.kind === 'credit_card') {
        return { done: false, reason: 'Платить себе на кредитку нельзя: выбери другой счёт' };
      }

      const current = activeAllocation(await db.all<AcceptedAllocation>('allocations'));
      const now = new Date().toISOString();

      // ─── дальше только записи ──────────────────────────────────────────

      // 1. Конверт: деньги перестали быть связанными.
      const envelope = reserves.find(
        (r) => r.forKind === 'stable' && r.forId === plan.id && r.accountId === plan.accountId,
      );
      const left = sub(buffer, amount);
      let nextReserves = [...reserves];
      if (envelope) {
        if (left <= ZERO) {
          db.del('reserves', envelope.id);
          nextReserves = nextReserves.filter((r) => r.id !== envelope.id);
        } else {
          const nextEnvelope = { ...envelope, amount: left };
          db.put('reserves', nextEnvelope);
          nextReserves = withRecord(nextReserves, nextEnvelope);
        }
      }

      // 2. Перевод — только когда счета разные: на одном банковской операции
      //    не было вовсе, освободилась часть конверта.
      let txId: string | undefined;
      let nextAccounts = [...accounts];
      if (moving && from && to) {
        const tx = makeTx({
          type: 'transfer',
          accountId: from.id,
          counterAccountId: to.id,
          amount,
          date: todayPd(),
          note: 'Выплата себе из буфера',
          buffer: true,
          ...(current ? { allocationId: current.id } : {}),
        });
        db.put('transactions', tx);
        const nextFrom = { ...from, balance: sub(from.balance, amount) };
        const nextTo = { ...to, balance: add(to.balance, amount) };
        db.put('accounts', nextFrom);
        db.put('accounts', nextTo);
        nextAccounts = withRecord(withRecord(nextAccounts, nextFrom), nextTo);
        txId = tx.id;
      }

      const unallocatedDelta = allocationDelta(
        accounts, reserves, nextAccounts, nextReserves,
      );

      // 3. Решение человека и журнал буфера — там же, а не «потом».
      const payout: StablePayoutFact = {
        id,
        planId: plan.id,
        scheduledDate: input.scheduledDate,
        decision: 'paid',
        amount,
        via: txId ? 'transfer' : 'release',
        ...(txId ? { txId } : {}),
        ...(current ? { allocationId: current.id, unallocatedDelta } : {}),
        // День, когда деньги двигались: просроченная августовская выплата,
        // проведённая сегодня, — сегодняшний факт, а не августовский.
        paidOn: todayPd(),
        paidAt: now,
      };
      db.put('stablePayouts', payout);
      db.put('stableMoves', {
        id: uid(),
        planId: plan.id,
        date: todayPd(),
        amount: neg(amount),
        kind: 'payout',
        payoutId: payout.id,
        createdAt: now,
      } satisfies BufferMove);

      return { done: true, payout };
    },
  ));
}

/**
 * Пропустить выплату.
 *
 * Решение, а не отсутствие решения. Без него приложение вечно спрашивало бы
 * про 1 октября, а горизонт стоял бы на месте: перескочить через
 * непроведённую выплату оно не вправе, догадаться о пропуске — тем более.
 * Деньги при этом не двигаются вовсе: буфер остаётся каким был.
 */
export async function skipPayout(input: {
  planId: string;
  scheduledDate: PlainDate;
}): Promise<PayoutWrite> {
  /*
    Та же граница, что у выплаты, и по той же причине: пропуск — решение о
    деньгах. Без проверки через хранилище можно было пропустить чужую дату,
    наступление приостановленного режима и даже уже выплаченное — последнее
    отвечало «готово», хотя ничего не пропускалось.
  */
  const state = await loadState();
  const decided = (state.stablePayouts ?? []).find(
    (x) => x.id === payoutId(input.planId, input.scheduledDate),
  );
  if (!decided) {
    const allowed = payoutAllowed(state, input.scheduledDate, 'skip');
    if (!allowed.ok) return { done: false, ...(allowed.reason ? { reason: allowed.reason } : {}) };
  }

  return queued(`stable:${input.planId}`, () => transact(
    ['stablePlans', 'stablePayouts'],
    async (db): Promise<PayoutWrite> => {
      const plan = (await db.all<StableBudgetPlan>('stablePlans')).find((x) => x.id === input.planId);
      if (!plan) return { done: false, reason: 'Режим не включён' };
      if (plan.status !== 'active') {
        return { done: false, reason: 'Режим приостановлен: решения по выплатам не принимаются' };
      }

      const id = payoutId(plan.id, input.scheduledDate);
      const already = await db.get<StablePayoutFact>('stablePayouts', id);
      if (already) {
        // Повтор пропуска — тот же ответ. А пропустить выплаченное нельзя:
        // сказать «пропущено» о деньгах, которые человек взял, — неправда.
        return decisionOf(already) === 'paid'
          ? { done: false, reason: 'Выплата уже проведена' }
          : { done: true, payout: already };
      }

      const payout: StablePayoutFact = {
        id,
        planId: plan.id,
        scheduledDate: input.scheduledDate,
        decision: 'skipped',
        amount: ZERO,
        via: 'none',
        paidAt: new Date().toISOString(),
      };
      db.put('stablePayouts', payout);
      return { done: true, payout };
    },
  ));
}

/**
 * Приостановить режим.
 *
 * Пауза записывается **отрезком**, а не флагом. Расписание считается от
 * опорной даты, поэтому без отрезка месяц паузы вернул бы человеку четыре
 * просроченные выплаты, которых он не пропускал: он сам их и остановил.
 * История при этом цела — прошлые выплаты лежат как лежали.
 */
export async function pauseStablePlan(id: string): Promise<void> {
  await queued(`stable:${id}`, () => transact(['stablePlans'], async (db) => {
    const plan = await db.get<StableBudgetPlan>('stablePlans', id);
    if (!plan || plan.status === 'stopped') return;
    const pauses = [...(plan.pauses ?? [])];
    if (!pauses.some((p) => p.to === undefined)) pauses.push({ from: todayPd() });
    db.put('stablePlans', {
      ...plan, status: 'paused', pausedAt: new Date().toISOString(), pauses,
    });
  }));
}

/**
 * Возобновить режим.
 *
 * Отрезок паузы закрывается сегодняшним днём, и ближайшей выплатой
 * становится первая дата расписания начиная с сегодня. Задним числом не
 * создаётся ничего.
 */
export async function resumeStablePlan(id: string): Promise<void> {
  await queued(`stable:${id}`, () => transact(['stablePlans'], async (db) => {
    const plan = await db.get<StableBudgetPlan>('stablePlans', id);
    if (!plan || plan.status === 'stopped') return;
    const { pausedAt: _dropped, ...rest } = plan;
    void _dropped;
    const pauses = (plan.pauses ?? []).map(
      (p) => (p.to === undefined ? { ...p, to: todayPd() } : p),
    );
    db.put('stablePlans', { ...rest, status: 'active', pauses });
  }));
}

/**
 * Отключить режим.
 *
 * Отменяет только будущие выплаты: прошлые — это то, что уже случилось, и
 * стирать их нельзя. Оставшийся резерв освобождается и **выплатой не
 * становится**: человек отменил назначение денег, а не получил очередную
 * сумму по графику.
 */
export async function stopStablePlan(id: string): Promise<void> {
  await queued(`stable:${id}`, () => transact(
    ['stablePlans', 'stableMoves', 'reserves', 'accounts', 'transactions', 'allocations'],
    async (db) => {
      const plan = (await db.all<StableBudgetPlan>('stablePlans')).find((p) => p.id === id);
      if (!plan) return;
      // Повторное отключение ничего не меняет: статус уже тот, остаток уже
      // освобождён. Молча выплатить второй раз было бы дороже всего.
      if (plan.status === 'stopped') return;

      const now = new Date().toISOString();
      const reserves = await db.all<Reserve>('reserves');
      const left = bufferOf(reserves, plan.id);
      const accounts = await db.all<Account>('accounts');
      const from = accounts.find((a) => a.id === plan.accountId);
      const to = accounts.find((a) => a.id === plan.toAccountId);
      const moving = left > ZERO && plan.toAccountId !== plan.accountId;
      // Переводить некуда — деньги просто перестают быть связанными на своём
      // счёте: терять их из-за пропавшего счёта нельзя.
      const transfer = moving && from && to && to.kind !== 'credit_card';
      const current = activeAllocation(await db.all<AcceptedAllocation>('allocations'));

      // ─── дальше только записи ──────────────────────────────────────────

      /*
        Отключение **всегда** освобождает запас.

        Вариант «оставить закреплённым» выглядел вежливым, а на деле запирал
        деньги: выключенный план из действующих пропадает, экран предлагает
        завести режим заново, а старый конверт продолжает вычитаться из
        доступного — и снять его больше негде. Деньги без хозяина в
        приложении про деньги недопустимы.
      */
      if (left > ZERO) {
        const nextReserves = reserves.filter(
          (x) => !(x.forKind === 'stable' && x.forId === plan.id),
        );
        for (const r of reserves.filter((x) => x.forKind === 'stable' && x.forId === plan.id)) {
          db.del('reserves', r.id);
        }
        let nextAccounts = [...accounts];
        if (transfer && from && to) {
          const tx = makeTx({
            type: 'transfer',
            accountId: from.id,
            counterAccountId: to.id,
            amount: left,
            date: todayPd(),
            note: 'Освобождение запаса при отключении режима',
          buffer: true,
            ...(current ? { allocationId: current.id } : {}),
          });
          db.put('transactions', tx);
          const nextFrom = { ...from, balance: sub(from.balance, left) };
          const nextTo = { ...to, balance: add(to.balance, left) };
          db.put('accounts', nextFrom);
          db.put('accounts', nextTo);
          nextAccounts = withRecord(withRecord(nextAccounts, nextFrom), nextTo);
        }
        db.put('stableMoves', {
          id: `stable:stop-release:${plan.id}:${todayPd()}`,
          planId: plan.id,
          date: todayPd(),
          amount: neg(left),
          kind: 'release',
          ...allocationMoveFields(
            current,
            allocationDelta(accounts, reserves, nextAccounts, nextReserves),
          ),
          createdAt: now,
        } satisfies BufferMove);
      }

      // Статус — в той же транзакции, что и деньги. Порознь это давало
      // отключение, при котором запас освобождён, а режим ещё работает.
      db.put('stablePlans', { ...plan, status: 'stopped' as const, stoppedAt: now });
    },
  ));
}

/**
 * Взять деньги из запаса раньше срока.
 *
 * Отдельное действие, а не досрочная выплата, и разница здесь не
 * формальная. Выплата закрывает **дату графика**: закрыв ближайшую, человек
 * получал ближайшей следующую и несколькими нажатиями подряд вычерпывал
 * запас на месяцы вперёд, а деньги, ушедшие сегодня, оказывались записаны
 * будущим месяцем.
 *
 * Здесь график не трогается вовсе: запас уменьшается, деньги становятся
 * нераспределёнными, ближайшая выплата остаётся на своём дне — и, если
 * запаса на неё уже не хватит, человек об этом честно предупреждён.
 */
export async function takeFromBuffer(input: {
  planId: string;
  amount: Money;
}): Promise<{ done: boolean; reason?: string }> {
  return queued(`stable:${input.planId}`, () => transact(
    ['stablePlans', 'stableMoves', 'reserves', 'accounts', 'transactions', 'allocations'],
    async (db): Promise<{ done: boolean; reason?: string }> => {
      const plan = (await db.all<StableBudgetPlan>('stablePlans')).find((p) => p.id === input.planId);
      if (!plan) return { done: false, reason: 'Режим не включён' };
      if (plan.status !== 'active') {
        return { done: false, reason: 'Режим приостановлен' };
      }
      const reserves = await db.all<Reserve>('reserves');
      const buffer = bufferOf(reserves, plan.id);
      if (input.amount <= ZERO) return { done: false, reason: 'Сумма не задана' };
      if (input.amount > buffer) {
        return { done: false, reason: `В запасе ${formatRub(buffer)} — этого не хватит` };
      }

      const accounts = await db.all<Account>('accounts');
      const from = accounts.find((a) => a.id === plan.accountId);
      const to = accounts.find((a) => a.id === plan.toAccountId);
      const moving = plan.toAccountId !== plan.accountId;
      if (moving && (!from || !to)) return { done: false, reason: 'Счёт не найден' };
      if (moving && to?.kind === 'credit_card') {
        return { done: false, reason: 'Брать себе на кредитку нельзя: выбери другой счёт' };
      }
      const current = activeAllocation(await db.all<AcceptedAllocation>('allocations'));
      const now = new Date().toISOString();

      // ─── дальше только записи ──────────────────────────────────────────

      const envelope = reserves.find(
        (r) => r.forKind === 'stable' && r.forId === plan.id && r.accountId === plan.accountId,
      );
      const left = sub(buffer, input.amount);
      let nextReserves = [...reserves];
      if (envelope) {
        if (left <= ZERO) {
          db.del('reserves', envelope.id);
          nextReserves = nextReserves.filter((r) => r.id !== envelope.id);
        } else {
          const nextEnvelope = { ...envelope, amount: left };
          db.put('reserves', nextEnvelope);
          nextReserves = withRecord(nextReserves, nextEnvelope);
        }
      }
      let nextAccounts = [...accounts];
      if (moving && from && to) {
        const tx = makeTx({
          type: 'transfer',
          accountId: from.id,
          counterAccountId: to.id,
          amount: input.amount,
          date: todayPd(),
          note: 'Взято из запаса раньше срока',
          buffer: true,
          ...(current ? { allocationId: current.id } : {}),
        });
        db.put('transactions', tx);
        const nextFrom = { ...from, balance: sub(from.balance, input.amount) };
        const nextTo = { ...to, balance: add(to.balance, input.amount) };
        db.put('accounts', nextFrom);
        db.put('accounts', nextTo);
        nextAccounts = withRecord(withRecord(nextAccounts, nextFrom), nextTo);
      }
      db.put('stableMoves', {
        id: uid(),
        planId: plan.id,
        date: todayPd(),
        amount: neg(input.amount),
        kind: 'withdrawal',
        ...allocationMoveFields(
          current,
          allocationDelta(accounts, reserves, nextAccounts, nextReserves),
        ),
        createdAt: now,
      } satisfies BufferMove);
      return { done: true };
    },
  ));
}

/**
 * Сверка после долгого отсутствия.
 *
 * Человек не заходил три месяца — у недельного режима это тринадцать
 * нерешённых наступлений. Спрашивать по одному («что было 1 октября? а
 * 8-го?») бессмысленно: он не помнит и не станет. Но и молча забыть их
 * нельзя — тогда приложение сделает вид, что всё это время всё шло по
 * плану.
 *
 * Поэтому отрезок закрывается одним решением, и решений три:
 *
 * · `paid` — «выплачивал себе»: наступления закрываются как сверенные, а
 *   остаток запаса приводится к тому, что человек видит в банке. Суммы по
 *   датам не выдумываются: их не знает никто;
 * · `skipped` — «не выплачивал»: каждое наступление закрывается явным
 *   пропуском, деньги в запасе остаются;
 * · `restart` — «начать заново с сегодня»: прошлый отрезок закрывается
 *   паузой, история остаётся, ближайшей становится первая дата с сегодня.
 */
export type ReconcileInput =
  /** «Выплачивал себе»: сумма остатка обязательна — только она и известна. */
  | { planId: string; mode: 'paid'; bufferNow: Money }
  | { planId: string; mode: 'skipped' | 'restart' };

export async function reconcileStable(input: ReconcileInput): Promise<{
  done: boolean;
  reason?: string;
  closed: number;
  /** Картина изменилась, пока человек думал: нужно свериться заново. */
  stale?: boolean;
}> {
  return queued(`stable:${input.planId}`, () => transact(
    ['stablePlans', 'stablePayouts', 'stableMoves', 'reserves', 'accounts', 'allocations'],
    async (db): Promise<{ done: boolean; reason?: string; closed: number; stale?: boolean }> => {
      const plan = (await db.all<StableBudgetPlan>('stablePlans')).find((p) => p.id === input.planId);
      if (!plan) return { done: false, reason: 'Режим не включён', closed: 0 };

      const reserves = await db.all<Reserve>('reserves');
      const accounts = await db.all<Account>('accounts');
      const allocation = activeAllocation(await db.all<AcceptedAllocation>('allocations'));
      const facts = (await db.all<StablePayoutFact>('stablePayouts'))
        .filter((x) => x.planId === plan.id);
      const buffer = bufferOf(reserves, plan.id);
      /*
        Разрыв пересчитывается **внутри** транзакции.

        Список дат, собранный до неё, за это время устаревает: другая
        вкладка могла выплатить, пропустить или уже свериться. Закрыть по
        старому списку значило бы записать решения о наступлениях, которых
        человек уже не видит.
      */
      const gap = stableOutlook({ today: todayPd(), plan, buffer, payouts: facts }).gap;
      if (!gap) return { done: false, reason: 'Сверять нечего', closed: 0, stale: true };

      const now = new Date().toISOString();
      const existing = new Set(facts.map((x) => x.id));

      if (input.mode === 'restart') {
        /*
          Прошлое закрывается паузой, а не записями о выплатах: человек
          сказал «начать заново», а не «я их получил». Наступления внутри
          отрезка не создаются, история настоящих выплат цела.
        */
        db.put('stablePlans', {
          ...plan,
          pauses: [...(plan.pauses ?? []), { from: gap.from, to: todayPd() }],
        });
        return { done: true, closed: gap.count };
      }

      if (input.mode === 'paid') {
        // Сверка не вправе создать запас, которого нет: больше свободных
        // денег на счёте закрепить нельзя, иначе одни и те же рубли окажутся
        // и в запасе, и под платежом.
        if (input.bufferNow < ZERO) {
          return { done: false, reason: 'Сумма не может быть отрицательной', closed: 0 };
        }
        const account = accounts.find((a) => a.id === plan.accountId);
        if (!account) return { done: false, reason: 'Счёт не найден', closed: 0 };
        const others = add(...reserves
          .filter((r) => r.accountId === plan.accountId)
          .filter((r) => !(r.forKind === 'stable' && r.forId === plan.id))
          .map((r) => r.amount));
        const room = clampPos(sub(account.balance, others));
        if (input.bufferNow > room) {
          return {
            done: false,
            reason: `На счёте свободно ${formatRub(room)} — больше в запасе быть не может`,
            closed: 0,
          };
        }
      }

      // ─── дальше только записи ────────────────────────────────────────────

      for (const date of gap.dates) {
        const id = payoutId(plan.id, date);
        if (existing.has(id)) continue;
        db.put('stablePayouts', {
          id,
          planId: plan.id,
          scheduledDate: date,
          decision: input.mode === 'paid' ? 'reconciled' as const : 'skipped' as const,
          amount: ZERO,
          via: 'none' as const,
          paidOn: todayPd(),
          paidAt: now,
        } satisfies StablePayoutFact);
      }

      /*
        «Выплачивал себе» требует сверки остатка: деньги уходили мимо
        приложения, и единственное, что о них известно точно, — сколько их
        осталось. Разница записывается движением `reconcile` и в отчёте
        стоит отдельной строкой, а не притворяется выплатой.
      */
      if (input.mode === 'paid' && input.bufferNow !== buffer) {
        const envelope = reserves.find(
          (r) => r.forKind === 'stable' && r.forId === plan.id && r.accountId === plan.accountId,
        );
        let nextReserves = [...reserves];
        if (input.bufferNow <= ZERO) {
          if (envelope) db.del('reserves', envelope.id);
          if (envelope) nextReserves = nextReserves.filter((r) => r.id !== envelope.id);
        } else {
          const nextEnvelope: Reserve = {
            id: envelope?.id ?? uid(),
            accountId: plan.accountId,
            forKind: 'stable' as const,
            forId: plan.id,
            amount: input.bufferNow,
          };
          db.put('reserves', nextEnvelope);
          nextReserves = withRecord(nextReserves, nextEnvelope);
        }
        db.put('stableMoves', {
          id: uid(),
          planId: plan.id,
          date: todayPd(),
          amount: sub(input.bufferNow, buffer),
          kind: 'reconcile',
          ...allocationMoveFields(
            allocation,
            allocationDelta(accounts, reserves, accounts, nextReserves),
          ),
          createdAt: now,
        } satisfies BufferMove);
      }

      return { done: true, closed: gap.count };
    },
  ));
}

/**
 * Долить буфер из аварийной подушки.
 *
 * Отдельная функция, а не ветка пополнения, потому что это **другое**
 * решение. Подушка собрана под потерю дохода и болезнь; тратить её на
 * ровное течение месяца можно, но только сознательно и зная цену. Молча
 * приложение не берёт оттуда ни рубля — ни при нехватке буфера, ни при
 * пропущенной выплате.
 *
 * Доходом перемещение не становится: деньги никуда не приходили, они сменили
 * назначение. В журнале цели остаётся изъятие — след, по которому видно, что
 * подушка похудела не сама.
 */
export async function moveFromEmergencyFund(input: {
  planId: string;
  goalId: string;
  amount: Money;
}): Promise<{ done: boolean; reason?: string }> {
  return queued(`stable:${input.planId}`, () => transact(
    ['stablePlans', 'goals', 'reserves', 'goalAllocations', 'stableMoves', 'accounts',
      'allocations'],
    async (db): Promise<{ done: boolean; reason?: string }> => {
      const plan = (await db.all<StableBudgetPlan>('stablePlans')).find((p) => p.id === input.planId);
      const goal = (await db.all<Goal>('goals')).find((g) => g.id === input.goalId);
      if (!plan || !goal) return { done: false, reason: 'Не найдено' };
      if (input.amount <= ZERO) return { done: false, reason: 'Сумма не задана' };
      if (input.amount > goal.currentAmount) {
        return { done: false, reason: 'В подушке меньше денег' };
      }

      const reserves = await db.all<Reserve>('reserves');
      const accounts = await db.all<Account>('accounts');
      const allocation = activeAllocation(await db.all<AcceptedAllocation>('allocations'));
      // Конверт цели уменьшается на ту же сумму, если он был: иначе одни и
      // те же рубли остались бы связанными дважды — и подушкой, и буфером.
      let rest = input.amount;
      let nextReserves = [...reserves];
      for (const r of reserves.filter((x) => x.forKind === 'goal' && x.forId === goal.id)) {
        if (rest <= ZERO) break;
        const take = minM(r.amount, rest);
        const left = sub(r.amount, take);
        if (left <= ZERO) {
          db.del('reserves', r.id);
          nextReserves = nextReserves.filter((x) => x.id !== r.id);
        } else {
          const nextReserve = { ...r, amount: left };
          db.put('reserves', nextReserve);
          nextReserves = withRecord(nextReserves, nextReserve);
        }
        rest = sub(rest, take);
      }

      db.put('goals', { ...goal, currentAmount: sub(goal.currentAmount, input.amount) });
      db.put('goalAllocations', {
        key: `${goal.id}:${todayPd()}:withdrawal`,
        goalId: goal.id,
        cycleStart: todayPd(),
        date: todayPd(),
        kind: 'withdrawal' as const,
        amount: input.amount,
        via: 'manual' as const,
        createdAt: new Date().toISOString(),
      } satisfies GoalEvent);

      const envelope = reserves.find(
        (r) => r.forKind === 'stable' && r.forId === plan.id && r.accountId === plan.accountId,
      );
      const stableReserve: Reserve = {
        id: envelope?.id ?? uid(),
        accountId: plan.accountId,
        forKind: 'stable' as const,
        forId: plan.id,
        amount: add(bufferOf(reserves, plan.id), input.amount),
      };
      db.put('reserves', stableReserve);
      nextReserves = withRecord(nextReserves, stableReserve);
      db.put('stableMoves', {
        id: uid(),
        planId: plan.id,
        date: todayPd(),
        amount: input.amount,
        kind: 'from_fund',
        ...allocationMoveFields(
          allocation,
          allocationDelta(accounts, reserves, accounts, nextReserves),
        ),
        createdAt: new Date().toISOString(),
      } satisfies BufferMove);

      return { done: true };
    },
  ));
}

const formatRub = (m: Money): string => `${Math.round(m / 100).toLocaleString('ru-RU')} ₽`;

/**
 * Применить распределение остатка.
 *
 * Одна транзакция: новая версия раскладки и конверты пишутся вместе. Порознь
 * это давало бы обещанные деньги без конверта или конверт без версии — и в
 * обоих случаях сверка потом обвинила бы человека в расхождении.
 *
 * Версия сравнивается с действующей: если в другой вкладке приняли новую
 * раскладку или подтвердили платёж, предложение устарело, и применять его
 * нельзя — суммы в нём уже не те.
 */
export async function applyRest(input: {
  /** Версия, на которой человек принимал решение. */
  allocationId: string;
  targets: readonly RestTarget[];
}): Promise<{ done: boolean; reason?: string; stale?: boolean }> {
  return queued('allocation:rest', () => transact(
    ['allocations', 'reserves', 'accounts', 'transactions', 'stablePayouts', 'stableMoves'],
    async (db): Promise<{ done: boolean; reason?: string; stale?: boolean }> => {
      const all = await db.all<AcceptedAllocation>('allocations');
      const current = activeAllocation(all);
      if (!current) return { done: false, reason: 'Раскладки ещё нет' };
      if (current.id !== input.allocationId) {
        return { done: false, reason: 'Раскладка изменилась — открой её заново', stale: true };
      }

      const accounts = await db.all<Account>('accounts');
      const reserves = await db.all<Reserve>('reserves');
      const transactions = await db.all<Tx>('transactions');
      const payouts = await db.all<StablePayoutFact>('stablePayouts');
      const moves = await db.all<BufferMove>('stableMoves');
      const view = allocationView({
        allocation: current,
        accounts,
        reserves,
        entries: allocationEntries(transactions, payouts),
        selfPayouts: payouts.map((x) => ({
          amount: x.unallocatedDelta ?? x.amount,
          ...(x.allocationId ? { allocationId: x.allocationId } : {}),
        })),
        bufferAdjustments: moves
          .filter((x) => x.unallocatedDelta !== undefined)
          .map((x) => ({
            delta: x.unallocatedDelta!,
            ...(x.allocationId ? { allocationId: x.allocationId } : {}),
          })),
      });
      const envelopes: Record<string, Money> = {};
      for (const r of reserves) {
        const key = `${r.forKind}:${r.forId}`;
        envelopes[key] = add(envelopes[key] ?? ZERO, r.amount);
      }

      const plan = distributeRest({
        allocation: current,
        current: {
          need: view.needLeft,
          want: view.wantLeft,
          carry: view.carry,
          unallocated: view.unallocated,
          owed: view.owedBy,
        },
        liquid: liquidOf(accounts),
        reserved: reservedOf(accounts, reserves),
        envelopes,
        targets: input.targets,
      });
      if (!plan.ok) return { done: false, ...(plan.reason ? { reason: plan.reason } : {}) };
      if (plan.assigned <= ZERO) return { done: false, reason: 'Ничего не назначено' };

      const now = new Date().toISOString();
      const spend = accounts.find(
        (a) => a.isLiquid && !a.archived && a.kind !== 'credit_card',
      );

      // ─── дальше только записи ────────────────────────────────────────────

      for (const e of plan.add) {
        const existing = reserves.find(
          (r) => r.forKind === e.kind && r.forId === e.id && r.accountId === (spend?.id ?? ''),
        );
        const held = envelopes[`${e.kind}:${e.id}`] ?? ZERO;
        if (!spend) continue;
        db.put('reserves', {
          id: existing?.id ?? uid(),
          accountId: spend.id,
          forKind: e.kind,
          forId: e.id,
          amount: add(existing ? existing.amount : ZERO, e.amount),
        });
        void held;
      }

      db.put('allocations', {
        ...current,
        id: uid(),
        acceptedAt: now,
        effectiveFrom: now,
        basis: 'manual' as const,
        lines: plan.lines,
        reserved: plan.after.reserved,
        supersedes: current.id,
        // Отпечаток меняется вместе с деньгами: старое предложение раскладки
        // после этого честно считается устаревшим.
        inputVersion: `${current.inputVersion}|rest:${now}`,
      } satisfies AcceptedAllocation);

      return { done: true };
    },
  ));
}

export async function deleteReserve(id: string): Promise<void> {
  await remove('reserves', id);
}

export async function updateIncome(id: string, patch: Partial<IncomeStream>): Promise<void> {
  const all = await getAll<IncomeStream>('incomeStreams');
  const s = all.find((x) => x.id === id);
  if (!s) return;
  await put('incomeStreams', { ...s, ...patch });
}

export async function deleteIncome(id: string): Promise<void> {
  await remove('incomeStreams', id);
}

export async function updateObligation(id: string, patch: Partial<Obligation>): Promise<void> {
  const all = await getAll<Obligation>('obligations');
  const o = all.find((x) => x.id === id);
  if (!o) return;
  /*
    Платёж подорожал — прошлые оплаты остаются по прежней сумме: оплаченное
    по 6 000 не становится долгом в 3 000 оттого, что с этой недели няня
    стоит 9 000. Снимок ставится только на подорожание: ошибку в сумме
    исправляют и вниз, и тогда новая, меньшая сумма прошлого не портит.
  */
  const raised = patch.amount !== undefined && patch.amount > o.amount;
  const was = instanceAmount(o);
  const today = todayPd();
  const payments = raised && o.payments
    ? o.payments.map((p) => (p.requiredAt === undefined && p.paid > ZERO && p.scheduledDate <= today
      ? { ...p, requiredAt: was } : p))
    : o.payments;
  await put('obligations', { ...o, ...patch, ...(payments ? { payments } : {}) });
  /*
    Платёж подорожал, а под него уже отложено — отложенное растёт вместе с
    ним, если на том же счёте есть свободные деньги.

    Человек обеспечил няню на 6 000 и поднял сумму до 9 000: платёж для него
    по-прежнему обеспечен, а приложение писало «не хватает 3 000».
  */
  if (patch.amount !== undefined && patch.amount > o.amount) {
    const reserves = await getAll<Reserve>('reserves');
    const mine = reserves.filter((r) => r.forKind === 'obligation' && r.forId === id && r.amount > ZERO);
    const held = add(...mine.map((r) => r.amount));
    const r = mine[0];
    if (r && held < patch.amount) {
      const acc = (await getAll<Account>('accounts')).find((a) => a.id === r.accountId);
      const taken = add(...reserves.filter((x) => x.accountId === r.accountId).map((x) => x.amount));
      const free = acc ? clampPos(sub(acc.balance, taken)) : ZERO;
      const more = minM(sub(patch.amount, held), free);
      if (more > ZERO) await put('reserves', { ...r, amount: add(r.amount, more) });
    }
  }
}

export async function deleteObligation(id: string): Promise<void> {
  // Конверты, закреплённые под это обязательство, теряют смысл вместе с ним.
  const reserves = await getAll<Reserve>('reserves');
  for (const r of reserves.filter((x) => x.forKind === 'obligation' && x.forId === id)) {
    await remove('reserves', r.id);
  }
  await remove('obligations', id);
}

/**
 * «Больше не плачу» — подписку отменили (04.11.2026).
 *
 * Не удаление: прошлые оплаты остаются в истории и в аналитике. Будущих
 * списаний больше нет — платёж выключается, а конверты под него
 * освобождаются: держать деньги под то, что не спишется, незачем.
 */
export async function stopObligation(id: string): Promise<void> {
  const reserves = await getAll<Reserve>('reserves');
  for (const r of reserves.filter((x) => x.forKind === 'obligation' && x.forId === id)) {
    await remove('reserves', r.id);
  }
  await updateObligation(id, { isActive: false });
}

export async function updateGoal(id: string, patch: Partial<Goal>): Promise<void> {
  const all = await getAll<Goal>('goals');
  const g = all.find((x) => x.id === id);
  if (!g) return;
  await put('goals', { ...g, ...patch });
}

/**
 * Подтвердить платёж по обязательству.
 *
 * Создаёт настоящий расход и помечает цикл подтверждённым. Два действия
 * неразделимы: подтверждение без транзакции сняло бы резерв, не оставив следа
 * в истории, и сверка потом списала бы эти деньги в «Прочее» как неизвестную
 * трату.
 */
/**
 * Подтвердить платёж по обязательству.
 *
 * Три вещи здесь различаются намеренно.
 *
 * `dueDate` — ключ цикла: по нему обязательство считается закрытым до
 * следующей зарплаты, и он не зависит от того, когда человек заплатил.
 *
 * `paidDate` — фактическая дата: платёж, запланированный на двадцатое, мог
 * уйти семнадцатого или двадцать второго. В историю и в аналитику попадает
 * фактическая, иначе месячная разбивка врёт.
 *
 * `applyToBalance = false` — капитал уже уменьшился вне приложения. Так бывает
 * на первом дне: платёж оплачен утром, а остатки на счетах названы вечером
 * уже за вычетом. Расход всё равно записывается — след нужен, — но баланс не
 * трогается, иначе деньги спишутся дважды.
 */
export async function settleObligation(input: {
  obligationId: string;
  dueDate: PlainDate;
  amount: Money;
  categoryId?: string;
  paidDate?: PlainDate;
  accountId?: string;
  applyToBalance?: boolean;
  /** Время платежа «ЧЧ:ММ» — как у ручной записи. */
  time?: string;
  /** Заметка человека; без неё в истории остаётся имя обязательства. */
  note?: string;
  /**
   * Ключ подтверждения: один на одно нажатие.
   *
   * Различает повтор доставки того же действия и вторую настоящую оплату.
   * Прежняя защита сравнивала сумму и дату — и два честных взноса по 4 000 ₽
   * в один день, сделанных с разных счетов, схлопывались в один. Сумма и дата
   * совпадения не доказывают: человек вправе заплатить дважды.
   */
  requestId?: string;
}): Promise<void> {
  // Подтверждения одного наступления выстраиваются в очередь: между чтением
  // факта и его записью иначе вклинивается второе подтверждение, и одна из
  // двух частичных оплат теряется.
  const key = `${input.obligationId}:${input.dueDate}`;
  const prior = settleQueue.get(key) ?? Promise.resolve();
  const next = prior.then(() => settleOnce(input), () => settleOnce(input));
  settleQueue.set(key, next.then(() => undefined, () => undefined));
  return next;
}

/** Очередь подтверждений по наступлениям. Живёт только в этой вкладке. */
const settleQueue = new Map<string, Promise<void>>();

async function settleOnce(input: {
  obligationId: string;
  dueDate: PlainDate;
  amount: Money;
  categoryId?: string;
  paidDate?: PlainDate;
  accountId?: string;
  applyToBalance?: boolean;
  time?: string;
  note?: string;
  requestId?: string;
}): Promise<void> {
  const all = await getAll<Obligation>('obligations');
  const o = all.find((x) => x.id === input.obligationId);
  if (!o) throw new Error('Обязательство не найдено');

  const paidDate = input.paidDate ?? input.dueDate;

  // Повтор доставки одного и того же действия.
  //
  // Узнаётся по ключу запроса, а не по сумме: между нажатием и записью
  // проходит время, палец успевает нажать дважды, и списать деньги второй раз
  // — самая дорогая ошибка, какую тут можно сделать.
  if (input.requestId) {
    const txs = await getAll<Tx>('transactions');
    if (txs.some((t) => t.requestId === input.requestId)) return;
  }

  const tx = await addExpense({
    accountId: input.accountId ?? o.accountId,
    amount: input.amount,
    categoryId: input.categoryId ?? o.categoryId ?? 'other',
    part: o.part,
    date: paidDate,
    ...(input.time ? { time: input.time } : {}),
    note: input.note ?? o.name,
    // Связь с конкретным наступлением, а не с обязательством вообще:
    // у недельного платежа их четыре в месяц, и «оплачен» относится к одному.
    link: { kind: 'obligation', obligationId: o.id, instanceId: `${o.id}:${input.dueDate}` },
    // Решение человека передаётся как есть, включая явное «учесть в остатке»:
    // подменять сказанное догадкой значит терять настоящие траты.
    ...(input.applyToBalance === undefined ? {} : { applyToBalance: input.applyToBalance }),
    ...(input.requestId ? { requestId: input.requestId } : {}),
  });
  // Конверт под этот платёж больше не нужен: деньги ушли. Сколько взято из
  // него — в операции: остальное раскладка спишет со свободных денег.
  const used = await releaseObligationReserve(o.id, input.amount, input.accountId ?? o.accountId);
  await put('transactions', { ...tx, reserveUsed: used });
  await syncObligationFacts(o.id);
}

/**
 * Засчитать записанный расход как оплату платежа.
 *
 * Расход уже уменьшил счёт — второй раз его не списываем. Связь ставится на
 * наступление, конверт под платёж снимается на сумму оплаты (сколько снято —
 * в `reserveUsed`), факты оплаты пересчитываются.
 */
export async function linkPaymentTx(txId: string, obligationId: string, dueDate: PlainDate): Promise<boolean> {
  const tx = (await getAll<Tx>('transactions')).find((t) => t.id === txId);
  const o = (await getAll<Obligation>('obligations')).find((x) => x.id === obligationId);
  if (!tx || !o || tx.type !== 'expense' || tx.link) return false;
  const used = await releaseObligationReserve(o.id, tx.amount, tx.accountId);
  await put('transactions', {
    ...tx,
    link: { kind: 'obligation', obligationId: o.id, instanceId: `${o.id}:${dueDate}` },
    reserveUsed: used,
  });
  await syncObligationFacts(o.id);
  return true;
}

/**
 * Снять конверт после оплаты.
 *
 * Конверт — это обещание: «эти деньги лежат под ипотеку». Платёж обещание
 * исполняет, и держать его дальше значит вычитать одни и те же деньги дважды —
 * они и ушли со счёта, и продолжают считаться закреплёнными. У владельца это
 * ровно так и вышло: ипотека 20-го оплачена, а 36 000 ₽ на счёте всё ещё
 * заперты, и «Доступно» на столько же меньше правды.
 *
 * Снимается **ровно оплаченное**, а не конверт целиком: человек мог отложить
 * с запасом на следующий месяц, и лишать его этого запаса за то, что он
 * заплатил, нельзя. Первым разбирается конверт на счёте, с которого платили:
 * деньги физически ушли именно оттуда.
 *
 * Для кредитки то же самое делает `applyCardPayment` — у обязательств этого
 * просто не было.
 */
async function releaseObligationReserve(
  obligationId: string, paid: Money, fromAccountId: string,
): Promise<Money> {
  const held = (await getAll<Reserve>('reserves'))
    .filter((r) => r.forKind === 'obligation' && r.forId === obligationId && r.amount > ZERO)
    .sort((a, b) => Number(b.accountId === fromAccountId) - Number(a.accountId === fromAccountId));

  let left = paid;
  for (const r of held) {
    if (left <= ZERO) break;
    const take = minM(r.amount, left);
    const rest = sub(r.amount, take);
    if (rest <= ZERO) await remove('reserves', r.id);
    else await put('reserves', { ...r, amount: rest });
    left = sub(left, take);
  }
  return sub(paid, left);
}

/**
 * Пересчитать факты оплаты обязательства по его операциям.
 *
 * Факт — проекция операций, а не второй источник правды. Пока он жил своей
 * жизнью, удалённая операция оставляла платёж «оплаченным», а исправленная с
 * 8 000 на 3 000 — закрытым целиком: приложение переставало удерживать деньги
 * на платёж, который человеку ещё предстоит сделать.
 *
 * Пересчёт идёт одной транзакцией: между чтением обязательства и записью
 * иначе вклинивается вторая оплата, и один из фактов теряется.
 */
export async function syncObligationFacts(obligationId: string): Promise<void> {
  const txs = await getAll<Tx>('transactions');
  const mine = txs.filter((t) => t.link?.kind === 'obligation' && t.link.obligationId === obligationId);

  await mutate<Obligation>('obligations', obligationId, (current) => {
    if (!current) return undefined;
    /*
      Сумма и операции — из операций; решение человека и снимок суммы — из
      прежней записи. Раньше запись строилась заново и теряла и то и другое:
      «платежа не было» воскресало, а прошлая оплата начинала требовать
      доплату по новой сумме. Записи без операций (оплата из первого
      знакомства, старые подтверждения) операциями не управляются — остаются.
    */
    const before = new Map((current.payments ?? []).map((p) => [p.scheduledDate, p]));
    const byDate = new Map<PlainDate, ObligationPayment>();
    for (const p of before.values()) {
      if (p.txIds.length === 0) byDate.set(p.scheduledDate, p);
      else if (p.decision || p.requiredAt !== undefined) {
        byDate.set(p.scheduledDate, { ...p, paid: ZERO, txIds: [] });
      }
    }
    for (const t of mine) {
      if (t.link?.kind !== 'obligation') continue;
      // Ключ наступления — часть идентичности связи: `${obligationId}:${date}`.
      const scheduledDate = t.link.instanceId.slice(obligationId.length + 1) as PlainDate;
      const fact = byDate.get(scheduledDate) ?? { scheduledDate, paid: ZERO, txIds: [] };
      byDate.set(scheduledDate, { ...fact, paid: add(fact.paid, t.amount), txIds: [...fact.txIds, t.id] });
    }
    const payments = [...byDate.values()]
      .sort((a, b) => (a.scheduledDate < b.scheduledDate ? -1 : 1));
    // Прежнее поле больше не ведётся: две правды об одном платеже разошлись бы
    // при первой же частичной оплате.
    const { settledCycle, ...rest } = current;
    void settledCycle;
    return { ...rest, payments };
  });
}

/**
 * Факты оплаты с учётом старых записей.
 *
 * У обязательства, подтверждённого до появления фактов, известен только
 * последний цикл. Он переносится как полная оплата — ровно так его и читали
 * расчёты, и менять смысл задним числом нельзя.
 */
export function factsWithLegacy(o: Obligation): ObligationPayment[] {
  if (o.payments && o.payments.length > 0) return o.payments;
  if (!o.settledCycle) return [];
  return [{
    scheduledDate: o.settledCycle,
    paid: o.amountType === 'fixed' ? o.amount : (o.amountEstimate ?? o.amount),
    txIds: [],
  }];
}


/** Снять подтверждение: платёж не проходил, резерв должен вернуться. */
/**
 * Решение по одному наступлению платежа.
 *
 * Отдельно от оплаты, потому что это разные события. Оплата — факт, у неё
 * есть операция и сумма. Решение — ответ человека на вопрос приложения: «этот
 * платёж всё ещё нужно сделать» или «его в этот раз не было».
 *
 * Нулевая сумма их не различает, и в этом была дыра: приложение не могло
 * отличить забытый долг от платежа, которого не существовало, и либо
 * требовало денег вечно, либо забывало о долге.
 *
 * Решение закрывает **одну дату**. Обязательство продолжает жить: няня,
 * уехавшая в отпуск на неделю, остаётся няней.
 */
export async function decideInstance(input: {
  obligationId: string;
  scheduledDate: PlainDate;
  decision: PaymentDecision;
  /** Сумма требования на момент решения. Снимок, чтобы правки не переписали прошлое. */
  requiredAt: Money;
}): Promise<void> {
  await mutate<Obligation>('obligations', input.obligationId, (o) => {
    if (!o) return undefined;
    const rest = (o.payments ?? []).filter((p) => p.scheduledDate !== input.scheduledDate);
    const prior = (o.payments ?? []).find((p) => p.scheduledDate === input.scheduledDate);
    const fact: ObligationPayment = {
      scheduledDate: input.scheduledDate,
      paid: prior?.paid ?? ZERO,
      txIds: prior?.txIds ?? [],
      decision: input.decision,
      requiredAt: prior?.requiredAt ?? input.requiredAt,
      decidedAt: new Date().toISOString(),
    };
    return { ...o, payments: [...rest, fact].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate)) };
  });
}

export async function unsettleObligation(id: string): Promise<void> {
  const all = await getAll<Obligation>('obligations');
  const o = all.find((x) => x.id === id);
  if (!o) return;
  const { settledCycle, ...rest } = o;
  void settledCycle;
  await put('obligations', rest);
}

/**
 * «Обычно в месяц» для расчётов: по истории трат, с оценкой как стартовой точкой.
 *
 * Пока полных месяцев учёта нет, остаётся оценка со своим статусом — и «не
 * знаю» остаётся «не знаю». Платежи, записанные обычным расходом, в жизнь не
 * идут (`looksLikePayment`).
 */
export function usualState(
  estimates: { need: Amount; want: Amount }, pinned: readonly string[], transactions: readonly Tx[],
  obligations: readonly Obligation[], categories: readonly Category[], trackingStartedAt: PlainDate | undefined,
): Pick<AppState, 'needBaseline' | 'wantBaseline' | 'baselines' | 'estimates' | 'usual'> {
  const usual = usualSpending({
    txs: transactions, today: todayPd(), estimate: estimates,
    pinned: { need: pinned.includes('need'), want: pinned.includes('want') },
    isPayment: (t) => looksLikePayment({ obligations: [...obligations], categories: [...categories] }, t),
    ...(trackingStartedAt ? { trackingStartedAt } : {}),
  });
  const amountOf = (key: 'need' | 'want'): Amount => (usual[key].source === 'history' ? fromHistory(usual[key].value) : estimates[key]);
  return {
    needBaseline: usual.need.value, wantBaseline: usual.want.value,
    baselines: { need: amountOf('need'), want: amountOf('want') },
    estimates, usual,
  };
}

/** Закрепить своё «обычно» для части — или снова считать по истории. */
export const setUsualPinned = (part: 'need' | 'want', pinned: boolean): Promise<void> => queued('usualPinned', async () => {
  await mutate<{ key: string; value: string[] | null }>('meta', 'usualPinned', (row) => {
    const list = row?.value ?? [];
    if (list.includes(part) === pinned) return undefined;
    const next = pinned ? [...list, part] : list.filter((x) => x !== part);
    return { key: 'usualPinned', value: next.length > 0 ? next : null };
  });
});

/** Человек назвал суммы сам: это подтверждённые значения, включая ноль. */
export async function setBaselines(need: Money, want: Money): Promise<void> {
  await setMeta('needBaseline', need);
  await setMeta('wantBaseline', want);
}

/** Запись значений со статусом: «не знаю» стирает прежнее число. */
export async function setBaselineAmounts(need: Amount, want: Amount): Promise<void> {
  await setMeta('needBaseline', need.known ? need.value : null);
  await setMeta('wantBaseline', want.known ? want.value : null);
}

/** Режим плана и выбор между резервом и гашением — решения человека. */
export const setPlanMode = (mode: PlanMode | null): Promise<void> => setMeta('planMode', mode);
/**
 * Откуда считать период — выбор человека (02.11.2026). Применяется сразу:
 * идущий период пересчитывается от последнего прихода выбранного дохода или
 * с 1-го числа (`choicePins`). `null` — снова выбирает само приложение.
 */
export const setPeriodChoice = (c: PeriodChoice | null): Promise<void> => queued('periods', () => transact(
  ['transactions', 'incomeStreams', 'expectedIncomes', 'stablePlans', 'stablePayouts', 'meta'],
  async (scope) => {
    scope.put('meta', { key: 'periodChoice', value: c });
    if (!c) return;
    const txs = await scope.all<Tx>('transactions');
    const streams = await scope.all<IncomeStream>('incomeStreams');
    const expectations = await scope.all<IncomeExpectation>('expectedIncomes');
    const stablePlans = await scope.all<StableBudgetPlan>('stablePlans');
    const stablePayouts = await scope.all<StablePayoutFact>('stablePayouts');
    const pins = (await scope.get<{ key: string; value: PeriodPin[] | null }>('meta', 'periodPins'))?.value ?? [];
    const since = (await scope.get<{ key: string; value: PlainDate | null }>('meta', 'trackingStartedAt'))?.value;
    const next = choicePins(pins, {
      today: todayPd(), ...(since ? { since } : {}), streams, stablePlans, stablePayouts, pins, choice: c,
      incomes: periodIncomes(txs, expectations),
    }, c);
    scope.put('meta', { key: 'periodPins', value: next.map((p) => (p.at ? p : { ...p, at: stamp() })) });
  },
)).then(() => { void settlePeriods(); });

export const setTradeoffChoice = (c: TradeoffChoice | null): Promise<void> =>
  setMeta('tradeoffChoice', c);
/** Как поступать с покупками без подходящей категории — ответ человека. */
export const setNewCategoryMode = (mode: NewCategoryMode | null): Promise<void> =>
  setMeta('newCategoryMode', mode);

/**
 * Черновик настройки.
 *
 * Раньше он жил в памяти: закрыл вкладку — и всё заново. Человек, которого
 * прервали на середине, возвращался к пустому первому вопросу, и это худший
 * момент для потери данных: он ещё ничего не получил взамен.
 *
 * Хранится отдельно от рабочих записей: незавершённая настройка не должна
 * попадать в расчёты.
 */
export interface DraftRecord {
  key: 'onboarding';
  /** Текущий шаг: возврат продолжает с него, а не с начала. */
  step: string;
  /** Данные формы как есть — строками, до разбора в суммы. */
  data: unknown;
  updatedAt: string;
}

export const loadDraft = async (): Promise<DraftRecord | null> =>
  (await getAll<DraftRecord>('onboardingDrafts')).find((d) => d.key === 'onboarding') ?? null;

export async function saveDraft(step: string, data: unknown): Promise<void> {
  await put('onboardingDrafts', {
    key: 'onboarding', step, data, updatedAt: new Date().toISOString(),
  } satisfies DraftRecord);
}

/** Настройка завершена или начата заново: черновик больше не нужен. */
export const clearDraft = (): Promise<void> => remove('onboardingDrafts', 'onboarding');

/** Сохранить версию плана периода: снимок сумм, а не ссылка на данные. */
export async function saveAcceptedPlan(plan: AcceptedPlan): Promise<void> {
  await put('acceptedPlans', plan);
}

/**
 * Приходы для границ периодов аналитики.
 *
 * Приход, закрывший ожидание, несёт его дату: это явная связь с конкретной
 * зарплатой, и она сильнее расписания.
 */
export function periodIncomes(
  txs: readonly Tx[], expectations: readonly IncomeExpectation[] = [],
): PeriodIncome[] {
  const out: PeriodIncome[] = [];
  for (const t of txs) {
    if (t.type !== 'income') continue;
    const link = t.link?.kind === 'income' ? t.link : undefined;
    const streamId = link?.incomeStreamId;
    const expected = link?.expectationId
      ? expectations.find((e) => e.id === link.expectationId && e.streamId === streamId)
      : undefined;
    out.push({
      txId: t.id,
      date: t.occurredDate,
      amount: t.amount,
      kind: link?.incomeKind ?? (streamId ? 'regular' : 'oneoff'),
      ...(streamId ? { streamId } : {}),
      ...(link?.occurrence ? { occurrence: link.occurrence } : {}),
      ...(link?.occurrenceBasis ? { occurrenceBasis: link.occurrenceBasis } : {}),
      ...(expected ? { expectedDate: expected.expectedDate } : {}),
      // Ответ при записи части: «жду остаток» или «не жду» (ноль).
      ...(link?.expectedRest !== undefined ? { expectedRest: link.expectedRest } : {}),
      // Когда записана: по этому времени упорядочены ответы человека.
      recordedAt: t.createdAt,
    });
  }
  return out;
}

/** Наступление дохода, которое закрывает приход, — по свежим источнику и ожиданию. */
async function occurrenceFor(tx: Tx) {
  const link = tx.link?.kind === 'income' ? tx.link : undefined;
  if (!link?.incomeStreamId) return null;
  const stream = (await getAll<IncomeStream>('incomeStreams')).find((x) => x.id === link.incomeStreamId);
  const expectations = link.expectationId ? await getAll<IncomeExpectation>('expectedIncomes') : [];
  const f = periodIncomes([tx], expectations)[0];
  return f ? occurrenceOf(stream, f) : null;
}

/**
 * Записать, что решено про периоды аналитики, — по свежим данным хранилища.
 *
 * Два решения: приходам, которым ещё не записано наступление, — какую
 * зарплату они закрывают; источнику — закрепление, если главный доход
 * сменился. Оба принимаются **внутри одной транзакции** по операциям,
 * доходам, ожиданиям, выплатам и закреплениям, прочитанным в ней же. Снимок
 * экрана сюда не попадает: вкладка, открытая до отключения зарплаты, иначе
 * закрепила бы её обратно, а приход с исправленной датой получил бы
 * наступление по прежней.
 */
export const settlePeriods = (): Promise<boolean> => queued('periods', () => transact(
  ['transactions', 'incomeStreams', 'expectedIncomes', 'stablePlans', 'stablePayouts', 'meta'],
  async (scope) => {
    const txs = await scope.all<Tx>('transactions');
    const streams = await scope.all<IncomeStream>('incomeStreams');
    const expectations = await scope.all<IncomeExpectation>('expectedIncomes');
    const stablePlans = await scope.all<StableBudgetPlan>('stablePlans');
    const stablePayouts = await scope.all<StablePayoutFact>('stablePayouts');
    const pins = (await scope.get<{ key: string; value: PeriodPin[] | null }>('meta', 'periodPins'))?.value ?? [];
    const since = (await scope.get<{ key: string; value: PlainDate | null }>('meta', 'trackingStartedAt'))?.value;
    const choice = (await scope.get<{ key: string; value: PeriodChoice | null }>('meta', 'periodChoice'))?.value ?? null;
    const input = {
      today: todayPd(),
      choice,
      ...(since ? { since } : {}),
      streams, stablePlans, stablePayouts, pins,
      incomes: periodIncomes(txs, expectations),
    };

    let wrote = false;
    const marks = new Map(pendingOccurrences(input).map((p) => [p.txId, p]));
    for (const t of txs) {
      const m = marks.get(t.id);
      if (!m || t.link?.kind !== 'income') continue;
      scope.put('transactions', { ...t, link: { ...t.link, occurrence: m.occurrence, occurrenceBasis: m.basis } });
      wrote = true;
    }
    // Закрепление — по тем же свежим данным, с уже решёнными наступлениями.
    const settled = input.incomes.map((f) => {
      const m = marks.get(f.txId);
      return m ? { ...f, occurrence: m.occurrence, occurrenceBasis: m.basis } : f;
    });
    const pin = nextPin(pins, { ...input, incomes: settled });
    if (pin) {
      // Время записи: приход нового источника, внесённый после этого задним
      // числом, — тоже его приход (`reportPeriods`).
      scope.put('meta', { key: 'periodPins', value: [...pins, { ...pin, at: stamp() }] });
      wrote = true;
    }
    return wrote;
  },
));

/**
 * Человек уточнил, какую зарплату закрывает приход.
 *
 * Досрочная ноябрьская зарплата без связи с ожиданием сначала считается
 * доплатой к октябрю — это осторожная оценка. Уточнение её заменяет и уже не
 * пересматривается ни правкой даты, ни переносом зарплатного дня.
 */
export const confirmIncomeOccurrence = (txId: string, occurrence: PlainDate): Promise<boolean> => transact(
  ['transactions', 'incomeStreams', 'expectedIncomes'],
  async (scope) => {
    const txs = await scope.all<Tx>('transactions');
    const t = txs.find((x) => x.id === txId);
    if (!t || t.link?.kind !== 'income' || !t.link.incomeStreamId) return false;
    const moved: Tx = { ...t, link: { ...t.link, occurrence, occurrenceBasis: 'confirmed' } };
    scope.put('transactions', moved);
    const rests = await restsOf(scope, t.link.incomeStreamId);
    if (!rests) return true;

    /*
      Ноль у перемещаемой части мог быть не ответом: она закрыла остаток
      прежней зарплаты, и вопроса не было. Тогда ответа у неё нет вовсе.
    */
    const old = rests.occurrenceOf(t);
    const oldParts = old ? rests.partsOf(txs, old) : [];
    const oldIdx = oldParts.findIndex((f) => f.txId === txId);
    const oldPending = oldIdx >= 0 ? rests.pendingBefore(oldParts.slice(0, oldIdx)) : null;
    const automatic = t.link.expectedRest === ZERO && oldPending !== null && t.amount >= oldPending;

    // И у новой зарплаты, и у прежней: от прежней деньги ушли, и её поздние
    // части остаток считали вместе с ними.
    const all = txs.map((x) => (x.id === txId ? moved : x));
    rests.replay(all, [occurrence, ...(old && old !== occurrence ? [old] : [])], automatic ? txId : undefined);
    return true;
  },
);

/**
 * Остатки «Жду» частей одной зарплаты — заново, когда изменилось, какие
 * части в неё входят: приход отнесли к другой зарплате или удалили.
 *
 * Ответы «Жду» / «Это всё» остаются, суммы считаются так, как их посчитала
 * бы форма (`pendingRest`), знай она, что это одна зарплата. Первая часть
 * 1 октября считалась доплатой к сентябрю, вторую 3 октября записали с
 * «Жду» — остаток посчитан от всей зарплаты: 130 000 вместо 30 000. Части
 * проходят в порядке записи: каждая видит записанные до неё, в любом
 * порядке дат. Без прежней части остаток снова считается от суммы дохода —
 * как записала бы форма.
 */
async function restsOf(scope: TxScope, streamId: string) {
  const stream = (await scope.all<IncomeStream>('incomeStreams')).find((s) => s.id === streamId);
  if (!stream) return null;
  const live = { ...stream, isActive: true };
  const expectations = await scope.all<IncomeExpectation>('expectedIncomes');
  const when = (f: PeriodIncome) => f.recordedAt ?? f.date;
  const partsOf = (list: Tx[], occ: PlainDate) => periodIncomes(list, expectations)
    .filter((f) => f.kind === 'regular' && f.streamId === streamId && f.amount > ZERO
      && occurrenceOf(live, f)?.occurrence === occ)
    .sort((a, b) => when(a).localeCompare(when(b)) || a.txId.localeCompare(b.txId));
  /** Сколько ещё ждали к этой части — как у формы: по записанным раньше. */
  const pendingBefore = (before: PeriodIncome[]): Money | null => {
    const last = [...before].reverse().find((f) => f.expectedRest !== undefined);
    if (!last) return null;
    const upTo = add(...before.filter((f) => when(f) <= when(last)).map((f) => f.amount));
    const left = sub(add(upTo, last.expectedRest!), add(...before.map((f) => f.amount)));
    return left > ZERO ? left : null;
  };
  const occurrenceOfTx = (t: Tx): PlainDate | undefined => {
    if (t.link?.kind !== 'income') return undefined;
    const f = periodIncomes([t], expectations)[0];
    return t.link.occurrence ?? (f ? occurrenceOf(live, f)?.occurrence : undefined);
  };
  /** `unanswered` — часть, чей ноль ответом не был. */
  const replay = (all: Tx[], occurrences: PlainDate[], unanswered?: string): void => {
    for (const occ of occurrences) {
      const parts = partsOf(all, occ);
      parts.forEach((p, i) => {
        if (p.expectedRest === undefined) return;
        const wait: boolean | null = p.txId === unanswered ? null : p.expectedRest > ZERO;
        const pending = pendingBefore(parts.slice(0, i));
        let rest: Money | undefined;
        if (pending !== null) rest = p.amount < pending && wait !== false ? sub(pending, p.amount) : ZERO;
        else if (p.amount < stream.amount) rest = wait === null ? undefined : wait ? sub(stream.amount, p.amount) : ZERO;
        else return;
        if (rest === p.expectedRest) return;
        parts[i] = rest === undefined ? (({ expectedRest: _, ...f }) => f)(p) : { ...p, expectedRest: rest };
        const x = all.find((y) => y.id === p.txId);
        if (x?.link?.kind !== 'income') return;
        const { expectedRest: _, ...link } = x.link;
        scope.put('transactions', { ...x, link: rest === undefined ? link : { ...link, expectedRest: rest } });
      });
    }
  };
  return { partsOf, pendingBefore, occurrenceOf: occurrenceOfTx, replay };
}

/**
 * «Попробую» — договорённость по итогам периода.
 *
 * Предел — две действующих — проверяется здесь, в одной записи, а не в
 * кнопке: нажатия с двух устройств не должны дать третью.
 */
export const addPeriodDeal = (deal: PeriodDeal): Promise<boolean> => queued('periodDeals', async () => {
  let added = false;
  await mutate<{ key: string; value: PeriodDeal[] | null }>('meta', 'periodDeals', (row) => {
    const list = row?.value ?? [];
    if (list.some((d) => !d.droppedAt && d.categoryId === deal.categoryId) || dealsLeft(list) === 0) return undefined;
    added = true;
    return { key: 'periodDeals', value: [...list, deal] };
  });
  return added;
});

/** Снять договорённость: «Не про меня» или три периода подряд уложились. */
export const dropPeriodDeal = (categoryId: string): Promise<void> => queued('periodDeals', async () => {
  await mutate<{ key: string; value: PeriodDeal[] | null }>('meta', 'periodDeals', (row) => {
    const list = row?.value ?? [];
    if (!list.some((d) => !d.droppedAt && d.categoryId === categoryId)) return undefined;
    const at = new Date().toISOString();
    return { key: 'periodDeals', value: list.map((d) => (!d.droppedAt && d.categoryId === categoryId ? { ...d, droppedAt: at } : d)) };
  });
});

/** «Это моё решение» — тему больше не поднимаем. */
export const hidePeriodTopic = (categoryId: string, reason: PeriodTopicOff['reason']): Promise<void> =>
  queued('periodTopicsOff', async () => {
    await mutate<{ key: string; value: PeriodTopicOff[] | null }>('meta', 'periodTopicsOff', (row) => {
      const list = row?.value ?? [];
      if (list.some((t) => t.categoryId === categoryId)) return undefined;
      return { key: 'periodTopicsOff', value: [...list, { categoryId, reason, at: new Date().toISOString() }] };
    });
  });

/** Вернуть тему в разбор («Ещё → Разбор периода»). */
export const showPeriodTopic = (categoryId: string): Promise<void> => queued('periodTopicsOff', async () => {
  await mutate<{ key: string; value: PeriodTopicOff[] | null }>('meta', 'periodTopicsOff', (row) => {
    const list = row?.value ?? [];
    return list.some((t) => t.categoryId === categoryId)
      ? { key: 'periodTopicsOff', value: list.filter((t) => t.categoryId !== categoryId) }
      : undefined;
  });
});

/** «Не сейчас» — разбор периода свёрнут в строку. */
export const laterPeriodReview = (periodKey: string, later: boolean): Promise<void> => queued('periodReviewLater', async () => {
  await mutate<{ key: string; value: string[] | null }>('meta', 'periodReviewLater', (row) => {
    const list = row?.value ?? [];
    const has = list.includes(periodKey);
    if (has === later) return undefined;
    return { key: 'periodReviewLater', value: later ? [...list, periodKey] : list.filter((k) => k !== periodKey) };
  });
});

/** Человек закрыл подсказку «Понятно» — больше её не показываем. */
export const dismissHint = (key: string): Promise<void> => queued('dismissedHints', async () => {
  await mutate<{ key: string; value: string[] | null }>('meta', 'dismissedHints', (row) => {
    const list = row?.value ?? [];
    return list.includes(key) ? undefined : { key: 'dismissedHints', value: [...list, key] };
  });
});

/**
 * Сохранить основу сравнения периода, если её ещё нет.
 *
 * Ключ периода — ключ основы, и проверка «уже есть» идёт внутри той же
 * транзакции, что и запись: два одновременных вызова дают одну основу, а
 * не две с разными суммами.
 */
export const saveBaselineOnce = (baseline: PeriodBaseline): Promise<boolean> =>
  queued('periodBaselines', async () => {
    let wrote = false;
    await mutate<{ key: string; value: PeriodBaseline[] | null }>('meta', 'periodBaselines', (row) => {
      const list = row?.value ?? [];
      if (list.some((b) => b.key === baseline.key)) return undefined;
      wrote = true;
      return { key: 'periodBaselines', value: [...list, baseline] };
    });
    return wrote;
  });


/**
 * Назначить главную задачу вручную или вернуть выбор расчёту.
 *
 * Пустое значение — не «нет задачи», а «считай сам»: задача всё равно будет
 * названа, просто приложением.
 */
/**
 * Записать согласие или его отзыв.
 *
 * Всегда новая запись: согласие — событие, и история подтверждений человека
 * должна читаться целиком. Стирать прошлое значило бы потерять доказательство
 * того, на что он соглашался раньше.
 */
export async function recordConsent(input: {
  kind: ConsentKind;
  documentVersion: string;
  method: ConsentMethod;
  granted: boolean;
}): Promise<Consent> {
  const consent: Consent = { ...input, id: uid(), acceptedAt: new Date().toISOString() };
  await put('consents', consent);
  return consent;
}

/**
 * Отметить наблюдение показанным.
 *
 * Показ и отклонение — разные события: первое нужно, чтобы не считать поводом
 * то, что человек уже видел; второе — чтобы не возвращать отложенное. Подпись
 * записывается вместе с ними: она и решает, когда повод станет новым.
 */
export async function markInsightShown(c: InsightCandidate): Promise<void> {
  await mutate<InsightDisplayState>('insightLog', c.key, (prev) => (
    prev && prev.signature === c.signature
      ? prev
      : { key: c.key, shownAt: new Date().toISOString(), signature: c.signature }
  ));
}

/** Отложить наблюдение: вернётся, только когда изменится его суть. */
export async function dismissInsight(c: InsightCandidate): Promise<void> {
  await put('insightLog', {
    key: c.key,
    shownAt: new Date().toISOString(),
    dismissedAt: new Date().toISOString(),
    signature: c.signature,
  } satisfies InsightDisplayState);
}

/** Убрать из журнала записи, у которых больше нет повода. */
export async function forgetInsights(keys: readonly string[]): Promise<void> {
  for (const key of keys) await remove('insightLog', key);
}

/**
 * Разговор со SKOKA: сохранение и чтение.
 *
 * Разговор жил только в памяти вкладки: обновил страницу — и разбора,
 * за который заплачено единицей пакета, больше нет. Здесь он переживает
 * перезагрузку и закрытие приложения.
 *
 * Хвост ограничен: это память разговора, а не архив. Старое уходит первым.
 */
const TALK_LIMIT = 60;

export async function saveTalk(talk: unknown): Promise<void> {
  const value = talk as { turns?: unknown[] };
  const turns = Array.isArray(value.turns) ? value.turns.slice(-TALK_LIMIT) : [];
  await put('advisorTalk', { key: 'current', ...value, turns, savedAt: new Date().toISOString() });
}

export async function loadTalk<T>(): Promise<T | null> {
  const rows = await getAll<{ key: string }>('advisorTalk');
  const row = rows.find((r) => r.key === 'current');
  return (row as T | undefined) ?? null;
}

/** Забыть разговор: человек попросил начать заново. */
export const clearTalk = (): Promise<void> => remove('advisorTalk', 'current');

/** Все записи согласий — включая отозванные. */
export const listConsents = (): Promise<Consent[]> => getAll<Consent>('consents');

/** Имя для подписи профиля. Пустая строка означает «не называл». */
export const setProfileName = (name: string | null): Promise<void> =>
  setMeta('profileName', name && name.trim() ? name.trim() : null);

export async function setFocusOverride(kind: FocusKind | null): Promise<void> {
  await setMeta('focusOverride', kind ?? undefined);
}

// ──────────────────────────────────────────────────────────── операции

/**
 * Запись расхода.
 *
 * Баланс счёта меняется здесь же: приложение local-first, и «Доступно»
 * должно быть верным сразу после ввода, без похода на сервер и без
 * пересчёта из истории при каждом открытии.
 */
export async function addExpense(input: {
  accountId: string;
  amount: Money;
  categoryId: string;
  part: PlanPart;
  date: PlainDate;
  note?: string;
  items?: TxItem[];
  source?: Tx['source'];
  /** false — баланс счёта уже учитывает эту трату, менять его не нужно. */
  applyToBalance?: boolean;
  receiptId?: string;
  link?: TxLink;
  time?: string;
  /** Ключ действия: отличает повтор доставки от второй настоящей записи. */
  requestId?: string;
}): Promise<Tx> {
  const tx = makeTx({
    ...(await allocationTag()),
    type: 'expense',
    accountId: input.accountId,
    amount: input.amount,
    date: input.date,
    categoryId: input.categoryId,
    part: input.part,
    ...(input.note ? { note: input.note } : {}),
    ...(input.items && input.items.length ? { items: input.items } : {}),
    ...(input.source ? { source: input.source } : {}),
    ...(input.applyToBalance === false ? { applyToBalance: false } : {}),
    ...(input.receiptId ? { receiptId: input.receiptId } : {}),
    ...(input.link ? { link: input.link } : {}),
    ...(input.time ? { time: input.time } : {}),
    ...(input.requestId ? { requestId: input.requestId } : {}),
  });

  const accounts = await getAll<Account>('accounts');
  const acc = accounts.find((a) => a.id === input.accountId);

  // Трата раньше даты названного остатка в нём уже учтена: человек назвал
  // остаток вечером, а записал вчерашний кофе. Списать её второй раз значило
  // бы испортить ровно ту цифру, ради которой продукт существует.
  //
  // Это догадка, и она уступает прямому указанию. `applyToBalance: true`
  // говорит «эта трата в остатке не учтена» — так бывает при импорте выписки
  // и при записи операции по чужому счёту. Догадываться поверх сказанного
  // значило бы терять настоящие траты.
  const alreadyInBalance = input.applyToBalance === undefined
    && acc?.balanceAsOf !== undefined
    && input.date < acc.balanceAsOf;
  const applies = input.applyToBalance !== false && !alreadyInBalance;
  if (alreadyInBalance) tx.balanceApplied = false;
  if (acc && applies) await tagPinned(tx, [input.accountId]);

  await putAll('transactions', [tx]);
  // Остаток меняется дельтой в одной транзакции: две оплаты с одного счёта,
  // сделанные одновременно, иначе обе читают прежний остаток, и одна запись
  // перекрывает другую — со счёта уходит половина того, что ушло на деле.
  // Позже есть корректировка — меняется она, а не остаток (`shiftAt`).
  if (acc && applies) await shiftAt(input.accountId, tx.occurredAt, neg(input.amount), tx.id);
  return tx;
}
/** Операция, которую объясняет корректировка, — в версии раскладки этой корректировки. */
async function tagPinned(tx: Tx, accountIds: readonly string[]): Promise<void> {
  const pin = await pinnedAllocation(accountIds, tx.occurredAt);
  if (!pin.pinned) return;
  if (pin.allocationId) tx.allocationId = pin.allocationId;
  else delete tx.allocationId;
}


/**
 * Поступление: зарплата, продажа, подарок, возврат.
 *
 * Увеличивает остаток счёта. Расходом не является ни в каком виде — знак
 * задаёт тип операции, а не сумма (сумма всегда положительная).
 */
/**
 * Записать приход и закрепить деньги под названные платежи.
 *
 * Одно действие человека — одна запись прихода и столько конвертов, сколько он
 * оставил отмеченными. Разбивать это на два нажатия значит открыть окно, в
 * котором деньги пришли, а обещания под них не закреплены.
 *
 * Оплатой это не становится: конверт означает «деньги готовы», а не «платёж
 * выполнен». Подтверждает оплату человек, когда деньги правда уйдут.
 */
export async function applyDistribution(input: {
  accountId: string;
  amount: Money;
  date?: PlainDate;
  /** Имя дохода для истории: «Зарплата», а не безликое «Приход». */
  source?: string;
  /** Какой ожидаемый доход пришёл. Без связи прогноз не закрывается. */
  incomeStreamId?: string;
  /** Сколько человек ещё ждёт по этому приходу. */
  expectedRest?: Money;
  /** Конверты под платежи: сколько закрепить дополнительно на этом счёте. */
  reserves: readonly { obligationId: string; amount: Money }[];
  /** Принятые взносы в цели: ровно те суммы, что человек видел на экране. */
  goals?: readonly { goalId: string; amount: Money }[];
  /** Ключ действия: один на одно принятие раскладки. */
  requestId?: string;
}): Promise<Tx> {
  const { tx, existed } = await recordIncome({
    accountId: input.accountId,
    amount: input.amount,
    date: input.date ?? todayPd(),
    source: input.source ?? 'Приход',
    ...(input.incomeStreamId ? { incomeStreamId: input.incomeStreamId } : {}),
    ...(input.expectedRest !== undefined ? { expectedRest: input.expectedRest } : {}),
    ...(input.requestId ? { requestId: input.requestId } : {}),
  });

  // Повтор всего действия, а не только записи прихода.
  //
  // Раньше идемпотентен был первый шаг: приход не удваивался, а конверты
  // закреплялись второй раз — 50 000 ₽ превращались в 100 000 ₽. Принятая
  // раскладка применяется целиком либо не применяется вовсе.
  if (existed) return tx;

  for (const r of input.reserves) {
    if (r.amount <= ZERO) continue;
    // Увеличиваем конверт **этого** счёта, а не сумму конвертов всех счетов.
    // Прежний расчёт складывал чужие конверты и записывал итог сюда: 20 000 ₽
    // на другом счёте превращались в лишние 20 000 ₽ на этом.
    const here = (await getAll<Reserve>('reserves')).find(
      (x) => x.forKind === 'obligation' && x.forId === r.obligationId
        && x.accountId === input.accountId,
    );
    await setReserve({
      accountId: input.accountId,
      forKind: 'obligation',
      forId: r.obligationId,
      amount: add(here?.amount ?? ZERO, r.amount),
    });
  }

  // Принятые взносы в цели — часть той же раскладки.
  //
  // Экран показывал их суммой, а сохранялся только приход: цель оставалась с
  // нулём, журнал пустым, и деньги, «розданные» на экране, назавтра снова
  // числились свободными.
  for (const g of input.goals ?? []) {
    if (g.amount <= ZERO) continue;
    await fundGoal({
      goalId: g.goalId,
      requestId: `income:${tx.id}:${g.goalId}`,
      amount: g.amount,
      cycleStart: input.date ?? todayPd(),
      fromAccountId: input.accountId,
    });
  }

  return tx;
}

export async function addIncomeTx(input: {
  accountId: string;
  amount: Money;
  date: PlainDate;
  source: string;
  note?: string;
  time?: string;
  /** Ключ действия: отличает повтор доставки от второго настоящего прихода. */
  requestId?: string;
  /** Какой ожидаемый доход это закрывает. Подарок не закрывает ничей. */
  incomeStreamId?: string;
  /** Сколько человек ещё ждёт по этому приходу. */
  expectedRest?: Money;
  /** Какое ожидание закрывает этот приход. */
  expectationId?: string;
  /** Что это за деньги: ожидаемый доход, разовые, возврат. */
  incomeKind?: 'regular' | 'oneoff' | 'refund';
  /** Возврат: категория и часть плана исходной покупки. */
  categoryId?: string;
  part?: PlanPart;
  /** Учтены ли деньги в названном остатке счёта. */
  applyToBalance?: boolean;
}): Promise<Tx> {
  return (await recordIncome(input)).tx;
}

/**
 * Запись прихода с ответом о повторе.
 *
 * `existed: true` означает, что это действие уже выполнялось: повтор доставки,
 * второе нажатие, возврат после ошибки. Знать это нужно вызывающему — у
 * распределения на повторе нельзя закреплять конверты второй раз.
 */
async function recordIncome(input: {
  accountId: string;
  amount: Money;
  date: PlainDate;
  source: string;
  note?: string;
  time?: string;
  requestId?: string;
  /**
   * Какой ожидаемый доход это закрывает.
   *
   * Связь, а не пометка: без неё пришедшая зарплата и та же зарплата в
   * прогнозе — два разных обещания, и приложение считает деньги дважды.
   * Подарок и продажа приходят без связи и ничей прогноз не закрывают.
   */
  incomeStreamId?: string;
  /** Сколько человек ещё ждёт по этому приходу. Ноль — больше не ждёт. */
  expectedRest?: Money;
  /** Какое ожидание закрывает этот приход. */
  expectationId?: string;
  /** Что это за деньги: ожидаемый доход, разовые, возврат. */
  incomeKind?: 'regular' | 'oneoff' | 'refund';
  /**
   * Куда вернуть деньги, если это возврат: та же категория и та же часть
   * плана, из которых ушла покупка. Отчёт уменьшит именно их.
   */
  categoryId?: string;
  part?: PlanPart;
  /**
   * Учтены ли эти деньги в названном остатке счёта.
   *
   * `false` говорит «остаток я называл уже с ними»: прибавить их второй раз
   * значило бы завысить капитал ровно на зарплату.
   */
  applyToBalance?: boolean;
}): Promise<{ tx: Tx; existed: boolean }> {
  // Повтор одного действия деньги второй раз не добавляет: приход, записанный
  // дважды, завышает остаток ровно так же, как трата, списанная дважды, его
  // занижает.
  if (input.requestId) {
    const txs = await getAll<Tx>('transactions');
    const same = txs.find((t) => t.requestId === input.requestId);
    if (same) return { tx: same, existed: true };
  }
  const tx = makeTx({
    ...(await allocationTag()),
    ...(input.time ? { time: input.time } : {}),
    type: 'income',
    accountId: input.accountId,
    amount: input.amount,
    date: input.date,
    note: input.note ?? input.source,
    ...(input.categoryId ? { categoryId: input.categoryId } : {}),
    ...(input.part ? { part: input.part } : {}),
    link: {
      kind: 'income',
      ...(input.incomeStreamId ? { incomeStreamId: input.incomeStreamId } : {}),
      ...(input.expectedRest !== undefined ? { expectedRest: input.expectedRest } : {}),
      ...(input.expectationId ? { expectationId: input.expectationId } : {}),
      // Вид поступления называется всегда: по умолчанию «разовые деньги».
      // Молчаливое «регулярный» учило бы ритм подаркам и продажам.
      incomeKind: input.incomeKind ?? (input.incomeStreamId ? 'regular' : 'oneoff'),
    },
    ...(input.requestId ? { requestId: input.requestId } : {}),
  });

  /*
    Какую зарплату закрывает приход — решается сразу, в той же записи.

    По свежему расписанию и ожиданию, а не позже по снимку экрана: иначе
    правка даты между записью и разметкой закрепила бы наступление, рассчитанное
    по прежней дате.
  */
  if (tx.link?.kind === 'income' && tx.link.incomeKind === 'regular' && tx.link.incomeStreamId) {
    const occ = await occurrenceFor(tx);
    if (occ) tx.link = { ...tx.link, occurrence: occ.occurrence, occurrenceBasis: occ.basis };
  }

  // Приход раньше даты названного остатка в нём уже сидит: человек посмотрел
  // баланс вечером, а зарплату записывает утром следующего дня. Прибавить её
  // второй раз — та же ошибка, что списать трату дважды, только в плюс.
  //
  // Догадка уступает прямому указанию: `applyToBalance: true` говорит «этих
  // денег в остатке нет», и тогда они прибавляются.
  const accounts = await getAll<Account>('accounts');
  const acc = accounts.find((a) => a.id === input.accountId);
  const alreadyInBalance = input.applyToBalance === undefined
    && acc?.balanceAsOf !== undefined
    && input.date < acc.balanceAsOf;
  const applies = input.applyToBalance !== false && !alreadyInBalance;
  if (!applies) tx.balanceApplied = false;
  if (applies) await tagPinned(tx, [input.accountId]);

  await putAll('transactions', [tx]);
  if (applies) await shiftAt(input.accountId, tx.occurredAt, input.amount, tx.id);
  return { tx, existed: false };
}

/**
 * Перевод между своими счетами.
 *
 * Инвариант И2: капитал домохозяйства при этом не меняется, поэтому расходом
 * это не является ни в одну сторону. Погашение кредитки — тоже перевод, а не
 * новая трата: расход был в момент покупки (И3).
 */
export async function addTransfer(input: {
  fromId: string;
  toId: string;
  amount: Money;
  date: PlainDate;
  note?: string;
  link?: TxLink;
  time?: string;
}): Promise<Tx> {
  if (input.fromId === input.toId) throw new Error('Перевод на тот же счёт невозможен');

  const tx = makeTx({
    ...(await allocationTag()),
    ...(input.time ? { time: input.time } : {}),
    type: 'transfer',
    accountId: input.fromId,
    counterAccountId: input.toId,
    amount: input.amount,
    date: input.date,
    ...(input.note ? { note: input.note } : {}),
    ...(input.link ? { link: input.link } : {}),
  });

  const accounts = await getAll<Account>('accounts');
  const from = accounts.find((a) => a.id === input.fromId);
  const to = accounts.find((a) => a.id === input.toId);
  if (!from || !to) throw new Error('Счёт не найден');

  await tagPinned(tx, [from.id, to.id]);
  await putAll('transactions', [tx]);
  await shiftAt(from.id, tx.occurredAt, neg(input.amount), tx.id);
  // У кредитки баланс отрицательный: платёж делает его ближе к нулю.
  await shiftAt(to.id, tx.occurredAt, input.amount, tx.id);

  if (to.kind === 'credit_card') {
    const used = await applyCardPayment(to.id, input.fromId, input.amount);
    const paid = { ...tx, reserveUsed: used };
    await put('transactions', paid);
    return paid;
  }
  return tx;
}

/**
 * Платёж по кредитке приводит в порядок две связанные величины сразу.
 *
 * Внесённое после выписки растёт — иначе пользователю пришлось бы править это
 * поле руками после каждого платежа, а забытая правка означает завышенный
 * недобор и заниженную главную цифру.
 *
 * Конверт на счёте-источнике, наоборот, уменьшается: деньги ушли, закреплять
 * их больше нечем. Без этого возник бы новый двойной счёт — уже внесённое
 * и всё ещё закреплённое одновременно.
 */
async function applyCardPayment(cardId: string, fromAccountId: string, amount: Money): Promise<Money> {
  const terms = (await getAll<CreditTerms>('creditTerms')).find((t) => t.accountId === cardId);
  // Срок, который платёж закрывает, — до правки внесённого (04.11.2026).
  const card = (await getAll<Account>('accounts')).find((x) => x.id === cardId);
  const due = card && terms ? creditState(card, terms, todayPd()).paymentDate : null;
  if (terms) {
    const capped = terms.statementDebt > ZERO
      ? minM(add(terms.reserved, amount), terms.statementDebt)
      : add(terms.reserved, amount);
    await put('creditTerms', { ...terms, reserved: capped });
  }

  /*
    Конверты этого счёта под эту карту — сначала без даты, потом по сроку. Отложенное
    к более позднему платежу (`forDate` позже закрываемого срока) не трогается:
    иначе «Отложить на декабрь» тихо уходило в ноябрьский платёж (04.11.2026).
  */
  const mine = (await getAll<Reserve>('reserves'))
    .filter((r) => r.accountId === fromAccountId && r.forKind === 'credit' && r.forId === cardId
      && (!due || cardEnvelopeNow(r, due)))
    .sort((a, b) => (a.forDate ?? '').localeCompare(b.forDate ?? ''));
  let rest = amount;
  let used = ZERO;
  for (const reserve of mine) {
    if (rest <= ZERO) break;
    const take = minM(reserve.amount, rest);
    const left = sub(reserve.amount, take);
    if (left <= ZERO) await remove('reserves', reserve.id);
    else await put('reserves', { ...reserve, amount: left });
    rest = sub(rest, take);
    used = add(used, take);
  }
  return used;
}

/**
 * Действующая версия раскладки — её ключ ставится каждой новой операции.
 *
 * Читается из хранилища прямо перед записью: операция обязана попасть в ту
 * версию, которая действует сейчас, а не в ту, что была при открытии экрана.
 */
async function activeAllocationId(): Promise<string | undefined> {
  const all = await getAll<AcceptedAllocation>('allocations');
  return activeAllocation(all)?.id;
}

/** Метка версии для новой операции. Пустая, пока раскладки нет. */
async function allocationTag(): Promise<{ allocationId?: string }> {
  const id = await activeAllocationId();
  return id ? { allocationId: id } : {};
}

function makeTx(input: {
  type: Tx['type'];
  accountId: string;
  counterAccountId?: string;
  amount: Money;
  date: PlainDate;
  categoryId?: string;
  part?: PlanPart;
  note?: string;
  items?: TxItem[];
  source?: Tx['source'];
  applyToBalance?: boolean;
  receiptId?: string;
  link?: TxLink;
  /** «ЧЧ:ММ», если человек указал время сам. */
  time?: string;
  /** Ключ действия: отличает повтор доставки от второй настоящей записи. */
  requestId?: string;
  /** Версия раскладки, действующая в момент записи. */
  allocationId?: string;
  /** Перевод запаса стабильного бюджета. */
  buffer?: true;
}): Tx {
  const now = new Date();
  // Сегодня — текущее время, прошлый день — полдень: по памяти час не помнят,
  // а полдень не сдвигает операцию через границу суток.
  // Названное человеком время побеждает умолчание: он мог вспомнить, что
  // платил утром, и в истории это должно быть видно.
  const time = /^\d{2}:\d{2}$/.test(input.time ?? '')
    ? `${input.date}T${input.time}:00.000Z`
    : input.date === todayPd() ? localMoment(now) : `${input.date}T12:00:00.000Z`;
  return {
    id: uid(),
    type: input.type,
    accountId: input.accountId,
    ...(input.counterAccountId ? { counterAccountId: input.counterAccountId } : {}),
    amount: input.amount,
    occurredAt: time,
    occurredDate: input.date,
    ...(input.categoryId ? { categoryId: input.categoryId } : {}),
    ...(input.part ? { part: input.part } : {}),
    ...(input.note ? { note: input.note } : {}),
    ...(input.items && input.items.length ? { items: input.items } : {}),
    ...(input.applyToBalance === false ? { balanceApplied: false } : {}),
    ...(input.receiptId ? { receiptId: input.receiptId } : {}),
    ...(input.link ? { link: input.link } : {}),
    ...(input.requestId ? { requestId: input.requestId } : {}),
    ...(input.allocationId ? { allocationId: input.allocationId } : {}),
    ...(input.buffer ? { buffer: true as const } : {}),
    // Источник записан всегда (И8): по нему видно, откуда взялась цифра, и
    // разбор расхождений начинается именно с этого вопроса.
    source: input.source ?? 'manual',
    createdAt: stamp(),
  };
}

/**
 * Удалённая операция — всё, что нужно, чтобы вернуть её кнопкой «Вернуть».
 *
 * Снимок чека сохраняется здесь же: удаление последней операции чека уносит
 * и фотографию, а вернуть операцию без неё значило бы потерять чек.
 */
export interface DeletedTx {
  tx: Tx;
  photo?: ReceiptPhoto;
  /** Запись журнала цели об этом взносе — вернётся вместе с ним. */
  goalEvents?: GoalEvent[];
}

export async function deleteTransaction(id: string): Promise<DeletedTx | null> {
  const txs = await getAll<Tx>('transactions');
  const tx = txs.find((t) => t.id === id);
  if (!tx) return null;
  const photo = tx.receiptId ? await getReceiptPhoto(tx.receiptId) : null;
  // Откат тот же, что при правке: две копии этой логики разошлись бы при
  // первом же изменении, а расхождение баланса с историей сверка спишет
  // в «Прочее» как настоящую трату.
  await reverseOnAccounts(tx);
  await remove('transactions', id);
  await replayIncomeRests(tx);
  // Факт оплаты — проекция операций: исчезла операция, исчезла и оплата.
  // Иначе платёж остаётся «исполненным» без единого рубля за ним.
  if (tx.link?.kind === 'obligation') await syncObligationFacts(tx.link.obligationId);
  // Снимок держится, пока на него ссылается хоть одна операция: у смешанного
  // чека их две, и удаление одной не должно уносить общую фотографию.
  await dropOrphanPhoto(tx.receiptId);
  const goalEvents = await dropGoalContribution(tx);
  return { tx, ...(photo ? { photo } : {}), ...(goalEvents.length ? { goalEvents } : {}) };
}

/**
 * Взнос в цель удалён: накопленное по цели уменьшается, его запись в журнале
 * уходит. Прежде удалённый взнос оставался и в «накоплено» цели, и в
 * «Отложено» аналитики — деньги вернулись на счёт, а цель их всё ещё считала.
 */
async function dropGoalContribution(tx: Tx): Promise<GoalEvent[]> {
  if (tx.type !== 'transfer' || tx.link?.kind !== 'goal') return [];
  await shiftGoal(tx.link.goalId, neg(tx.amount));
  const events = (await getAll<GoalEvent>('goalAllocations')).filter((e) => e.txId === tx.id);
  for (const e of events) {
    if (e.reservedAccountId) await shiftGoalReserve(e.goalId, e.reservedAccountId, neg(e.amount));
    await remove('goalAllocations', e.key);
  }
  return events;
}

/** Только резерв этого взноса: остальные конверты на счёте сохраняются. */
async function shiftGoalReserve(goalId: string, accountId: string, delta: Money): Promise<void> {
  const existing = (await getAll<Reserve>('reserves')).find(
    (r) => r.forKind === 'goal' && r.forId === goalId && r.accountId === accountId,
  );
  await setReserve({ accountId, forKind: 'goal', forId: goalId,
    amount: add(existing?.amount ?? ZERO, delta) });
}

/** Накопленное по цели — на дельту, не ниже нуля. */
const shiftGoal = async (goalId: string, delta: Money): Promise<void> => {
  await mutate<Goal>('goals', goalId, (g) => {
    if (!g) return undefined;
    const next = add(g.currentAmount, delta);
    return { ...g, currentAmount: next < ZERO ? ZERO : next };
  });
};

/**
 * Вернуть удалённую операцию — ту же самую, с прежним идентификатором.
 *
 * Шаги удаления в обратном порядке: снимок, запись, остаток счёта, остатки
 * частей зарплаты, оплата платежа. Операция уже на месте (второе нажатие,
 * вернули с другого устройства) — ничего не делаем: деньги второй раз не
 * прибавляются.
 */
export async function restoreTransaction(deleted: DeletedTx): Promise<void> {
  const { tx } = deleted;
  const txs = await getAll<Tx>('transactions');
  if (txs.some((t) => t.id === tx.id)) return;
  if (deleted.photo) await put('receiptPhotos', deleted.photo);
  await put('transactions', tx);
  await applyOnAccounts(tx);
  await replayIncomeRests(tx);
  if (tx.link?.kind === 'obligation') await syncObligationFacts(tx.link.obligationId);
  if (tx.type === 'transfer' && tx.link?.kind === 'goal') {
    await shiftGoal(tx.link.goalId, tx.amount);
    for (const e of deleted.goalEvents ?? []) {
      if (e.reservedAccountId) await shiftGoalReserve(e.goalId, e.reservedAccountId, e.amount);
      await put('goalAllocations', e);
    }
  }
}

/** Часть зарплаты удалена или вернулась: остальные части считали остаток вместе с ней. */
async function replayIncomeRests(tx: Tx): Promise<void> {
  if (tx.link?.kind !== 'income' || !tx.link.incomeStreamId) return;
  const streamId = tx.link.incomeStreamId;
  await transact(['transactions', 'incomeStreams', 'expectedIncomes'], async (scope) => {
    const rests = await restsOf(scope, streamId);
    const occ = rests?.occurrenceOf(tx);
    if (rests && occ) rests.replay(await scope.all<Tx>('transactions'), [occ]);
  });
}

/**
 * Правка сохранённой операции.
 *
 * Сделана как откат и повторное применение, а не как точечное изменение полей.
 * Правка может поменять сумму, счёт и даже тип, и вычислять разницу по каждому
 * случаю значило бы завести второй набор правил рядом с существующими — они
 * разошлись бы при первом же изменении. Откат уже описан в удалении и проверен
 * на всех типах, включая погашение кредитки.
 *
 * Идентификатор и время создания сохраняются: это та же операция, а не новая.
 */
export async function updateTransaction(id: string, patch: {
  amount?: Money;
  accountId?: string;
  counterAccountId?: string;
  categoryId?: string;
  part?: PlanPart;
  date?: PlainDate;
  time?: string;
  note?: string;
  /**
   * Поступление — возврат за покупку (или уже нет).
   *
   * Только у прихода, не закрывающего ожидаемый доход: зарплата возвратом не
   * становится. Возврат несёт расходную категорию и часть плана — их и
   * уменьшает; обычному приходу часть плана не нужна.
   */
  refund?: boolean;
}): Promise<void> {
  const txs = await getAll<Tx>('transactions');
  const old = txs.find((t) => t.id === id);
  if (!old) throw new Error('Операция не найдена');

  await reverseOnAccounts(old);

  const date = patch.date ?? old.occurredDate;
  const next: Tx = {
    ...old,
    ...(patch.amount !== undefined ? { amount: patch.amount } : {}),
    ...(patch.accountId ? { accountId: patch.accountId } : {}),
    ...(patch.counterAccountId ? { counterAccountId: patch.counterAccountId } : {}),
    ...(patch.categoryId ? { categoryId: patch.categoryId } : {}),
    ...(patch.part ? { part: patch.part } : {}),
    occurredDate: date,
    occurredAt: /^\d{2}:\d{2}$/.test(patch.time ?? '')
      ? `${date}T${patch.time}:00.000Z`
      : patch.date ? `${date}T12:00:00.000Z` : old.occurredAt,
    ...(patch.note !== undefined ? { note: patch.note } : {}),
  };
  if (patch.refund !== undefined && next.type === 'income' && next.link?.kind === 'income' && !next.link.incomeStreamId) {
    const was = next.link.incomeKind === 'refund';
    if (patch.refund && !was) next.link = { ...next.link, incomeKind: 'refund' };
    if (!patch.refund && was) {
      next.link = { ...next.link, incomeKind: 'oneoff' };
      delete next.part;
    }
  }
  /*
    Исправленная дата прихода меняет и то, какую зарплату он закрывает, —
    если это было выведено из даты.

    Только при **изменённой** дате: форма присылает дату всегда, и правка
    заметки после переноса зарплатного дня иначе перепривязала бы прошлую
    зарплату к новому расписанию. Явная связь с ожиданием или уточнение
    человека от даты не зависят. А если пересчитать не из чего (источник
    удалён), прежняя связь остаётся: другое назначение — только явным
    уточнением (`confirmIncomeOccurrence`).
  */
  if (patch.date && patch.date !== old.occurredDate
    && next.link?.kind === 'income' && next.link.occurrence
    && (next.link.occurrenceBasis ?? 'schedule') === 'schedule') {
    const { occurrence: _occurrence, occurrenceBasis: _basis, ...link } = next.link;
    const occ = await occurrenceFor({ ...next, link });
    if (occ) next.link = { ...link, occurrence: occ.occurrence, occurrenceBasis: occ.basis };
  }
  if (next.type === 'transfer' && next.counterAccountId === next.accountId) {
    // Откат уже сделан — возвращаем как было, иначе деньги исчезнут.
    await applyOnAccounts(old);
    throw new Error('Перевод на тот же счёт невозможен');
  }

  await put('transactions', next);
  await applyOnAccounts(next);
  // Сумма изменилась — изменился и остаток по платежу: 3 000 вместо 8 000
  // означают, что 5 000 ещё предстоит заплатить.
  if (next.link?.kind === 'obligation') await syncObligationFacts(next.link.obligationId);
  // Взнос в цель поправили — накопленное и журнал цели следуют за ним.
  if (next.type === 'transfer' && next.link?.kind === 'goal') {
    await shiftGoal(next.link.goalId, sub(next.amount, old.amount));
    for (const e of (await getAll<GoalEvent>('goalAllocations')).filter((x) => x.txId === id)) {
      if (e.reservedAccountId && next.counterAccountId) {
        await shiftGoalReserve(e.goalId, e.reservedAccountId, neg(e.amount));
        await shiftGoalReserve(e.goalId, next.counterAccountId, next.amount);
      }
      await put('goalAllocations', { ...e, amount: next.amount,
        ...(e.reservedAccountId ? { reservedAccountId: next.counterAccountId } : {}) });
    }
  }
}

/**
 * Изменить остаток счёта на дельту — атомарно.
 *
 * Читать баланс, считать новый и записывать порознь нельзя: между чтением и
 * записью вклинивается вторая операция, обе видят прежний остаток, и одна
 * запись перекрывает другую. Две оплаты по 3 000 ₽ списывали 3 000 ₽ вместо
 * 6 000 ₽ — деньги появлялись из воздуха.
 *
 * Чтение и запись идут в одной транзакции IndexedDB: она держит блокировку
 * хранилища, и это единственное, что защищает от второй вкладки.
 */
const shiftBalance = async (accountId: string, delta: Money): Promise<void> => {
  await mutate<Account>('accounts', accountId, (acc) => (
    acc ? { ...acc, balance: add(acc.balance, delta) } : undefined
  ));
};

/** Долг по карте после перевода на неё: тот же приём чтения-записи. */
const shiftCardDebt = async (accountId: string, delta: Money): Promise<void> => {
  const terms = (await getAll<CreditTerms>('creditTerms')).find((t) => t.accountId === accountId);
  if (!terms) return;
  await mutate<CreditTerms>('creditTerms', accountId, (current) => {
    if (!current) return undefined;
    const next = add(current.reserved, delta);
    return { ...current, reserved: next < ZERO ? ZERO : next };
  });
};

/**
 * Что операция сделала с остатком счёта: плюс — прибавила, минус — убавила.
 *
 * Одно место на все виды. Корректировка несёт сумму со знаком, у остальных
 * сумма положительная, а знак даёт вид операции.
 */
export function effectOn(tx: Pick<Tx, 'type' | 'amount' | 'accountId' | 'counterAccountId' | 'balanceApplied'>, accountId: string): Money {
  if (tx.balanceApplied === false) return ZERO;
  const own = tx.accountId === accountId;
  if (tx.type === 'expense') return own ? neg(tx.amount) : ZERO;
  if (tx.type === 'income' || tx.type === 'adjustment') return own ? tx.amount : ZERO;
  return add(own ? neg(tx.amount) : ZERO, tx.counterAccountId === accountId ? tx.amount : ZERO);
}

/**
 * Ближайшая корректировка счёта после момента `at`.
 *
 * Корректировка — это «в такой-то момент на счёте было столько». Пока она
 * есть, остаток после неё держится на названном числе, а всё, что записано
 * раньше неё, уже в нём учтено.
 */
async function pinAfter(accountId: string, at: string, except?: string): Promise<Tx | undefined> {
  return (await getAll<Tx>('transactions'))
    .filter((t) => t.type === 'adjustment' && t.accountId === accountId && t.id !== except && t.occurredAt > at)
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))[0];
}

/**
 * Сдвинуть остаток счёта операцией, случившейся в момент `at`.
 *
 * Если позже есть корректировка, остаток не меняется — меняется она: человек
 * сверил счёт и назвал число, а пропущенная тогда трата это число только
 * объясняет. Без этого трата из выписки за прошлую неделю списалась бы второй
 * раз — она уже сидит в сверенном остатке. Корректировка при этом честно
 * уменьшается: −1 250 ₽ «не знаю куда» становятся −800 ₽ после трат на 450 ₽.
 */
async function shiftAt(accountId: string, at: string, delta: Money, except?: string): Promise<void> {
  if (delta === ZERO) return;
  const pin = await pinAfter(accountId, at, except);
  if (pin) {
    await mutate<Tx>('transactions', pin.id, (t) => (t ? { ...t, amount: sub(t.amount, delta) } : undefined));
    return;
  }
  await shiftBalance(accountId, delta);
}

/**
 * Версия раскладки для операции задним числом, которую объясняет корректировка.
 *
 * Деньги ушли тогда же, когда их списала корректировка, — в её версии. Иначе
 * текущая раскладка уменьшила бы корзину ещё раз, хотя сверенный остаток, с
 * которого она считалась, эту трату уже не содержит.
 */
async function pinnedAllocation(accountIds: readonly string[], at: string): Promise<{ pinned: boolean; allocationId?: string }> {
  for (const id of accountIds) {
    const pin = await pinAfter(id, at);
    if (pin) return { pinned: true, ...(pin.allocationId ? { allocationId: pin.allocationId } : {}) };
  }
  return { pinned: false };
}

/** Снять действие операции со счетов. Обратное `applyOnAccounts`. */
async function reverseOnAccounts(tx: Tx): Promise<void> {
  if (tx.balanceApplied === false) return;
  for (const id of new Set([tx.accountId, ...(tx.counterAccountId ? [tx.counterAccountId] : [])])) {
    await shiftAt(id, tx.occurredAt, neg(effectOn(tx, id)), tx.id);
  }
  if (tx.type === 'transfer' && tx.counterAccountId) {
    const to = (await getAll<Account>('accounts')).find((a) => a.id === tx.counterAccountId);
    if (to?.kind === 'credit_card') await shiftCardDebt(to.id, neg(tx.amount));
  }
}

async function applyOnAccounts(tx: Tx): Promise<void> {
  if (tx.balanceApplied === false) return;
  for (const id of new Set([tx.accountId, ...(tx.counterAccountId ? [tx.counterAccountId] : [])])) {
    await shiftAt(id, tx.occurredAt, effectOn(tx, id), tx.id);
  }
  if (tx.type === 'transfer' && tx.counterAccountId) {
    const to = (await getAll<Account>('accounts')).find((a) => a.id === tx.counterAccountId);
    if (to?.kind === 'credit_card') await shiftCardDebt(to.id, tx.amount);
  }
}

/**
 * Снимок чека, привязанный к операции.
 *
 * Хранится на устройстве и в снимок синка не входит: один чек весит сотни
 * килобайт, а состояние уходит на сервер целиком при каждом изменении. Значит,
 * на другом устройстве фотографии не будет — это цена того, чтобы синхронизация
 * осталась пригодной. Сам состав чека при этом лежит в операции и ездит с ней.
 */
export interface ReceiptPhoto {
  id: string;
  /** JPEG в виде data: — читается напрямую в <img src>. */
  dataUrl: string;
  savedAt: string;
}

/** Сохранить снимок и вернуть его идентификатор для ссылки из операций. */
export async function putReceiptPhoto(dataUrl: string): Promise<string> {
  const id = uid();
  await put('receiptPhotos', { id, dataUrl, savedAt: new Date().toISOString() });
  return id;
}

export async function getReceiptPhoto(id: string): Promise<ReceiptPhoto | null> {
  const all = await getAll<ReceiptPhoto>('receiptPhotos');
  return all.find((p) => p.id === id) ?? null;
}

/** Убрать снимок, на который больше не ссылается ни одна операция. */
async function dropOrphanPhoto(receiptId: string | undefined): Promise<void> {
  if (!receiptId) return;
  const txs = await getAll<Tx>('transactions');
  if (txs.some((t) => t.receiptId === receiptId)) return;
  await remove('receiptPhotos', receiptId);
}

/* ─────────────────────────────────────────────── электронный чек (§20) ── */

/**
 * Загруженный документ.
 *
 * Лежит отдельно от операций, и это не деталь хранения, а само правило:
 * **до подтверждения импорт баланс не меняет**. После подтверждения запись
 * остаётся с ссылкой на операцию — по ней узнаётся повторная загрузка того же
 * файла, и вторая покупка из него уже не создастся.
 */
export interface ReceiptImport {
  id: string;
  fileHash: string;
  fiscal?: { fn?: string; fd?: string; fp?: string };
  receipt: ImportedReceipt;
  /** `draft` — разобран и ждёт человека; дальше либо своя операция, либо связь. */
  status: 'draft' | 'confirmed' | 'linked';
  txId?: string;
  /**
   * Все операции, созданные из документа.
   *
   * Смешанный чек становится двумя: необходимое и необязательное — разные
   * части плана, и одной операцией они быть не могут (И1). `txId` при этом
   * указывает на большую из них, чтобы прежние проверки «документ уже стал
   * тратой» продолжали работать не зная о разделении.
   */
  txIds?: string[];
  savedAt: string;
}

export const listReceiptImports = (): Promise<ReceiptImport[]> =>
  getAll<ReceiptImport>('receiptImports');

/**
 * Загружали ли уже этот файл.
 *
 * Спрашивается **до** разбора, по одному хешу. Дедупликация после разбора
 * ловит повтор так же надёжно, но к тому моменту вызов модели уже сделан и
 * оплачен — а человек всего лишь второй раз нажал на тот же файл.
 */
export const knownReceipt = async (fileHash: string): Promise<ReceiptImport | null> =>
  (await listReceiptImports()).find((r) => r.fileHash === fileHash) ?? null;

/**
 * Что делать с разобранным документом.
 *
 * Черновик заводится только для новой покупки: повтор открывает прежний
 * результат, а возврат и нераспознанный итог покупкой не становятся вовсе.
 */
export async function receiveReceipt(receipt: ImportedReceipt): Promise<{
  verdict: ImportVerdict; importId?: string;
}> {
  const known = await listReceiptImports();
  const txs = await getAll<Tx>('transactions');
  const verdict = classifyImport({ receipt, known, expenses: expenseCandidates(txs) });

  if (verdict.kind === 'duplicate' || verdict.kind === 'not_expense' || verdict.kind === 'unusable') {
    return { verdict };
  }

  const row: ReceiptImport = {
    id: uid(),
    fileHash: receipt.fileHash,
    ...(receipt.fiscal ? { fiscal: receipt.fiscal } : {}),
    receipt,
    status: 'draft',
    savedAt: new Date().toISOString(),
  };
  await put('receiptImports', row);
  return { verdict, importId: row.id };
}

/** Траты, к которым документ может относиться. */
const expenseCandidates = (txs: Tx[]): ExpenseCandidate[] => txs
  .filter((t) => t.type === 'expense')
  .map((t) => ({
    id: t.id,
    date: t.occurredDate,
    amount: t.amount,
    ...(t.note ? { merchant: t.note } : {}),
    ...(t.receiptId ? { receiptId: t.receiptId } : {}),
    ...(t.balanceApplied === false ? { balanceApplied: false as const } : {}),
  }));

/**
 * Подтвердить импорт как новую покупку.
 *
 * Повторное нажатие возвращает прежнюю операцию, а не создаёт вторую: между
 * нажатием и записью проходит время, и человек успевает нажать дважды.
 */
export async function confirmReceiptImport(input: {
  importId: string;
  accountId: string;
  categoryId: string;
  part: PlanPart;
  /**
   * Часть плана для каждой позиции, в порядке чека.
   *
   * Чек из магазина почти всегда смешанный, и одна часть на весь чек означала
   * бы либо конфеты в необходимом, либо продукты в необязательном. Список
   * длиной с состав превращает документ в две операции; без него остаётся
   * одна, как прежде.
   */
  parts?: readonly PlanPart[];
  /**
   * Категория каждой позиции, в порядке чека: в одном пакете бывают продукты
   * и бытовая химия. Без списка весь чек идёт в `categoryId`.
   */
  categories?: readonly string[];
}): Promise<{ txId: string; created: boolean; count: number }> {
  // Подтверждения одного импорта выстраиваются в очередь.
  //
  // Проверка «операция уже создана» и её создание — два разных ожидания, и
  // между ними успевает вклиниться второе нажатие: оба прохода видят пустое
  // поле `txId` и оба создают покупку. Кнопка, нажатая дважды, не должна
  // стоить человеку двойного списания.
  const prior = confirmQueue.get(input.importId) ?? Promise.resolve();
  const next = prior.then(() => confirmOnce(input), () => confirmOnce(input));
  confirmQueue.set(input.importId, next.then(() => undefined, () => undefined));
  return next;
}

/** Очередь подтверждений по импортам. Живёт только в этой вкладке. */
const confirmQueue = new Map<string, Promise<void>>();

async function confirmOnce(input: {
  importId: string;
  accountId: string;
  categoryId: string;
  part: PlanPart;
  parts?: readonly PlanPart[];
  categories?: readonly string[];
}): Promise<{ txId: string; created: boolean; count: number }> {
  const row = (await listReceiptImports()).find((r) => r.id === input.importId);
  if (!row) throw new Error('Импорт не найден');
  if (row.txId) return { txId: row.txId, created: false, count: row.txIds?.length ?? 1 };
  if (row.receipt.total === undefined) throw new Error('В документе нет итога');

  const total = row.receipt.total;
  const items = row.receipt.items ?? [];
  // Разметка другой длины означает, что она съехала: делить по ней — отнести
  // пиво к еде и не заметить этого. Такой список не используется вовсе.
  const parts = input.parts && input.parts.length === items.length ? input.parts : null;
  const cats = input.categories && input.categories.length === items.length ? input.categories : null;

  // Делим только тогда, когда состав сходится с итогом до копейки. Иначе
  // разница ушла бы в одну из операций молча, и документ на 1 078,17 ₽
  // стал бы тратами на другую сумму.
  const exact = (parts !== null || cats !== null) && items.length > 0
    && add(...items.map((it) => it.sum)) === total;

  const write = async (categoryId: string, part: PlanPart, amount: Money, own: readonly TxItem[]) => addExpense({
    accountId: input.accountId,
    amount,
    categoryId,
    part,
    date: row.receipt.date ?? todayPd(),
    ...(row.receipt.merchant ? { note: row.receipt.merchant } : {}),
    ...(own.length > 0 ? { items: [...own] } : {}),
    ...(row.receipt.time ? { time: row.receipt.time } : {}),
    source: 'receipt_photo',
  });

  const created: { id: string; amount: Money }[] = [];
  if (exact) {
    // Одна операция на категорию и часть плана. У операции одна категория и
    // одна часть (И1), и это не обход правила, а честная запись: в одном
    // пакете было несколько трат.
    const groups = new Map<string, { categoryId: string; part: PlanPart; amount: Money; items: TxItem[] }>();
    items.forEach((it, n) => {
      const categoryId = cats?.[n] ?? input.categoryId;
      const part: PlanPart = parts ? (parts[n] === 'want' ? 'want' : 'need') : input.part;
      const key = `${categoryId}:${part}`;
      const g = groups.get(key) ?? { categoryId, part, amount: ZERO as Money, items: [] };
      g.amount = add(g.amount, it.sum);
      g.items.push(it);
      groups.set(key, g);
    });
    for (const g of groups.values()) {
      const tx = await write(g.categoryId, g.part, g.amount, g.items);
      created.push({ id: tx.id, amount: g.amount });
    }
  } else {
    // Весь чек одной тратой: позиций нет, разметки нет или состав не сошёлся
    // с итогом и делить нечего.
    const tx = await write(input.categoryId, input.part, total, items);
    created.push({ id: tx.id, amount: total });
  }

  // Ведущей считаем большую операцию: по ней документ и опознаётся как
  // ставший тратой.
  const lead = [...created].sort((a, b) => b.amount - a.amount)[0]!;
  await put('receiptImports', {
    ...settledImport(row),
    status: 'confirmed',
    txId: lead.id,
    ...(created.length > 1 ? { txIds: created.map((c) => c.id) } : {}),
  });
  return { txId: lead.id, created: true, count: created.length };
}

/**
 * Импорт после того, как его содержимое перешло в операцию.
 *
 * Состав чека остаётся в операции и второй раз не хранится: он же уезжает в
 * каждый снимок синхронизации, а нужен там ровно один раз. Опознание документа
 * держится на хеше файла и реквизитах, и они остаются.
 */
const settledImport = (row: ReceiptImport): ReceiptImport => {
  const { items, ...rest } = row.receipt;
  void items;
  return { ...row, receipt: rest };
};

/**
 * Привязать документ к уже записанной трате.
 *
 * Сумма второй раз не списывается: деньги учтены в момент записи расхода.
 * Документ добавляет к покупке состав и продавца — и только это.
 */
export async function linkReceiptImport(input: {
  importId: string; expenseId: string;
}): Promise<LinkPlan> {
  const row = (await listReceiptImports()).find((r) => r.id === input.importId);
  if (!row) throw new Error('Импорт не найден');
  const txs = await getAll<Tx>('transactions');
  const tx = txs.find((t) => t.id === input.expenseId);
  if (!tx) throw new Error('Трата не найдена');

  const plan = planLink({
    receipt: row.receipt,
    receiptId: row.id,
    expense: expenseCandidates([tx])[0]!,
  });

  await put('transactions', {
    ...tx,
    receiptId: row.id,
    ...(row.receipt.items?.length ? { items: row.receipt.items } : {}),
    ...(tx.note ? {} : row.receipt.merchant ? { note: row.receipt.merchant } : {}),
  });
  await put('receiptImports', { ...settledImport(row), status: 'linked', txId: tx.id });
  return plan;
}

/** Человек отказался от импорта: черновик уходит, операции не было. */
export const dropReceiptImport = (id: string): Promise<void> => remove('receiptImports', id);

// ──────────────────────────────────────────────────────────────── сброс

/** «Начать с чистого листа»: операции уходят, структура остаётся. */
export async function resetTransactions(): Promise<void> {
  await clearStores(['transactions', 'transactionItems', 'receiptPhotos', 'receiptImports']);
}

/** «Сбросить всё»: уходит и структура. Категории пересоздаются при загрузке. */
export async function resetEverything(): Promise<void> {
  // Список берётся из описания хранилищ, а не пишется рядом руками.
  //
  // Написанный руками он отставал: принятые планы, черновик настройки,
  // назначения будущего дохода и согласия появились позже и в него не попали.
  // «Удалить всё», оставляющее половину данных на устройстве, — это не
  // удаление, а обещание удаления.
  await clearStores(Object.keys(STORES) as StoreName[]);
}

/** Заполнить эталонным примером — для проверки экранов на живых числах. */
export async function seedDemo(): Promise<void> {
  await resetEverything();
  await putAll('accounts', demoAccounts);
  await putAll('creditTerms', demoCreditTerms);
  await putAll('incomeStreams', demoIncome);
  await putAll('obligations', demoObligations);
  await putAll('goals', demoGoals);
  await putAll('categories', STARTER_CATEGORIES);
  await setBaselines(DEMO_NEED_BASELINE, DEMO_WANT_BASELINE);
}

/**
 * Заменить локальное состояние снимком с сервера.
 *
 * Категории не перезаписываем: справочник системный и одинаковый, а тащить его
 * через сеть в каждом снимке — лишние килобайты в каждом запросе.
 */
export interface ReplaceResult {
  /** Изменились ли локальные данные. `false` — снимок отклонён целиком. */
  applied: boolean;
  /** Мелкие потери: поле заменено значением по умолчанию. */
  errors: string[];
  /** Почему снимок отклонён. Непустой список означает `applied: false`. */
  fatal: string[];
}

/**
 * Загрузка снимка поверх локальных данных.
 *
 * Принимает сырой снимок — с конвертом версии или старый плоский — и
 * раскладывает его кодеками (`persisted.ts`). Битая запись в одном поле не
 * обнуляет остальные: список ошибок возвращается вызывающему.
 *
 * Снимок из будущей версии и снимок с испорченным списком **не применяются
 * вовсе**: локальные данные в этом случае заведомо лучше пришедших.
 */
/**
 * Заменить данные устройства снимком с сервера.
 *
 * Время последнего изменения здесь — время снимка, а не «сейчас»: иначе
 * сразу после загрузки телефон считался бы новее сервера.
 */
export async function replaceLocalState(raw: unknown): Promise<ReplaceResult> {
  const result = await quietly(() => replaceLocalStateNow(raw));
  if (result.applied) {
    const savedAt = (raw as { savedAt?: unknown } | null)?.savedAt;
    await quietly(() => setMeta(LOCAL_CHANGED, typeof savedAt === 'string' ? savedAt : null));
  }
  return result;
}

/**
 * Отпечатки последних снимков, отправленных с этого устройства.
 *
 * Ответ сервера может потеряться: снимок записан, а устройство об этом не
 * узнало и осталось на старой версии. Следующая отправка получает
 * «расхождение версий» — хотя на сервере наш же снимок. По отпечатку его и
 * узнаём, чтобы не спрашивать человека, какая версия свежее.
 */
const PUSHED = 'pushedPrints';
export const pushedPrints = async (): Promise<string[]> => (await getMeta<string[]>(PUSHED)) ?? [];
export const rememberPushed = async (print: string): Promise<void> => {
  const list = (await pushedPrints()).filter((x) => x !== print);
  await setMeta(PUSHED, [print, ...list].slice(0, 8));
};

/** Когда данные устройства последний раз менялись; `null` — неизвестно. */
export const getLocalChangedAt = async (): Promise<string | null> => (await getMeta<string | null>(LOCAL_CHANGED)) ?? null;

async function replaceLocalStateNow(raw: unknown): Promise<ReplaceResult> {
  const { state: p, errors, fatal, unknown } = decode(raw);

  // Порядок здесь и есть защита: сначала снимок признаётся годным, и только
  // потом трогаются локальные данные. Раньше хранилище очищалось до разбора —
  // и снимок из будущей версии или с испорченным списком стирал счета
  // человека, оставляя взамен пустоту.
  if (fatal.length > 0) return { applied: false, errors, fatal };

  // Одна транзакция на всю замену.
  //
  // Очистка и запись порознь оставляют окно, в котором данных нет вовсе: сбой
  // записи между ними уничтожает картину человека, а не заменяет её. Здесь
  // либо применится всё, либо не изменится ничего.
  await replaceAll([
    { store: 'accounts', values: p.accounts },
    { store: 'creditTerms', values: p.creditTerms },
    { store: 'incomeStreams', values: p.incomeStreams },
    { store: 'obligations', values: p.obligations },
    { store: 'goals', values: p.goals },
    { store: 'goalAllocations', values: p.goalEvents },
    { store: 'plannedExpenses', values: p.plannedExpenses },
    { store: 'plannedAllocations', values: p.plannedAllocations },
    { store: 'expectedIncomes', values: p.expectedIncomes },
    { store: 'reserves', values: p.reserves },
    // Стабильный бюджет переезжает целиком: план, история выплат и сам буфер
    // (он лежит в резервах выше). Иначе на новом устройстве человек увидел
    // бы предложение включить режим, который у него уже включён, а деньги
    // буфера — свободными.
    { store: 'stablePlans', values: p.stablePlans },
    { store: 'stablePayouts', values: p.stablePayouts },
    { store: 'stableMoves', values: p.stableMoves },
    { store: 'transactions', values: p.transactions },
    { store: 'transactionItems', values: [] },
    // Снимки, сделанные до появления кредитов, их не содержат — тогда список
    // просто пуст, а условия карт остаются в своём прежнем хранилище.
    { store: 'loans', values: p.loans },
    // Категории раньше не синхронизировались вовсе: пользовательские
    // подкатегории терялись при переходе на другое устройство.
    { store: 'categories', values: p.categories.length > 0 ? p.categories : STARTER_CATEGORIES },
    { store: 'acceptedPlans', values: p.acceptedPlans },
    { store: 'allocations', values: p.allocations },
    { store: 'consents', values: p.consents },
    { store: 'insightLog', values: p.insightLog },
    // Опознание чеков переезжает вместе с данными: иначе на новом устройстве
    // тот же документ стал бы второй покупкой.
    { store: 'receiptImports', values: p.receiptImports },
  ]);
  await setBaselineAmounts(p.needBaseline, p.wantBaseline);
  await setMeta('obligationsKnown', p.obligationsKnown === true);
  await setMeta('profileName', p.profileName ?? null);
  await setMeta('categoryDefaultsReady', p.categoryDefaultsReady ?? false);
  await setMeta('trackingStartedAt', p.trackingStartedAt ?? null);
  await setFocusOverride(p.focusOverride ?? null);
  await setMeta('planMode', p.planMode ?? null);
  await setMeta('tradeoffChoice', p.tradeoffChoice ?? null);
  await setMeta('newCategoryMode', p.newCategoryMode ?? null);
  await setMeta('periodChoice', p.periodChoice ?? null);
  await setMeta('periodPins', p.periodPins.length > 0 ? p.periodPins : null);
  await setMeta('periodBaselines', p.periodBaselines.length > 0 ? p.periodBaselines : null);
  await setMeta('dismissedHints', p.dismissedHints.length > 0 ? p.dismissedHints : null);
  await setMeta('periodDeals', p.periodDeals.length > 0 ? p.periodDeals : null);
  await setMeta('periodTopicsOff', p.periodTopicsOff.length > 0 ? p.periodTopicsOff : null);
  await setMeta('periodReviewLater', p.periodReviewLater.length > 0 ? p.periodReviewLater : null);
  await setMeta('usualPinned', p.usualPinned.length > 0 ? p.usualPinned : null);
  await setMeta('accountGroups', p.accountGroups.length > 0 ? p.accountGroups : null);
  // Чужие поля переживают загрузку и уедут обратно в следующем снимке.
  await setMeta('snapshotUnknown', unknown);
  return { applied: true, errors, fatal: [] };
}

/** Версия снимка, от которой отталкивается это устройство. */
export const getSyncVersion = (): Promise<number | undefined> => getMeta<number>('syncVersion');
/** Новая версия — значит, устройство и сервер сошлись: недосланного нет. */
export const setSyncVersion = async (v: number): Promise<void> => {
  await setMeta('syncVersion', v);
  await setMeta('syncUnsent', false);
};
/**
 * Изменения устройства не дошли до сервера — отправка не удалась.
 *
 * Помнится в хранилище, а не в памяти вкладки: человек мог ответить без
 * сети и закрыть приложение. Досылаем при следующем запуске или когда сеть
 * вернётся.
 */
export const getSyncUnsent = async (): Promise<boolean> => (await getMeta<boolean>('syncUnsent')) === true;
export const setSyncUnsent = (v: boolean): Promise<void> => setMeta('syncUnsent', v);

/**
 * Разряды при вводе суммы.
 *
 * «151974,93» читается заметно хуже, чем «151 974,93», а ошибиться на разряд
 * в деньгах стоит дорого. Разделитель — неразрывный пробел, тот же, что в
 * выводе formatMoney, и parseAmountInput его отбрасывает наравне с прочим.
 */
export const formatAmountInput = (raw: string): string => {
  const cleaned = raw.replace(/[^\d.,]/g, '');
  const [int = '', ...rest] = cleaned.split(/[.,]/);
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
  if (rest.length === 0) return grouped;
  // Копеек больше двух не бывает: лишние цифры отбрасываем сразу, чтобы
  // человек не вводил то, что потом молча округлится.
  return `${grouped},${rest.join('').slice(0, 2)}`;
};

/** Знаки выражения — как на клавишах калькулятора. */
const EXPR_OPS: Record<string, string> = { '-': '−', '+': '+', '*': '×', '/': '÷' };

/**
 * Набранное выражение суммы — с разрядами и знаками, как на клавишах, с
 * пробелами вокруг (28.10.2026): «1649,07» → «1 649,07»,
 * «12000+3500» → «12 000 + 3 500», «5000-522» → «5 000 − 522».
 * Копейки — как набраны.
 */
export const groupExpr = (expr: string): string =>
  expr.replace(/(\d+)([.,]\d*)?/g, (_m, int: string, frac?: string) =>
    `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0')}${frac ?? ''}`)
    .replace(/\s*([-+*/])\s*/g, (_m, op: string) => `\u00A0${EXPR_OPS[op] ?? op} `);

export const parseAmountInput = (raw: string): Money => {
  const cleaned = raw.replace(/[^\d,.]/g, '').replace(',', '.');
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n <= 0) return ZERO;
  return kop(Math.round(n * 100));
};

/**
 * Ставка из поля ввода: `null` — не названа.
 *
 * Пустое поле и «0» — разные ответы. Первое означает «не знаю», второе —
 * беспроцентную рассрочку, и подменять одно другим нельзя: кредит под 12%,
 * у которого стёрли ставку, превращался в беспроцентный долг, гасился в
 * расчёте за десять месяцев без единого рубля процентов и уходил в конец
 * очереди гашения — то есть человек переставал платить по нему досрочно
 * ровно тогда, когда это выгоднее всего.
 *
 * Мусор в поле — тоже «не знаю»: «дв» не ноль процентов.
 */
export const parseRateInput = (raw: string): number | null => {
  const cleaned = raw.trim().replace(',', '.');
  if (cleaned === '') return null;
  if (!/^\d+(\.\d+)?$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) && n >= 0 && n < 1000 ? n : null;
};
